﻿// © Microsoft Corporation. All rights reserved.

import { AppError, AppErrorPropertyBag, Severity } from "@msnews/core-contracts";

/**
 * This file contains the list of errors to log to the server for each feature.
 * We are centralizing errors here to make sure we do not have any dupes\conflicts.
 * The idea is that each feature would have their own range of values.
 *
 * IMPORTANT: Please do not add strings to this file. Please map messages to send in your
 * code.
 */

/**
 * All app errors are set to create IcM ticket by default unless specified in individual error code otherwise.
 */
const severity = Severity.Alert;

/**
 * Build number is only passed in from updateAppErrorsPropertyBag at page initialization
 */
const pb: AppErrorPropertyBag = {
    build: ""
};

/**
 * Append or update property bag properties. At page level intialization, this should be called to update the build number.
 * Since all app error objects has reference to this base property bag, calling this method will in fact update for all app errors.
 * @param newPbInfo Additional information needed for app error property bag
 */
export function updateAppErrorsPropertyBag(newPbInfo: Partial<AppErrorPropertyBag>): void {
    Object.assign(pb, newPbInfo);
}

/**
 * Getter for current base app error property bag.
 */
export function getAppErrorsPropertyBag(): AppErrorPropertyBag {
    return pb;
}

const pageTsFileName = "page.ts";

/**
 * Error during app initialization for Edge page experience.
 * Need to mimic the prime one since telemetry cooking logic looks for this
 */
const establishSignInSource = "EstablishSignInStateHelpers.ts";
export const EstablishSignedInState: AppError = {
    id: 1405,
    source: establishSignInSource,
    severity: Severity.NoAlert,
    pb
};

/** Represents a ServiceUI related error */
export const ServiceUiError: AppError = {
    id: 5100,
    source: "ServiceUiComm.ts",
    severity,
    pb
};

/** Represents a Top Sites related error */
export const TopSitesError: AppError = {
    id: 8010,
    source: "TopSites.tsx",
    severity,
    pb
};

/** Represents a Top Sites error when there are zero rendered top sites */
export const ZeroTopSitesError: AppError = {
    id: 8011,
    source: "TopSites.connector.ts",
    severity,
    pb
};

/** Curated sites list is empty */
export const CuratedSitesEmpty: AppError = {
    id: 8012,
    source: "TopSites.connector.ts",
    severity,
    pb
};

const TopSitesEdgeConnectorFileName = "TopSitesEdge.connector.tsx";
const TopSitesEdgeStorageFileName = "TopSitesStorage.ts";

export const TopSitesEdgeAppErrors = {
    ZeroTopSitesError: <AppError>{
        id: 8013,
        source: TopSitesEdgeConnectorFileName,
        severity: Severity.NoAlert,
        pb
    },

    CuratedSitesEmpty: <AppError>{
        id: 8014,
        source: TopSitesEdgeStorageFileName,
        severity,
        pb
    },

    ComponentMountFailed: <AppError>{
        id: 8015,
        source: TopSitesEdgeConnectorFileName,
        severity,
        pb
    },

    ErrorFetchingRichTopSitesDataCollectionFromCms: <AppError>{
        id: 8016,
        source: TopSitesEdgeStorageFileName,
        severity,
        pb
    }
};

const TopSitesEdgeNextConnectorFileName = "TopSitesEdgeNext.connector.tsx";

export const TopSitesEdgeNextAppErrors = {

    /** The React component failed to mount */
    ComponentMountFailed: <AppError>{
        id: 8020,
        source: TopSitesEdgeNextConnectorFileName,
        severity,
        pb
    },

    /** Represents a Top Sites error when there are zero rendered top sites */
    ZeroTopSitesError: <AppError>{
        id: 8021,
        source: TopSitesEdgeNextConnectorFileName,
        severity: Severity.NoAlert,
        pb
    }
};

export const TopSiteDialogAppErrors = {
    /** The React component failed to mount */
    TopSitesDialogFailedToMount: <AppError>{
        id: 8025,
        source: "TopSitesDialog.tsx",
        severity,
        pb
    }
};

const OneServiceCardProviderFileName = "OneServiceCardProvider.ts";

export const OneServiceCardProviderAppErrors = {
    OneServiceResponseEmpty: <AppError>{
        id: 8029,
        source: OneServiceCardProviderFileName,
        severity: Severity.NoAlert,
        pb
    },

    EmptyMyFeed: <AppError>{
        id: 8030,
        source: OneServiceCardProviderFileName,
        severity,
        pb
    },

    OneServiceResponseWasNullOrErroneous: <AppError>{
        id: 8031,
        source: OneServiceCardProviderFileName,
        severity,
        pb
    },

    ErrorFetchingOneServiceResponse: <AppError>{
        id: 8032,
        source: OneServiceCardProviderFileName,
        severity: Severity.HighImpact,
        pb
    },

    InvalidJsonInOneServiceResponse: <AppError>{
        id: 8033,
        source: OneServiceCardProviderFileName,
        severity: Severity.HighImpact,
        pb
    },

    InvalidFeedListInOneServiceResponse: <AppError>{
        id: 8034,
        source: OneServiceCardProviderFileName,
        severity,
        pb
    },

    RiverConfigurationIssue: <AppError>{
        id: 8035,
        source: OneServiceCardProviderFileName,
        severity,
        pb
    },

    /** Failed to load views river content based on current vertical/category. */
    QueryInterestContentFetchError: <AppError>{
        id: 8036,
        source: OneServiceCardProviderFileName,
        severity,
        pb
    },

    StaticContentLoaded: <AppError>{
        id: 8037,
        source: OneServiceCardProviderFileName,
        severity: Severity.NoAlert,
        pb
    }
};

const OneServiceDailyBriefApi = "dailyBriefServiceClient.ts";

export const DailyBriefServiceClientAppErrors = {
    OneServiceResponseWasNullOrErroneous: <AppError>{
        id: 8038,
        source: OneServiceDailyBriefApi,
        severity,
        pb
    },

    OneServiceArticleListWasNullOrErroneous: <AppError>{
        id: 8039,
        source: OneServiceDailyBriefApi,
        severity,
        pb
    }
};

const ContentPreviewSourceFile = "ContentPreview.connector.ts";
const ContentPreviewDataSourceFile = "ContentPreviewData.ts";
const ExternalContentMapperHelperFile = "ExternalContentMapperHelper.ts";

export const ContentPreviewAppErrors = {

    ErrorWhileFetchingContentPreviewContent: <AppError>{
        id: 8041,
        source: ContentPreviewDataSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    InvalidResponseFromContentPreviewFetch: <AppError>{
        id: 8042,
        source: ContentPreviewDataSourceFile,
        severity,
        pb
    },

    InvalidResponseFromContentPreviewResponseJson: <AppError>{
        id: 8043,
        source: ContentPreviewDataSourceFile,
        severity,
        pb
    },

    ErrorWhileInitializingAsyncContentPreviewContent: <AppError>{
        id: 8044,
        source: ContentPreviewSourceFile,
        severity,
        pb
    },

    InvalidOrMissingContentId: <AppError>{
        id: 8045,
        source: ContentPreviewDataSourceFile,
        severity,
        pb
    },

    MissingDataForExternalContent: <AppError>{
        id: 8046,
        source: ExternalContentMapperHelperFile,
        severity: Severity.NoAlert,
        pb
    }
};

export const MissingPartnerLink: AppError = {
    id: 8047,
    source: ContentPreviewDataSourceFile,
    severity,
    pb
};

const RiverSourceFile = "River.connector.ts";

export const RiverAppErrors = {
    UnknownMetadataReceivedFromRiverCardProvider: <AppError>{
        id: 8051,
        source: RiverSourceFile,
        severity,
        pb
    },

    RiverContentProviderFetchException: <AppError>{
        id: 8052,
        source: RiverSourceFile,
        severity,
        pb
    },

    SkipPositionedCardsFallbackStrategyFailed: <AppError>{
        id: 8053,
        source: RiverSourceFile,
        severity,
        pb
    },

    InvalidConfig: <AppError>{
        id: 8054,
        source: RiverSourceFile,
        severity: Severity.HighImpact,
        pb
    },

    MissingExperienceTypeMap: <AppError>{
        id: 8055,
        source: RiverSourceFile,
        severity: Severity.HighImpact,
        pb
    },

    ChildLoadError: <AppError>{
        id: 8056,
        source: RiverSourceFile,
        severity,
        pb
    },

    InitialDataLoadError: <AppError>{
        id: 8057,
        source: RiverSourceFile,
        severity,
        pb
    },

    /** Warning for when the ordinal children we get from config contains a duplicate instance id. */
    DuplicateInstanceIdWarning: <AppError>{
        id: 8058,
        source: RiverSourceFile,
        severity: Severity.NoAlert,
        pb
    }
};

export const FullHeightFallbackStrategyFailed: AppError = {
    id: 8059,
    source: RiverSourceFile,
    severity,
    pb
};

const UserInitiatedPageRefreshSourceFile = "UserInitiatedPageRefresh.ts";
export const UserInitiatedPageRefreshErrors = {
    Default: <AppError>{
        id: 8060,
        source: UserInitiatedPageRefreshSourceFile,
        severity,
        pb
    }
};

const PivotContentSourceFile = "PivotContent.connector.ts";
export const PivotContentErrors = {
    LoadFailure: <AppError>{
        id: 8070,
        source: PivotContentSourceFile,
        severity,
        pb
    }
};

const PivotContentV2SourceFile = "PivotContentV2.connector.ts";
export const PivotContentV2Errors = {
    LoadFailure: <AppError>{
        id: 8071,
        source: PivotContentV2SourceFile,
        severity,
        pb
    }
};

export const PivotContentRenderFailure: AppError = {
    id: 8072,
    source: "PivotContent.tsx",
    severity,
    pb
};

const PivotsNavSourceFile = "PivotsNav.connector.ts";
export const PivotsNavErrors = {
    TopicDataConnectorUnavailable: <AppError>{
        id: 8075,
        source: PivotsNavSourceFile,
        severity,
        pb
    },

    NavigationDataConnectorUnavailable: <AppError>{
        id: 8076,
        source: PivotsNavSourceFile,
        severity,
        pb
    },

    DynamicPivotsHelperLoaderFailed: <AppError>{
        id: 8077,
        source: PivotsNavSourceFile,
        severity,
        pb
    },

    LayoutPreferenceDataConnectorUnavailable: <AppError>{
        id: 8078,
        source: PivotsNavSourceFile,
        severity,
        pb
    }
};

const PivotsNavV2SourceFile = "PivotsNavV2.connector.ts";
export const PivotsNavV2Errors = {
    PivotHoverLoadFailure: <AppError>{
        id: 8079,
        source: PivotsNavV2SourceFile,
        severity,
        pb
    }
};

/** Smart list is empty */
export const SmartListEmpty: AppError = {
    id: 8080,
    source: "SmartList.connector.ts",
    severity,
    pb
};

/** Represents BingWebSSO related App errors */
const BingWebSSOSourceFile = "BingWebSSO.tsx";
export const BingWebSSOAppErrors = {
    /** Failed to render bing websso iframe */
    BingWebSSORenderFailed: <AppError>{
        id: 8090,
        source: BingWebSSOSourceFile,
        severity,
        pb
    },

    /** User signed-in to MSA on page but bing SSO returned authentication failed response */
    BingWebSSOSignInFailed: <AppError>{
        id: 8091,
        source: BingWebSSOSourceFile,
        severity,
        pb
    },

    /** Bing SSO process did not complete withing required time */
    BingWebSSOTimeout: <AppError>{
        id: 8092,
        source: BingWebSSOSourceFile,
        severity,
        pb
    }
};

const WeatherDataConnectorSourceFile = "WeatherData.connector.ts";
const MultiLocationWeatherServiceSourceFile = "MultiLocationWeatherService.ts";
const WeatherCardConnectorSourceFile = "WeatherCard.connector.ts";
const WeatherPdpClientSourceFile = "WeatherPdpClient.ts";
const WeatherOverviewApiClientSourceFile = "WeatherOverviewApiClient.ts";
const WeatherMapRadarSourceFile = "RadarLayer.ts";
const WeatherMapWeatherLayersSourceFile = "WeatherLayers.ts";
const WeatherMapPrecipitationSourceFile = "PrecipitationLayer.ts";
const WeatherMapGenericSevereLayerSourceFile = "GenericSevereLayer.tsx";
const WeatherMapHeatmapRenderLayerSourceFile = "HeatmapRender.ts";
const WeatherMapBingJsonLabelLayerSourceFile = "BingJsonLabelLayer.ts";
const WeatherMapBingOneCanvasTileLayerSourceFile = "OneCanvasTileLayer.ts";

/** Failed to fetch weather summary */
export const WeatherSummaryFetchError: AppError = {
    id: 8100,
    source: WeatherDataConnectorSourceFile,
    severity,
    pb
};

/** No valid Location set from User */
export const WeatherLocationEmpty: AppError = {
    id: 8101,
    source: WeatherDataConnectorSourceFile,
    severity: Severity.NoAlert,
    pb
};

/** Not able to read from PDP */
export const WeatherPDPReadFailed: AppError = {
    id: 8102,
    source: WeatherPdpClientSourceFile,
    severity: Severity.NoAlert,
    pb
};

/** Not able to write home location to PDP */
export const WeatherPDPHomeLocationWriteFailed: AppError = {
    id: 8103,
    source: WeatherPdpClientSourceFile,
    severity,
    pb
};

/** Not able to write display Unit to PDP */
export const WeatherPDPDisplayUnitWriteFailed: AppError = {
    id: 8104,
    source: WeatherPdpClientSourceFile,
    severity,
    pb
};

/** Not able to localize Location */
export const WeatherLocalizeLocationFailed: AppError = {
    id: 8105,
    source: WeatherDataConnectorSourceFile,
    severity: Severity.NoAlert,
    pb
};

export const WeatherCardAppErrors = {
    /** Failed to fetch local news */
    WeatherLocalNewsFetchError: <AppError>{
        id: 8106,
        source: WeatherCardConnectorSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /** Failed to fetch autosuggest for locations */
    WeatherAutoSuggestFetchError: <AppError>{
        id: 8107,
        source: WeatherCardConnectorSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /** No valid Location set from User */
    WeatherLocationEmpty: <AppError>{
        id: 8108,
        source: WeatherCardConnectorSourceFile,
        severity,
        pb
    }
};

/** Failed to fetch autosuggest for locations */
export const WeatherAutoSuggestFetchError: AppError = {
    id: 8109,
    source: WeatherDataConnectorSourceFile,
    severity,
    pb
};

/** for more error about Weather, use 295XX */

const SportsPdpServiceClientSourceFile = "SportsPdpServiceClient.ts";
const SportsDataMapperFile = "libs/feed-layout/.../SportsCardDataMapper.ts";
export const SportsCardError = {
    /** Failed to fetch league sports pdp  */
    SportsLeaguePdpFetchError: <AppError>{
        id: 8110,
        source: SportsPdpServiceClientSourceFile,
        severity,
        pb
    },

    /** Failed to fetch league sports pdp  */
    SportsLeaguePdpInvalidResponse: <AppError>{
        id: 8111,
        source: SportsPdpServiceClientSourceFile,
        severity,
        pb
    },

    /** Failed to fetch players sports pdp  */
    SportsPlayersPdpFetchError: <AppError>{
        id: 8112,
        source: SportsPdpServiceClientSourceFile,
        severity,
        pb
    },

    /** Failed to fetch players sports pdp  */
    SportsPlayersPdpInvalidResponse: <AppError>{
        id: 8113,
        source: SportsPdpServiceClientSourceFile,
        severity,
        pb
    },

    /** Failed to fetch teams sports pdp  */
    SportsTeamsPdpFetchError: <AppError>{
        id: 8114,
        source: SportsPdpServiceClientSourceFile,
        severity,
        pb
    },

    /** Failed to fetch teams sports pdp  */
    SportsTeamsPdpInvalidResponse: <AppError>{
        id: 8115,
        source: SportsPdpServiceClientSourceFile,
        severity,
        pb
    },

    /** Failed to get sports card config */
    SportsCardConfigFetchError: <AppError>{
        id: 8116,
        source: "SportsCardConfigReader.ts",
        severity,
        pb
    },

    /** Failed to get Team Data */
    SportsCardGetFavoriteTeamDataError: <AppError>{
        id: 8117,
        source: "SportsCard.connector.ts",
        severity,
        pb
    },

    /** Failed to get Team Data */
    SportsCardGetPlayerBioError: <AppError>{
        id: 8118,
        source: "SportsCard.connector.ts",
        severity,
        pb
    },
    SportsCardGetLeaguesGamesArrayError: <AppError>{
        id: 8119,
        source: "SportsCard.connector.ts",
        severity,
        pb
    },
    SportsCardGetPdpDataFailure: <AppError>{
        id: 8120,
        source: "SportsCard.connector.ts",
        severity,
        pb
    },
    SportsCardGetLeagueSeasonYearDataFailure: <AppError>{
        id: 8121,
        source: "SportsCardServiceClient.ts",
        severity,
        pb
    },
    SportsCardGetLocalizedTeamNameDataFailure: <AppError>{
        id: 8122,
        source: "SportsCardServiceClient.ts",
        severity,
        pb
    },
    SportsCardGetTeamDataFailure: <AppError>{
        id: 8123,
        source: "SportsCardServiceClient.ts",
        severity,
        pb
    },
    SportsCardGetLeagueDataFailure: <AppError>{
        id: 8124,
        source: "SportsCardServiceClient.ts",
        severity,
        pb
    }
};

export const SportsCardDataMapperError: AppError = {
    id: 8125,
    source: SportsDataMapperFile,
    severity,
    pb
};

const MoneyCardConnectorFile = "MoneyCard.connector.ts";
const MoneyPdpServiceClientFile = "MoneyPdpServiceClient.ts";
const MoneyDataMapperFile = "libs/feed-layout/.../MoneyCardDataMapper.ts";
export const MoneyCardError = {
    /** Failed to get quote summary for user */
    MoneyQuoteSummaryGetError: <AppError>{
        id: 8130,
        source: MoneyCardConnectorFile,
        severity,
        pb
    },

    /** Failed to get quote summaries */
    MoneyQuoteSummariesGetError: <AppError>{
        id: 8131,
        source: MoneyCardConnectorFile,
        severity,
        pb
    },

    /** Failed to get pdp data */
    MoneyPdpDataGetErrorFromService: <AppError>{
        id: 8132,
        source: MoneyPdpServiceClientFile,
        severity,
        pb
    },

    /** Failed to get pdp data because of invalid response */
    MoneyPdpInvalidResponseFromService: <AppError>{
        id: 8133,
        source: MoneyPdpServiceClientFile,
        severity,
        pb
    },

    /** Failed to get quote summary response for user from service client */
    MoneyQuoteSummaryGetErrorFromService: <AppError>{
        id: 8134,
        source: MoneyPdpServiceClientFile,
        severity,
        pb
    },

    /** Failed to get quote summary response for user from service client because of invalid response */
    MoneyQuoteSummaryInvalidResponseFromService: <AppError>{
        id: 8135,
        source: MoneyPdpServiceClientFile,
        severity,
        pb
    }
};

/** Failed to parse data from WPO API */
export const MoneyDataMapperError: AppError = {
    id: 8136,
    source: MoneyDataMapperFile,
    severity,
    pb
};

const TrendingNewsConnectorFile = "TrendingNews.connector.ts";
export const TrendingNewsErrors = {
    /** Failed to fetch the trending news stories */
    DataFetchError: <AppError>{
        id: 8140,
        source: TrendingNewsConnectorFile,
        severity,
        pb
    },
    /** Failed to fetch the backup data for trending news stories */
    BackupDataFetchError: <AppError>{
        id: 8141,
        source: TrendingNewsConnectorFile,
        severity,
        pb
    }
};

const ComplexContentPreviewSourceFile = "ComplexContentPreview.connector.ts";
export const ComplexContentPreviewErrors = {
    InitError: <AppError>{
        id: 8150,
        source: ComplexContentPreviewSourceFile,
        severity,
        pb
    },
    LoadError: <AppError>{
        id: 8151,
        source: ComplexContentPreviewSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    ContentError: <AppError>{
        id: 8152,
        source: ComplexContentPreviewSourceFile,
        severity,
        pb
    },
    ContentCountError: <AppError>{
        id: 8153,
        source: ComplexContentPreviewSourceFile,
        severity,
        pb
    }
};

export const GridSectionAppErrors = {
    /** Unable to generate river section cards */
    GridSectionCardGenerationError: <AppError>{
        id: 8160,
        source: "GridSection.tsx",
        severity,
        pb
    }
};

const MmxEntryPointFileName = "MMXPage.connector.ts";

export const MmxEntryPointAppErrors = {
    /**
     * MMX river is not able to load Error Page.
     */
    MmxErrorPage: <AppError>{
        id: 8170,
        source: MmxEntryPointFileName,
        severity,
        pb
    },

    /**
     * Error fetching the APP_ANON cookie on MMX page load
     */
    ErrorFetchingAppAnonCookie: <AppError>{
        id: 8171,
        source: pageTsFileName,
        severity,
        pb
    },

    /**
     * Warning that MMX server side rendering is skipped and client side rendering is triggered
     * due to either server error or slow server response.
     */
    MMXSSRSkip: <AppError>{
        id: 8172,
        source: MmxEntryPointFileName,
        severity: Severity.NoAlert,
        pb
    }
};

/**
 * Errors related to Background image
 * More errors can be found on 386XX
 */
const irisImageHotspotsProviderFile = "IrisImageHotspotsProvider.ts";
const irisMuseumCardFile = "IrisMuseumCard.tsx";
const irisHotspotCtaFile = "Hotspot.cta.ts";
const bingImageProvider = "BingImageOfTheDayProvider.ts";
const freImageProvider = "FREBackgroundProvider.ts";

/** The Bing Image of the Day failed to load properly */
export const BingIOTDFailedToLoad: AppError = {
    id: 8180,
    source: bingImageProvider,
    severity,
    pb
};

/** The FRE image method has not been implemented */
export const FREImageMethodNotImplemented: AppError = {
    id: 8181,
    source: freImageProvider,
    severity,
    pb
};

/** Iris call from museum card failed */
export const ImageMuseumIrisDataFetchFailed: AppError = {
    id: 8182,
    source: irisMuseumCardFile,
    severity,
    pb
};

/** Hotspots data found for the off page navigation failed inspection. */
export const InvalidOffPageNavCTA: AppError = {
    id: 8183,
    source: irisHotspotCtaFile,
    severity,
    pb
};

/** Hotspots cta type or metadata not defined */
export const CTATypeOrMetdataNotDefined: AppError = {
    id: 8184,
    source: irisHotspotCtaFile,
    severity,
    pb
};

/** Event not defined */
export const EventNotDefined: AppError = {
    id: 8185,
    source: irisHotspotCtaFile,
    severity,
    pb
};

/** Hotspot call to action state not defined */
export const HotspotCtaStateNotDefined: AppError = {
    id: 8186,
    source: irisHotspotCtaFile,
    severity,
    pb
};

/** Iris data connector not defined */
export const ImageIrisDataConnectorUndefined: AppError = {
    id: 8187,
    source: irisImageHotspotsProviderFile,
    severity,
    pb
};

/** Iris data connector not defined on Museum card */
export const ImageIrisDataConnectorUndefinedMuseumCard: AppError = {
    id: 8188,
    source: irisMuseumCardFile,
    severity,
    pb
};

/** Iris data call failed */
export const ImageIrisDataFetchFailed: AppError = {
    id: 8189,
    source: irisImageHotspotsProviderFile,
    severity,
    pb
};

export const SettingsDialogAppErrors = {
    /** Failed to get the saved layout preferences */
    ReadPreferencesFailed: <AppError>{
        id: 8190,
        source: "SettingsDialogEdgeNext.connector.ts",
        severity,
        pb
    },

    /** Failed to fetch the ConfigData object from the client API */
    FetchNtpConfigDataFailed: <AppError>{
        id: 8191,
        source: "SettingsDialogEdgeNextBase.connector.ts",
        severity,
        pb
    },

    ChromiumClientEmbededsearchAPIError: <AppError>{
        id: 8192,
        source: "SettingsDialogEdgeNext.connector.ts",
        severity,
        pb
    }
};

const MsnSettingMenuConnectorFileName = "MsnSettingMenuConnector.connector.ts";

export const MsnSettingsMenuAppErrors = {
    /** Failed to get feedback dialog connector */
    FailedToGetFeedbackDialogConnector: <AppError>{
        id: 8193,
        source: MsnSettingMenuConnectorFileName,
        severity,
        pb
    },
    /** Failed to get feedback data connector */
    FailedToGetFeedbackDataConnector: <AppError>{
        id: 8194,
        source: MsnSettingMenuConnectorFileName,
        severity,
        pb
    },
    /** The Msn settings menu failed to load valid config settings */
    MsnSettingsMenuFailedToFetchConfig: <AppError>{
        id: 8195,
        source: MsnSettingMenuConnectorFileName,
        severity,
        pb
    }
};

export const EdgePageSettingsAppErrors = {
    ServiceUiError: <AppError>{
        id: 8196,
        source: "EdgeSettingsProvider.ts",
        severity,
        pb
    },

    InlineServiceUiError: <AppError>{
        id: 8197,
        source: "inline-head.ts",
        severity,
        pb
    }
};

const ShowFeedConnectorFileName = "ShowFeed.connector.ts";

export const ShowFeedAppErrors = {
    ExpectedDataMissing: <AppError>{
        id: 8198,
        source: ShowFeedConnectorFileName,
        severity,
        pb
    },
    MissingPageSettings: <AppError>{
        id: 8199,
        source: ShowFeedConnectorFileName,
        severity,
        pb
    }
};

const InfopaneSourceFile = "Infopane.connector.ts";
export const InfopaneErrors = {
    InitializeError: <AppError>{
        id: 8201,
        source: InfopaneSourceFile,
        severity,
        pb
    },
    InvalidConfig: <AppError>{
        id: 8202,
        source: InfopaneSourceFile,
        severity,
        pb
    }
};

export const FeedbackLinkAppErrors = {
    /** The Feedback link failed to load valid config settings */
    FeedbackLinkFailedToFetchConfig: <AppError>{
        id: 8210,
        source: "FeedbackLink.connector.ts",
        severity,
        pb
    },
    /** Failed to get feedback dialog connector */
    FailedToGetFeedbackDialogConnector: <AppError>{
        id: 8211,
        source: "FeedbackLink.connector.ts",
        severity,
        pb
    },
    /** Failed to get feedback data connector */
    FailedToGetFeedbackDataConnector: <AppError>{
        id: 8212,
        source: "FeedbackLink.connector.ts",
        severity,
        pb
    }
};

const FeedbackServiceClientSourceFile = "FeedbackServiceClient.ts";
export const FeedbackDialogAppErrors = {
    /** Failed to get feedback data connector */
    FailedToGetFeedbackDataConnector: <AppError>{
        id: 8213,
        source: "FeedbackDialog.connector.ts",
        severity,
        pb
    },
    /** Request is not valid, it has no content, or the manifest is not formatted correctly */
    BadRequestReportingFeedback: <AppError>{
        id: 8214,
        source: FeedbackServiceClientSourceFile,
        severity,
        pb
    },
    /** Exception thrown when reporting feedback to OCV */
    ExceptionReportingFeedback: <AppError>{
        id: 8215,
        source: FeedbackServiceClientSourceFile,
        severity,
        pb
    }
};

const WindowsShellSettingFeedbackFileName = "HeaderWindowsShell.connector.ts";

/** The Setting Feedback menu failed to load valid config settings */
export const WindowsShellSettingFeedbackMenuFailedToFetchConfig: AppError = {
    id: 8216,
    source: WindowsShellSettingFeedbackFileName,
    severity,
    pb
};

/** The Setting Feedback menu Failed to get feedback dialog connector */
export const WindowsShellSettingFeedbackMenuFailedToGetFeedbackDialogConnector: AppError = {
    id: 8217,
    source: WindowsShellSettingFeedbackFileName,
    severity,
    pb
};

/** The Setting Feedback menu Failed to get feedback data connector */
export const WindowsShellSettingFeedbackMenuFailedToGetFeedbackDataConnector: AppError = {
    id: 8218,
    source: WindowsShellSettingFeedbackFileName,
    severity,
    pb
};

const OneFooterConnectorFileName = "OneFooter.connector.ts";
export const OneFooterErrors = {
    InvalidConfig: <AppError>{
        id: 8220,
        source: OneFooterConnectorFileName,
        severity,
        pb
    },
    /** Failed to get feedback data connector */
    FailedToGetFeedbackDataConnector: <AppError>{
        id: 8221,
        source: OneFooterConnectorFileName,
        severity,
        pb
    }
};

export const PoweredByLegendAppErrors = {
    /** The PoweredBy legend failed to load expected config settings */
    PoweredByLegendInvalidConfig: <AppError>{
        id: 8230,
        source: "PoweredByLegend.connector.ts",
        severity,
        pb
    }
};

const EdgeHeaderConnectorFileName = "EdgeHeader.connector.ts";

export const EdgeHeaderAppErrors = {
    /** The BingImageData Connector was not loaded */
    BingImageDataConnectorNotLoaded: <AppError>{
        id: 8240,
        source: EdgeHeaderConnectorFileName,
        severity,
        pb
    }
};

const MicrosoftNewsPageConnectorFileName = "MicrosoftNewsPage.connector.ts";

export const MicrosoftNewsPageAppErrors = {
    /** The BingImageData Connector was not loaded */
    BingImageDataConnectorNotLoaded: <AppError>{
        id: 8241,
        source: MicrosoftNewsPageConnectorFileName,
        severity,
        pb
    }
};

const WindowsPageConnectorFileName = "WindowsPage.connector.ts";

export const WindowsPageAppErrors = {
    /** The BingImageData Connector was not loaded */
    BingImageDataConnectorNotLoaded: <AppError>{
        id: 8242,
        source: WindowsPageConnectorFileName,
        severity,
        pb
    }
};

const TabbedInfopaneSourceFile = "TabbedInfopane.connector.ts";
export const TabbedInfopaneErrors = {
    InitializeError: <AppError>{
        id: 8250,
        source: TabbedInfopaneSourceFile,
        severity,
        pb
    },
    InvalidConfig: <AppError>{
        id: 8251,
        source: TabbedInfopaneSourceFile,
        severity,
        pb
    },
    ChildLoadError: <AppError>{
        id: 8252,
        source: TabbedInfopaneSourceFile,
        severity,
        pb
    },
    /** Problems with the second oneservice call that gathers the rest of the tabs*/
    SubTabFailure: <AppError>{
        id: 8253,
        source: TabbedInfopaneSourceFile,
        severity,
        pb
    }
};

export const MeControlAppErrors = {
    /** The SSO call failed to return valid SSO sign-in information. */
    SSOFailedError: <AppError>{
        id: 8260,
        source: "MeControl.tsx",
        severity,
        pb
    },
    /** The Me Control failed to load */
    MeControlLoadFailedError: <AppError>{
        id: 8261,
        source: "MeControl.tsx",
        severity: Severity.HighImpact,
        pb
    }
};

const TabbedInfopaneTabPreviewSourceFile = "TabbedInfopaneTabPreview.connector.ts";
export const TabbedInfopaneTabPreviewErrors = {
    InitError: <AppError>{
        id: 8270,
        source: TabbedInfopaneTabPreviewSourceFile,
        severity,
        pb
    },
    InvalidConfig: <AppError>{
        id: 8271,
        source: TabbedInfopaneTabPreviewSourceFile,
        severity,
        pb
    },
    LoadError: <AppError>{
        id: 8272,
        source: TabbedInfopaneTabPreviewSourceFile,
        severity,
        pb
    }
};

export const HeaderNavigationLogoAppErrors = {
    MegaMenuDataError: <AppError>{
        id: 8280,
        source: "HeaderNavigationLogoConnector.ts",
        severity,
        pb
    }
};

const pageClicksTrackerFileName = "pageClicksTracker.ts";
export const PageClickTrackerErrors = {
    InvalidResponse: <AppError>{
        id: 8290,
        severity: Severity.NoAlert,
        source: pageClicksTrackerFileName,
        pb
    },
    FetchFailure: <AppError>{
        id: 8291,
        severity: Severity.NoAlert,
        source: pageClicksTrackerFileName,
        pb
    }
};

const TargetingSystemSourceFile = "TargetingSystem.ts";
export const TargetingSystemAppErrors = {
    GeneralError: <AppError>{
        id: 8310,
        source: TargetingSystemSourceFile,
        severity,
        pb
    },
    TopLevelDataMissing: <AppError>{
        id: 8311,
        source: TargetingSystemSourceFile,
        severity,
        pb
    },
    TrackingDataUPSMissing: <AppError>{
        id: 8312,
        source: TargetingSystemSourceFile,
        severity,
        pb
    },
    AppEnvironmentValuesMissing: <AppError>{
        id: 8313,
        source: TargetingSystemSourceFile,
        severity,
        pb
    },
    CallingOneServiceFailed: <AppError>{
        id: 8314,
        source: TargetingSystemSourceFile,
        severity,
        pb
    },
    CallingUPSServiceFailed: <AppError>{
        id: 8315,
        source: TargetingSystemSourceFile,
        severity,
        pb
    }
};

const CoachmarkSourceFile = "Coachmark.connector.ts";
const CoachmarkTSX = "Coachmark.tsx";
const CoachmarkCTA = "Coachmark.cta.ts";
export const CoachmarkAppErrors = {
    GeneralError: <AppError>{
        id: 8320,
        source: CoachmarkTSX,
        severity,
        pb
    },
    NotPlaceable: <AppError>{
        id: 8321,
        source: CoachmarkTSX,
        severity: Severity.NoAlert,
        pb
    },
    NotFoundComponent: <AppError>{
        id: 8322,
        source: CoachmarkTSX,
        severity: Severity.NoAlert,
        pb
    },
    CoachmarkDataConnectorUndefined: <AppError>{
        id: 8323,
        source: CoachmarkSourceFile,
        severity,
        pb
    },
    InvalidCTAType: <AppError>{
        id: 8324,
        source: CoachmarkCTA,
        severity,
        pb
    },
    CTATypeNotDefined: <AppError>{
        id: 8325,
        source: CoachmarkCTA,
        severity,
        pb
    },
    ConfigDataUndefined: <AppError>{
        id: 8326,
        source: CoachmarkSourceFile,
        severity,
        pb
    },
    FailedToFetchConfigData: <AppError>{
        id: 8327,
        source: CoachmarkSourceFile,
        severity,
        pb
    },
    ExceptionFetchingConfigData: <AppError>{
        id: 8328,
        source: CoachmarkSourceFile,
        severity,
        pb
    },
    EventNotDefined: <AppError>{
        id: 8329,
        source: CoachmarkCTA,
        severity,
        pb
    },
    CoachmarkStateNotDefined: <AppError>{
        id: 8330,
        source: CoachmarkCTA,
        severity,
        pb
    }
};

const CoachmarkDataConnectorSourceFile = "CoachmarkData.connector.ts";
const CoachmarkPayloadSourceFile = "CoachmarkPayload.ts";
const IrisHandlerSourceFile = "IrisHandler.ts";
export const CoachmarkDataAppErrors = {
    InvalidCoachmarkMessage: <AppError>{
        id: 8331,
        source: CoachmarkPayloadSourceFile,
        severity,
        pb
    },
    IrisDataConnectorUndefined: <AppError>{
        id: 8332,
        source: IrisHandlerSourceFile,
        severity,
        pb
    },
    ErrorFoundForSurface: <AppError>{
        id: 8334,
        source: IrisHandlerSourceFile,
        severity,
        pb
    },
    TelemetryInfoUndefined: <AppError>{
        id: 8335,
        source: CoachmarkDataConnectorSourceFile,
        severity,
        pb
    },
    EventTypeUndefined: <AppError>{
        id: 8336,
        source: CoachmarkDataConnectorSourceFile,
        severity,
        pb
    },
    IrisActionUndefined: <AppError>{
        id: 8337,
        source: CoachmarkDataConnectorSourceFile,
        severity,
        pb
    }
};

const AppTraceFile = "AppTrace";
export const AppTraceErrors = {
    /** Error for when app trace upload fails */
    UploadError: <AppError>{
        id: 8350,
        source: AppTraceFile,
        severity,
        pb
    }
};

const ConditionalBannerPayloadSourceFile = "ConditionalBannerPayload.ts";
const ConditionalBannerIrisHandlerSourceFile = "ConditionalBannerIrisHandler.ts";

export const ConditionalBannerInvalidConditionalBannerMessage: AppError = {
    id: 8363,
    source: ConditionalBannerPayloadSourceFile,
    severity,
    pb
};

export const ConditionalBannerIrisDataConnectorUndefined: AppError = {
    id: 8364,
    source: ConditionalBannerIrisHandlerSourceFile,
    severity,
    pb
};

export const ConditionalBannerNoSurfaceFoundForCampaign: AppError = {
    id: 8365,
    source: ConditionalBannerIrisHandlerSourceFile,
    severity,
    pb
};

export const ConditionalBannerErrorFoundForSurface: AppError = {
    id: 8366,
    source: ConditionalBannerIrisHandlerSourceFile,
    severity,
    pb
};

export const ConditionalBannerTelemetryInfoUndefined: AppError = {
    id: 8367,
    source: ConditionalBannerIrisHandlerSourceFile,
    severity,
    pb
};

export const ConditionalBannerEventTypeUndefined: AppError = {
    id: 8368,
    source: ConditionalBannerIrisHandlerSourceFile,
    severity,
    pb
};

export const ConditionalBannerIrisActionUndefined: AppError = {
    id: 8369,
    source: ConditionalBannerIrisHandlerSourceFile,
    severity,
    pb
};

const SpotlightPreviewSourceFile = "SpotlightPreview.connector.ts";

export const SpotlightPreviewAppErrors = {
    ErrorWhileFetchingSpotlightPreviewCardInfo: <AppError>{
        id: 8401,
        source: SpotlightPreviewSourceFile,
        severity,
        pb
    },

    NoServiceContextMetadata: <AppError>{
        id: 8402,
        source: SpotlightPreviewSourceFile,
        severity,
        pb
    }
};

const SpotlightSourceFile = "Spotlight.connector.ts";
export const SpotlightErrors = {
    UnknownError: <AppError>{
        id: 8500,
        source: SpotlightSourceFile,
        severity,
        pb
    },

    ContentFetchError: <AppError>{
        id: 8501,
        source: SpotlightSourceFile,
        severity,
        pb
    },

    PartnerFetchFail: <AppError>{
        id: 8502,
        source: "PartnerService.ts",
        severity,
        pb
    }
};

export const CommentsErrors: { [key: string]: AppError } = {
    NoPost: {
        id: 8510,
        source: "Comments.connector.ts",
        severity,
        pb
    }
};

const CardActionServiceClientSourceFile = "CardActionServiceClient.ts";
export const CardActionErrors = {
    /** Error catch in base CardAction experience container */
    CardActionBaseExperienceError: <AppError>{
        id: 8600,
        source: "CardAction.tsx",
        severity,
        pb
    },
    /** Post action failed (Like/Dislike) */
    CardActionPostActionError: <AppError>{
        id: 8601,
        source: "CarActionServiceClient.tsx",
        severity,
        pb
    },
    /** Delete action failed (Unlike/Undislike) */
    CardActionDeleteActionError: <AppError>{
        id: 8602,
        source: "CardActionServiceClient.tsx",
        severity,
        pb
    },
    /** Get action failed (GetPublisherDetails) */
    CardActionGetActionError: <AppError>{
        id: 8603,
        source: "CardActionServiceClient.tsx",
        severity,
        pb
    },
    /** Request is not valid, it has no content, or the manifest is not formatted correctly */
    CardActionBadRequestReportingToOcv: <AppError>{
        id: 8604,
        source: CardActionServiceClientSourceFile,  // This error is used in both CardAction and CardActionV2 components
        severity,
        pb
    },
    /** Exception thrown when reporting to OCV */
    CardActionExceptionReportingToOcv: <AppError>{
        id: 8605,
        source: CardActionServiceClientSourceFile, // This error is used in both CardAction and CardActionV2 components
        severity,
        pb
    }
};

const ContentRelatedSourceFile = "ContentRelated.connector.ts";
export const ContentRelatedErrors = {
    /**
     * Missing source Id error
     */
    MissingSourceIdError: <AppError>{
        id: 8700,
        source: ContentRelatedSourceFile,
        severity,
        pb
    },

    /**
     * Invalid sourceIdError
     */
    InvalidSourceIdError: <AppError>{
        id: 8701,
        source: ContentRelatedSourceFile,
        severity,
        pb
    },

    /**
     * Related contents feed fetch error
     */
    ContentRelatedFeedFetchError: <AppError>{
        id: 8702,
        source: ContentRelatedSourceFile,
        severity,
        pb
    }
};

const CookieConsentBannerHelper = "MsccCookieConsentHelper.ts";
export const CookieConsentBannerHelperErrors = {
    FailedToParseStaticsUrls: <AppError>{
        id: 8810,
        source: CookieConsentBannerHelper,
        severity: Severity.HighImpact,
        pb
    },
    ScriptsNotFound: <AppError>{
        id: 8811,
        source: CookieConsentBannerHelper,
        severity: Severity.HighImpact,
        pb
    },
    StyleSheetsNotFound: <AppError>{
        id: 8812,
        source: CookieConsentBannerHelper,
        severity: Severity.HighImpact,
        pb
    },
    FailedToMountScript: <AppError>{
        id: 8813,
        source: CookieConsentBannerHelper,
        severity: Severity.HighImpact,
        pb
    },
    FailedToMountStyleSheet: <AppError>{
        id: 8814,
        source: CookieConsentBannerHelper,
        severity: Severity.HighImpact,
        pb
    }
};

/** Represents IFrame related errors */
const IFrameSourceFile = "IFrame.tsx";
export const IFrameErrors = {
    /** Message received by event listener not from expected origin */
    IFrameUnallowedOriginMessage: <AppError>{
        id: 8820,
        source: IFrameSourceFile,
        severity: Severity.NoAlert,
        pb
    }
};

export const RightRailCarouselAppErrors = {
    NoChildrenExperienceFoundError: <AppError>{
        id: 8830,
        source: "RightRailCarousel.connector.ts",
        severity,
        pb
    }
};

const Cookies = "Cookies.ts";
export const CookieRelatedErrors = {
    SharedStateConnectorNull: <AppError>{
        id: 8840,
        source: Cookies,
        severity,
        pb
    },

    CookieNotCategorized: <AppError>{
        id: 8841,
        source: Cookies,
        severity: Severity.HighImpact,
        pb
    }
};

export const BreakingNewsErrors = {
    /** Error fetching Breaking News content */
    ContentFetchError: <AppError>{
        id: 8901,
        source: "BreakingNews.connector.ts",
        severity,
        pb
    },
    /** Error fetching Blowout content */
    BlowoutDocumentFetchError: <AppError>{
        id: 8902,
        source: "BreakingNews.connector.ts",
        severity,
        pb
    }
};

export const BlowoutErrors = {
    /** Error fetching Blowout content */
    ContentFetchError: <AppError>{
        id: 8921,
        source: "Blowout.connector.ts",
        severity,
        pb
    }
};

export const OverlayErrors: { [name: string]: AppError } = {
    /** Error loading experience */
    LoadExperienceError: {
        id: 8941,
        source: "Overlay.connector.ts",
        severity,
        pb
    }
};

export const ViewsCommonAppErrors = {
    /** Invalid intra article component metadata passed. */
    InvalidIntraArticleDataError: <AppError>{
        id: 9001,
        source: "IntraArticleHelper.ts",
        severity,
        pb
    },

    /** Failed to fetch from data api. */
    DataFetchError: <AppError>{
        id: 9002,
        source: "ViewsServiceClient.ts",
        severity,
        pb
    },

    /** Invalid content markup encountered. */
    InvalidDocumentContentError: <AppError>{
        id: 9003,
        source: "ViewsUtility.ts",
        severity,
        pb
    },

    /** Failed to load views paddle data from data api. */
    ViewsPaddleDataLoadError: <AppError>{
        id: 9004,
        source: "ViewsPaddle.connector.ts",
        severity,
        pb
    },

    /** Error during app initialization for views page experience. */
    ViewsErrorPage: <AppError>{
        id: 9005,
        source: pageTsFileName,
        severity,
        pb
    },

    /** Error during mapping provider data for views content header experience */
    ProviderMapperError: <AppError>{
        id: 9006,
        source: "ViewContentHeader.connector.ts",
        severity,
        pb
    }
};

export const LegacySharingToolbarAppErrors = {
    /** Failed to invoke legacy sharing toolbar. */
    LegacySharingToolbarInvokeError: <AppError>{
        id: 9100,
        source: "SharingHelper.ts",
        severity,
        pb
    },

    /** Failed to load sharing toolbar. */
    SharingToolbarLoadError: <AppError>{
        id: 9101,
        source: "LegacySharingToolbar.connector.ts",
        severity,
        pb
    },

    /** Invalid source content id passed in. */
    InvalidContentIdError: <AppError>{
        id: 9102,
        source: "LegacySharingToolbar.connector.ts",
        severity,
        pb
    }
};

/** Represents ContentActionsToolbar experience related errors */
const ContentActionsToolbarConnectorSourceFile = "ContentActionToolbar.connector.ts";
const ContentActionsToolbarSourceFile = "ContentActionToolbar.tsx";
const ContentActionsToolbarServiceClientSourceFile = "ContentActionToolbarServiceClient.tsx";
export const ContentActionsToolbarAppErrors = {
    /** Failed to load sharing toolbar - 9120 */
    ContentActionsSharingToolbarLoadError: <AppError>{
        id: 9120,
        source: ContentActionsToolbarConnectorSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Error fetching article data - 9121 */
    ContentActionsArticleDatafetchError: <AppError>{
        id: 9121,
        source: ContentActionsToolbarConnectorSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Base Experience error - 9122 */
    ContentActionsBaseExperienceError: <AppError>{
        id: 9122,
        source: ContentActionsToolbarSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Get content views action failed (getContentViewsRequest) - 9123 */
    ContentActionsGetDocumentDataError: <AppError>{
        id: 9123,
        source: ContentActionsToolbarServiceClientSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Get content status action failed (getContentStatusRequest) - 9124 */
    ContentActionsGetContentStatusActionError: <AppError>{
        id: 9124,
        source: ContentActionsToolbarServiceClientSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Delete action failed (Unlike/Undislike) - 9125 */
    ContentActionsDeleteActionError: <AppError>{
        id: 9125,
        source: ContentActionsToolbarServiceClientSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Post action failed (Like/Dislike) - 9126 */
    ContentActionsPostActionError: <AppError>{
        id: 9126,
        source: ContentActionsToolbarServiceClientSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Prime hybrid error loading the experience - 9127 */
    ContentActionsExperienceLoadError: <AppError>{
        id: 9127,
        source: ContentActionsToolbarSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Missing vert/horiz placeholder to flip toolbar - 9128 */
    ContentActionsFlipToolbarError: <AppError>{
        id: 9128,
        source: ContentActionsToolbarSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Failed fetching contentStatus - 9129 */
    ContentActionsContentStatusFetchError: <AppError>{
        id: 9129,
        source: ContentActionsToolbarServiceClientSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    /** Failed fetching contentPreview - 9130 */
    ContentActionsContentPreviewFetchError: <AppError>{
        id: 9130,
        source: ContentActionsToolbarServiceClientSourceFile,
        severity: Severity.NoAlert,
        pb
    }
};

export const ContentSharingToolbarAppErrors = {
    ContentSharingToolbarInitializationError: <AppError>{
        id: 9150,
        source: "ContentSharingToolbar.connector.ts",
        severity,
        pb
    }
};

export const ContentSharingToolbarSaveForLaterServiceError: AppError = {
    id: 9151,
    source: "SaveForLaterServiceClient.ts",
    severity,
    pb
};

export const ContentProviderAppErrors = {
    /** Invalid source article/gallery id passed in. */
    InvalidSourceIdError: <AppError>{
        id: 9200,
        source: "ContentProvider.connector.ts",
        severity,
        pb
    },

    /** Failed to fetch provider details. */
    ProviderDetailsFetchError: <AppError>{
        id: 9201,
        source: "ContentProvider.connector.ts",
        severity,
        pb
    },

    /** Failed to fetch provider feed. */
    ProviderFeedFetchError: <AppError>{
        id: 9202,
        source: "ContentProvider.connector.ts",
        severity,
        pb
    },

    /** Invalid layout template passed in. */
    InvalidLayoutTemplate: <AppError>{
        id: 9203,
        source: "ContentProvider.templates.ts",
        severity,
        pb
    }
};

/** ApplicationCache related messages */
const refreshHeadDataFile = "Appcache/RefreshHeadDataAppCache.ts";
const edgeAppCacheHelperFile = "Appcache/EdgeApplicationCacheHelper.ts";
const refreshHeadDataAppCacheFile = "page-error-handling/AppCacheErrorHandler.ts";

export const ApplicationCacheMessages = {
    FailedToFetchHeadData: <AppError>{
        id: 9310,
        source: refreshHeadDataFile,
        severity,
        pb
    },
    CatastrophicFailedToFetchHeadData: <AppError>{
        id: 9311,
        source: refreshHeadDataFile,
        severity,
        pb
    },
    StaleActivityIdEncountered: <AppError>{
        id: 9312,
        source: refreshHeadDataFile,
        severity: Severity.NoAlert,
        pb
    },
    /**
     * The current DOM that is in AppCache doesn't match what should be getting served
     * to the user due to the existence of the ntps cookie that defines what the locale should be.
     * In this case, we must bypass AppCache to get the proper DOM from PCS.
     */
    LocaleMismatch: <AppError>{
        id: 9313,
        source: edgeAppCacheHelperFile,
        severity: Severity.NoAlert,
        pb
    },
    /**
     * Our checks determining we need to bypass appcache for the user are failing
     * to detect that the appcache refresh has succeeded. We've tried, for the current page view, to
     * bypass without any success.
     */
    RapidAppcacheForcedRefreshes: <AppError>{
        id: 9314,
        source: edgeAppCacheHelperFile,
        severity: Severity.NoAlert,
        pb
    },
    ApplicationCacheAPIErrorEvent: <AppError>{
        id: 9317,
        source: refreshHeadDataAppCacheFile,
        severity: Severity.NoAlert,
        pb
    },
    ApplicationCacheAPIObsoleteEvent: <AppError>{
        id: 9318,
        source: refreshHeadDataAppCacheFile,
        severity: Severity.NoAlert,
        pb
    }
};

const MsRewardsConnectorSourceFile = "MsRewards.connector.ts";
export const MsRewardsAppErrors = {
    RewardsDataConnectorUndefined: <AppError>{
        id: 9400,
        source: MsRewardsConnectorSourceFile,
        severity,
        pb
    },
    CategoryKeyOrAppTypeUndefined: <AppError>{
        id: 9401,
        source: MsRewardsConnectorSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    FailedToFetchCoachmarkUXConfig: <AppError>{
        id: 9402,
        source: MsRewardsConnectorSourceFile,
        severity: Severity.NoAlert,
        pb
    },
    ConfigDataUndefined: <AppError>{
        id: 9403,
        source: MsRewardsConnectorSourceFile,
        severity,
        pb
    },
    FailedToFetchConfigData: <AppError>{
        id: 9404,
        source: MsRewardsConnectorSourceFile,
        severity,
        pb
    },
    ExceptionFetchingConfigData: <AppError>{
        id: 9405,
        source: MsRewardsConnectorSourceFile,
        severity,
        pb
    },
    ChromiumPageSettingsDataConnectorUndefined: <AppError>{
        id: 9406,
        source: MsRewardsConnectorSourceFile,
        severity,
        pb
    },
    PageSettingsStateUndefined: <AppError>{
        id: 9407,
        source: MsRewardsConnectorSourceFile,
        severity,
        pb
    }
};

const RewardsDataConnectorSourceFile = "RewardsData.connector.ts";
const RewardsPayloadSourceFile = "RewardsPayload.ts";
const RewardsServiceClientSourceFile = "RewardsServiceClient.ts";
export const RewardsDataAppErrors = {
    IrisDataConnectorUndefined: <AppError>{
        id: 9421,
        source: IrisHandlerSourceFile,
        severity,
        pb
    },
    CoachmarkDataConnectorUndefined: <AppError>{
        id: 9424,
        source: RewardsDataConnectorSourceFile,
        severity,
        pb
    },
    ErrorFetchingUserProfile: <AppError>{
        id: 9425,
        source: RewardsServiceClientSourceFile,
        severity,
        pb
    },
    ExceptionFetchingUserProfile: <AppError>{
        id: 9426,
        source: RewardsServiceClientSourceFile,
        severity,
        pb
    },
    ExceptionReportingRewardActivity: <AppError>{
        id: 9427,
        source: RewardsServiceClientSourceFile,
        severity,
        pb
    },
    BadRequestReportingRewardActivity: <AppError>{
        id: 9428,
        source: RewardsServiceClientSourceFile,
        severity,
        pb
    },
    OfferIdNotDefined: <AppError>{
        id: 9429,
        source: RewardsDataConnectorSourceFile,
        severity,
        pb
    },
    ActionNameNotDefined: <AppError>{
        id: 9430,
        source: RewardsDataConnectorSourceFile,
        severity,
        pb
    },
    InvalidParameters: <AppError>{
        id: 9431,
        source: RewardsServiceClientSourceFile,
        severity,
        pb
    },
    UnknownErrorsReportingRewardActivity: <AppError>{
        id: 9432,
        source: RewardsServiceClientSourceFile,
        severity,
        pb
    },
    UnknownErrorOccurred: <AppError>{
        id: 9433,
        source: RewardsServiceClientSourceFile,
        severity,
        pb
    },
    MaxRetryReachedForUpdatingRewardsPoints: <AppError>{
        id: 9434,
        source: RewardsDataConnectorSourceFile,
        severity,
        pb
    }
};

const TrendingNewsCardConnectorFile = "mobile-feed/trending-news/TrendingNews.connector.ts";
/** Failed to fetch the trending news card in mobile feed */
export const TrendingNewsDataFetchError: AppError = {
    id: 9501,
    source: TrendingNewsCardConnectorFile,
    severity,
    pb
};

/** Failed to parse JSON data for trending news in mobile feed */
export const TrendingNewsDataParseError: AppError = {
    id: 9502,
    source: TrendingNewsCardConnectorFile,
    severity,
    pb
};

/** Received null or empty response for trending news in mobile feed */
export const TrendingNewsEmptyResponseDataError: AppError = {
    id: 9503,
    source: TrendingNewsCardConnectorFile,
    severity,
    pb
};

const ShoppingServiceClientSourceFile = "mobile-feed/shopping-connector/ShoppingServiceClient.ts";
const ShoppingWpoDataMapperFile = "libs/feed-layout/.../ShoppingCarouselCardDataMapper.ts";
/** Received null or empty response for ShoppingServiceClient */
export const ShoppingTrendingProductDataError: AppError = {
    id: 9504,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingMetaDataError: AppError = {
    id: 9505,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingRecentPriceDropsDataError: AppError = {
    id: 9506,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingRecommendedForYouProductsDataError: AppError = {
    id: 9507,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingRecommendedForYouProductDealsDataError: AppError = {
    id: 9508,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingMomentInTimeDataError: AppError = {
    id: 9509,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingForYouDataError: AppError = {
    id: 9510,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingPopularProductDealDataError: AppError = {
    id: 9511,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingMerchantDealDataError: AppError = {
    id: 9512,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingMerchantCatDealDataEnUsError: AppError = {
    id: 9513,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingMerchantCatDealDataEnGbError: AppError = {
    id: 9514,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingTrendingCatDataError: AppError = {
    id: 9515,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingTrendingCatProductsDataError: AppError = {
    id: 9516,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingTrendingCatDealsDataError: AppError = {
    id: 9517,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingTrendingDealsDataError: AppError = {
    id: 9518,
    source: ShoppingServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingTrendingDataMapperError: AppError = {
    id: 9519,
    source: ShoppingWpoDataMapperFile,
    severity,
    pb
};

const LocalNewsCardConnectorFile = "LocalNews.connector.ts";
/** Failed to fetch the local news card in mobile feed*/
export const LocalNewsDataFetchError: AppError = {
    id: 9601,
    source: LocalNewsCardConnectorFile,
    severity,
    pb
};

/** Failed to parse JSON data for local news in mobile feed */
export const LocalNewsDataParseError: AppError = {
    id: 9602,
    source: LocalNewsCardConnectorFile,
    severity,
    pb
};

/** Received null or empty response for local news in mobile feed */
export const LocalNewsEmptyResponseDataError: AppError = {
    id: 9603,
    source: LocalNewsCardConnectorFile,
    severity,
    pb
};

const MobileFeedConnectorFile = "mobile-feed/MobileFeed.connector.ts";
/** Failed to prefetch data for mobile feed in super app */
export const MobileFeedPrefetchError: AppError = {
    id: 9701,
    source: MobileFeedConnectorFile,
    severity,
    pb
};

/** Failed to JSON prase prefetched data for mobile feed in super app */
export const MobileFeedPrefetchParseError: AppError = {
    id: 9702,
    source: MobileFeedConnectorFile,
    severity,
    pb
};

/** Sesion id not found on next page url in oneservice response */
export const MobileFeedSessionIdNotFound: AppError = {
    id: 9703,
    source: MobileFeedConnectorFile,
    severity,
    pb
};

/** Failed to fetch mobile feed data from bridge get http method. */
export const MobileFeedBridgeGetHttpError: AppError = {
    id: 9704,
    source: MobileFeedConnectorFile,
    severity,
    pb
};

/** Failed to JSON parse mobile feed data from bridge get http method. */
export const MobileFeedBridgeGetHttpParseError: AppError = {
    id: 9705,
    source: MobileFeedConnectorFile,
    severity,
    pb
};

/** Timeout in fetching mobile feed data from bridge get http method */
export const MobileFeedBridgeGetHttpTimeout: AppError = {
    id: 9706,
    source: MobileFeedConnectorFile,
    severity,
    pb
};

/** Failed to fetch mobile feed data from network get http method. */
export const MobileFeedNetworkGetHttpError: AppError = {
    id: 9707,
    source: MobileFeedConnectorFile,
    severity,
    pb
};

/** Failed to JSON parse mobile feed data from bridge get http method. */
export const MobileFeedNetworkGetHttpParseError: AppError = {
    id: 9708,
    source: MobileFeedConnectorFile,
    severity,
    pb
};

/** Recieved empty data from Oneservice network call. */
export const MobileFeedNetworkEmptyData: AppError = {
    id: 9709,
    source: MobileFeedConnectorFile,
    severity,
    pb
};

const MobileFeedShoppingFile = "mobile-feed/shopping-connector/ShoppingServiceClient.ts";
/** Failed to prefetch data for mobile feed shopping in super app */
export const MobileFeedShoppingPrefetchError: AppError = {
    id: 9711,
    source: MobileFeedShoppingFile,
    severity,
    pb
};

/** Failed to JSON prase prefetched data for mobile feed shopping in super app */
export const MobileFeedShoppingPrefetchParseError: AppError = {
    id: 9712,
    source: MobileFeedShoppingFile,
    severity,
    pb
};

const MobileFeedFinanceGlanceCardFile = "mobile-feed/glance-stripe/FinanceGlanceCard.connector.ts";
/** Failed to prefetch data for mobile feed fiance glance card in super app */
export const MobileFeedFinanceGlanceCardPrefetchError: AppError = {
    id: 9721,
    source: MobileFeedFinanceGlanceCardFile,
    severity,
    pb
};

/** Failed to JSON prase prefetched data for mobile feed finance glance card in super app */
export const MobileFeedFinanceGlanceCardPrefetchParseError: AppError = {
    id: 9722,
    source: MobileFeedFinanceGlanceCardFile,
    severity,
    pb
};

const MobileFeedEsportsGlanceCardFile = "mobile-feed/glance-stripe/EsportsGlanceCard.connector.ts";
/** Failed to prefetch data for mobile feed Esports glance card in super app */
export const MobileFeedEsportsGlanceCardPrefetchError: AppError = {
    id: 9731,
    source: MobileFeedEsportsGlanceCardFile,
    severity,
    pb
};

/** Failed to JSON prase prefetched data for mobile feed Esports glance card in super app */
export const MobileFeedEsportsGlanceCardPrefetchParseError: AppError = {
    id: 9732,
    source: MobileFeedEsportsGlanceCardFile,
    severity,
    pb
};

const SuperAppHomepageFile = "app-types/superapp/Homepage.ts";
const SuperAppPersonalizeFile = "app-types/superapp/Personalize.ts";
/** Failed to prefetch mobile feed config in super app */
export const MobileFeedConfigPrefetchError: AppError = {
    id: 9741,
    source: SuperAppHomepageFile,
    severity,
    pb
};

/** Failed to JSON prase prefetched config for mobile feed in super app */
export const MobileFeedConfigPrefetchParseError: AppError = {
    id: 9742,
    source: SuperAppHomepageFile,
    severity,
    pb
};

/** Failed to fetch mobile feed config in super app */
export const MobileFeedConfigFetchError: AppError = {
    id: 9743,
    source: SuperAppHomepageFile,
    severity,
    pb
};

/** Failed to JSON prase fetched config for mobile feed in super app */
export const MobileFeedConfigFetchParseError: AppError = {
    id: 9744,
    source: SuperAppHomepageFile,
    severity,
    pb
};

/** Failed to generate config url in super app homepage */
export const MobileFeedConfigGetUrlError: AppError = {
    id: 9745,
    source: SuperAppHomepageFile,
    severity,
    pb
};

/** Failed to fetch InterestV3 config in super app */
export const InterestV3ConfigFetchError: AppError = {
    id: 9746,
    source: SuperAppPersonalizeFile,
    severity,
    pb
};

/** Failed to fetch InterestV3 config in super app */
export const InterestV3ConfigGetUrlError: AppError = {
    id: 9747,
    source: SuperAppPersonalizeFile,
    severity,
    pb
};

/** Failed to parse super app homepage user config object from local storage */
export const localStorageUserConfigObjectParseError: AppError = {
    id: 9748,
    source: SuperAppHomepageFile,
    severity,
    pb
};

/** Failed to write super app homepage user config object in to local storage */
export const localStorageUserConfigObjectWriteError: AppError = {
    id: 9749,
    source: SuperAppHomepageFile,
    severity,
    pb
};

/** Timeout in fetching config for mobile feed in super app  */
export const MobileFeedConfigFetchTimeoutError: AppError = {
    id: 9750,
    source: SuperAppHomepageFile,
    severity,
    pb
};

/** Failed to fetch mobile feed config via network in super app */
export const MobileFeedConfigNetworkFetchError: AppError = {
    id: 9751,
    source: SuperAppHomepageFile,
    severity,
    pb
};

/** Failed to JSON prase mobile feed config fetched via network in super app */
export const MobileFeedConfigNetworkFetchParseError: AppError = {
    id: 9752,
    source: SuperAppHomepageFile,
    severity,
    pb
};

const MobileFeedActionMenuHelperFile = "ActionMenuHelper.ts";
/** OneService call failed for action menu Actions type */
export const MobileFeedActionMenuServiceCallFailedError: AppError = {
    id: 9760,
    source: MobileFeedActionMenuHelperFile,
    severity,
    pb
};

/** Action menu Sapphire bridge api returned with error response */
export const MobileFeedActionMenuServiceCallParsedError: AppError = {
    id: 9761,
    source: MobileFeedActionMenuHelperFile,
    severity,
    pb
};

/** Action menu Sapphire bridge api returned with error response */
export const MobileFeedActionMenuBridgeCallFailedError: AppError = {
    id: 9762,
    source: MobileFeedActionMenuHelperFile,
    severity,
    pb
};

const TopicCarouselConnectorFile = "TopicCarousel.connector.ts";
/** Failed to fetch the topic carousel in mobile feed*/
export const TopicCarouselDataFetchError: AppError = {
    id: 9801,
    source: TopicCarouselConnectorFile,
    severity,
    pb
};

/** Received null or empty response for topic carousel in mobile feed */
export const TopicCarouselEmptyResponseDataError: AppError = {
    id: 9802,
    source: TopicCarouselConnectorFile,
    severity,
    pb
};

/** Failed to parse content url */
export const FailedToParseContentUrl: AppError = {
    id: 9851,
    source: "mobile-feed/src/helpers/common.ts",
    severity,
    pb
};

/** Screen width in SA is zero */
export const ZeroScreenWidth: AppError = {
    id: 9861,
    source: "mobile-feed/src/helpers/DisplayHelper.ts",
    severity,
    pb
};

const MobileFeedRewardsGlanceCardFile = "mobile-feed/src/glance-stripe/rewards/RewardsGlanceCard.connector.ts";
/** Failed to prefetch data for mobile feed rewards glance card in super app */
export const MobileFeedRewardsGlanceCardPrefetchError: AppError = {
    id: 9871,
    source: MobileFeedRewardsGlanceCardFile,
    severity,
    pb
};

/** Failed to JSON prase prefetched data for mobile feed finance glance card in super app */
export const MobileFeedRewardsGlanceCardPrefetchParseError: AppError = {
    id: 9872,
    source: MobileFeedRewardsGlanceCardFile,
    severity,
    pb
};

const VideoCarouselConnectorFile = "mobile-feed/video-carousel/VideoCarousel.connector.ts";
/** Failed to parse video data in mobile feed */
export const VideoCarouselDataParseError: AppError = {
    id: 9901,
    source: VideoCarouselConnectorFile,
    severity,
    pb
};

/** Received null or empty response for video carousel in mobile feed */
export const VideoCarouselEmptyResponseDataError: AppError = {
    id: 9902,
    source: VideoCarouselConnectorFile,
    severity,
    pb
};

/** Failed to fetch the video carousel card in mobile feed */
export const VideoCarouselDataFetchError: AppError = {
    id: 9903,
    source: VideoCarouselConnectorFile,
    severity,
    pb
};

const ViewsGallerySourceFile = "ViewsGallery.connector.ts";
export const ViewsGalleryAppErrors = {
    /** Invalid gallery id passed. */
    InvalidGalleryIdError: <AppError>{
        id: 10001,
        source: ViewsGallerySourceFile,
        severity,
        pb
    },

    /** Failed to load gallery data. */
    GalleryDataLoadError: <AppError>{
        id: 10010,
        source: ViewsGallerySourceFile,
        severity,
        pb
    },

    /** Failed to load images. */
    GalleryImagesLoadError: <AppError>{
        id: 10020,
        source: ViewsGallerySourceFile,
        severity,
        pb
    }
};

const SlideShowConnectorFile = "SlideShow.connector.ts";
const SlideShowCarouselFile = "SlideShowCarousel.tsx";
export const SlideShowAppErrors = {
    /** Invalid slideshow id passed. */
    InvalidSlideShowIdError: <AppError>{
        id: 10100,
        source: SlideShowConnectorFile,
        severity,
        pb
    },

    /** Failed to load slideshow data. */
    SlideShowDataLoadError: <AppError>{
        id: 10110,
        source: SlideShowConnectorFile,
        severity,
        pb
    },

    /** Failed to load images. */
    SlideShowImagesLoadError: <AppError>{
        id: 10120,
        source: SlideShowConnectorFile,
        severity,
        pb
    },

    /** Failed to ads refresh. */
    SlideShowAdsRefreshError: <AppError>{
        id: 10130,
        source: SlideShowCarouselFile,
        severity,
        pb
    },

    /** Failed to load image. */
    SlideShowImageLoadFailed: <AppError>{
        id: 10135,
        source: SlideShowCarouselFile,
        severity,
        pb
    }
};

export const VideoListAppErrors = {
    /** Failed to load the video list data. */
    VideoListDataLoadError: <AppError>{
        id: 11001,
        source: "VideoList.connector.ts",
        severity,
        pb
    }
};

const VideoContentSourceFile = "VideoContent.connector.ts";
export const VideoContentAppErrors = {
    /** Failed to load the video content data. */
    InvalidResponseFromVideoContentResponseJson: <AppError>{
        id: 11101,
        source: VideoContentSourceFile,
        severity
    },
    /** Failed to fetch the video content data from server. */
    ErrorWhileFetchingContentPreviewContent: <AppError>{
        id: 11102,
        source: VideoContentSourceFile,
        severity
    }
};

const VideoContentDownScriptFile = "AdsHelper.ts";
/** Failed to download external script file for content video player */
export const DownloadScriptError: AppError = {
    id: 11103,
    source: VideoContentDownScriptFile,
    severity,
    pb
};

const ViewsArticleSourceFile = "ViewsArticle.connector.ts";
export const ViewsArticleAppErrors = {
    /** Invalid article id passed. */
    InvalidArticleIdError: <AppError>{
        id: 12001,
        source: ViewsArticleSourceFile,
        severity,
        pb
    },

    /** Failed to load article data. */
    ArticleDataLoadError: <AppError>{
        id: 12002,
        source: ViewsArticleSourceFile,
        severity,
        pb
    },

    /** Failed to load intra-article component. */
    IntraArticleComponentLoadError: <AppError>{
        id: 12003,
        source: ViewsArticleSourceFile,
        severity,
        pb
    },

    /** Failed to invoke inline gallery experience. */
    InlineGalleryInvokeError: <AppError>{
        id: 12004,
        source: ViewsArticleSourceFile,
        severity,
        pb
    }
};

/**
 * Set of errors in Article Page.
 */
const ArticleParserFileName = "ArticleParser.ts";
const ArticleVideoParserFileName = "VideoParser.ts";
export const ArticleInvalidContentError: AppError = {
    id: 12100,
    source: ArticleParserFileName,
    severity,
    pb
};

export const ArticleInvalidVideoIdError: AppError = {
    id: 12101,
    source: ArticleParserFileName,
    severity,
    pb
};

export const ArticleInvalidSlideshowIdError: AppError = {
    id: 12102,
    source: ArticleParserFileName,
    severity,
    pb
};

export const ArticleInvalidVideoContentError: AppError = {
    id: 12103,
    source: ArticleVideoParserFileName,
    severity,
    pb
};

export const ArticleCopyrightTruncateError: AppError = {
    id: 12104,
    source: ArticleParserFileName,
    severity,
    pb
};

export const ArticleCreateElementError: AppError = {
    id: 12105,
    source: ArticleParserFileName,
    severity,
    pb
};

export const InterestsSearchAppErrors = {
    /** Received non 200 from OneService */
    ErrorResponseFromOneService: <AppError>{
        id: 13000,
        source: "InterestsSearchServiceClient.ts",
        severity,
        pb
    },
    /** Received a response from 200 but didn't have expected structure */
    ErrorResponseFromOneServiceIsInvalid: <AppError>{
        id: 13001,
        source: "InterestsSearchServiceClient.ts",
        severity,
        pb
    },
    /**
     * When building the XFeed URL for one of the suggestion items,
     *  we weren't able to build a valid URL
     */
    InvalidUrlFoundForElement: <AppError>{
        id: 13002,
        source: "InterestsSearch.connector.ts",
        severity,
        pb
    },
    /**
     * When building the XFeed URL for one of the suggestion items,
     *  we weren't able to build a valid URL
     */
    SelectedItemNotFoundInSuggestions: <AppError>{
        id: 13003,
        source: "InterestsSearch.connector.ts",
        severity,
        pb
    },
    /**
     * When building the XFeed URL for one of the suggestion items,
     *  we weren't able to build a valid URL
     */
    SelectedItemIsInvalid: <AppError>{
        id: 13004,
        source: "InterestsSearch.connector.ts",
        severity,
        pb
    }
};

const InterestsConnectorFileName = "Interests.connector.ts";
const SettingsConnectorManagerFileName = "Setting.connector.manager.ts";
const TopicDataConnectorFileName = "TopicData.connector.ts";
const TopicCacheFileName = "TopicCache.ts";
const XfeedConnectorFileName = "Xfeed.connector.ts";
export const InterestsAppErrors = {
    /** We could not retrieve the list of Topics from OneService */
    InterestsRetrieveTopicsError: <AppError>{
        id: 13100,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** We could not retrieve the user's list of Followed Topics from OneService */
    InterestsRetrieveFollowedTopicsError: <AppError>{
        id: 13101,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** We could not retrieve the user's list of Muted Publishers from OneService */
    InterestsRetrieveMutedPublishersError: <AppError>{
        id: 13102,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** We could not UnMute a Muted Publisher */
    InterestsUnMutePublisherError: <AppError>{
        id: 13103,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** We could not follow a new interests topic */
    InterestsFollowTopicError: <AppError>{
        id: 13104,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** We could not unfollow an interests topic */
    InterestsUnfollowTopicError: <AppError>{
        id: 13105,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** TopicState should not be undefined */
    InterestsTopicStateUndefined: <AppError>{
        id: 13106,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** Topics should have rendered */
    InterestsShouldHaveRendered: <AppError>{
        id: 13107,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** Could not retrieve a specific topic */
    InterestsRetrieveTopicError: <AppError>{
        id: 13108,
        source: XfeedConnectorFileName,
        severity,
        pb
    },
    /** We could not retrieve the user's list of Saved Stories from OneService */
    InterestsRetrieveSavedStoriesError: <AppError>{
        id: 13109,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** Saved stories config is invalid */
    InterestsSavedStoriesInvalidConfigError: <AppError>{
        id: 13110,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** We could not load saved stories children */
    InterestsSavedStoriesChildLoadError: <AppError>{
        id: 13111,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** OneService response for saved stories had invalid json */
    InterestsSavedStoriesInvalidJsonError: <AppError>{
        id: 13112,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** OneService response for saved stories had invalid list of response */
    InterestsSavedStoriesInvalidListError: <AppError>{
        id: 13113,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** We could not retrieve the user's list of Recommended Topics from OneService */
    InterestsRetrieveRecommendedTopicsError: <AppError>{
        id: 13114,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** Topic Data Connector is not defined */
    UndefinedTopicDataConnector: <AppError>{
        id: 13115,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** We could not retrieve the user's list of Read Stories from OneService */
    InterestsRetrieveReadStoriesError: <AppError>{
        id: 13116,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** Read stories config is invalid */
    InterestsReadStoriesInvalidConfigError: <AppError>{
        id: 13117,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** We could not load Read stories children */
    InterestsReadStoriesChildLoadError: <AppError>{
        id: 13118,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** OneService response for Read stories had invalid json */
    InterestsReadStoriesInvalidJsonError: <AppError>{
        id: 13119,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** OneService response for Read stories had invalid list of response */
    InterestsReadStoriesInvalidListError: <AppError>{
        id: 13120,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** OneService failed resetting user default set of interests */
    FailedRestoringDefaultTopicsError: <AppError>{
        id: 13121,
        source: InterestsConnectorFileName,
        severity,
        pb
    },
    /** Failed opening topics IndexedDB connection */
    TopicsFailedIndexedDBConnection: <AppError>{
        id: 13122,
        source: TopicCacheFileName,
        severity,
        pb
    },
    /** Failed getting topics indexedDB */
    TopicsFailedGettingIndexedDB: <AppError>{
        id: 13123,
        source: TopicCacheFileName,
        severity,
        pb
    },
    /** Failed updating topics indexedDB */
    TopicsFailedUpdatingIndexedDB: <AppError>{
        id: 13124,
        source: TopicCacheFileName,
        severity,
        pb
    }
};

/** Failed updating segment card user settings to one service */
export const FailedUpdatingUserSegmentCardsPreferences: AppError = {
    id: 13125,
    source: SettingsConnectorManagerFileName,
    severity,
    pb
};

/** Failed while checking if topic has limited data in feed */
export const TopicsFailedCheckingLimitedFeed: AppError = {
    id: 13126,
    source: TopicDataConnectorFileName,
    severity,
    pb
};

/** We could not retrieve the user's list of Followed Publishers from OneService */
export const InterestsRetrieveFollowedPublishersError: AppError = {
    id: 13127,
    source: InterestsConnectorFileName,
    severity,
    pb
};

/** Could not follow a publisher */
export const PublisherFollowTopicError: AppError = {
    id: 13128,
    source: InterestsConnectorFileName,
    severity,
    pb
};

/** Could not unfollow a publisher */
export const PublisherUnfollowTopicError: AppError = {
    id: 13129,
    source: InterestsConnectorFileName,
    severity,
    pb
};

/** We could not retrieve the user's list of Work Topics from OneService */
export const InterestsRetrieveWorkTopicsError: AppError = {
    id: 13130,
    source: InterestsConnectorFileName,
    severity,
    pb
};

const ComponentConnector = "ComponentConnector.ts";

/** {ComponentConnector} base app errors */
export const ComponentConnectorAppErrors = {

    /** Duplicate child experience instance found */
    DuplicateChildInstanceFound: <AppError>{
        id: 14000,
        source: ComponentConnector,
        severity,
        pb
    },

    /** Failed to load bundle */
    BundleLoadError: <AppError>{
        id: 14001,
        source: ComponentConnector,
        severity,
        pb
    },

    /** ChildConfigInfoList is null */
    ChildConfigInfoListNull: <AppError>{
        id: 14002,
        source: ComponentConnector,
        severity,
        pb
    },

    /** Merge List start position must be greater than 0 */
    ListStartPositionInvalid: <AppError>{
        id: 14003,
        source: ComponentConnector,
        severity,
        pb
    },

    /** Failed to delay load child bundle */
    ChildDelayLoadError: <AppError>{
        id: 14004,
        source: ComponentConnector,
        severity,
        pb
    },

    /** Milestone dependency failed to resolve and timeout triggered */
    ExperienceDependencyTimeout: <AppError>{
        id: 14005,
        source: ComponentConnector,
        severity: Severity.NoAlert,
        pb
    }
};

/** Data connector loading failed */
export const LoadDataConnectorConfigFailed: AppError = {
    id: 14006,
    source: ComponentConnector,
    severity,
    pb
};

/** Data connector loading failed */
export const LoadDataConnectorFailed: AppError = {
    id: 14007,
    source: ComponentConnector,
    severity,
    pb
};

/** Attempted to load child with invalid experience */
export const ChildWithInvalidExperience: AppError = {
    id: 14008,
    source: ComponentConnector,
    severity,
    pb
};

/** App errors for {ExperienceLoader} */
export const ExperienceLoaderAppErrors = {
    /** Logged when neither the config type nor config HREF was provided to the loader */
    InsufficientLoadArgs: <AppError>{
        id: 14100,
        source: "ExperienceLoader.ts",
        severity: Severity.HighImpact,
        pb
    }
};

/** Logged when ConnectComponent call in getConnectedExperience fails */
export const ConnectComponentFailureError: AppError = {
    id: 14101,
    source: "ExperienceLoader.ts",
    severity,
    pb
};

/** Logged when loadComponent fails */
export const LoadComponentFailureError: AppError = {
    id: 14102,
    source: "ExperienceLoader.ts",
    severity,
    pb
};

/** Represents SearchHistoryCommon related App errors */
const SearchHistoryCommonSourceFile = "SearchHistoryCommon.tsx";
export const SearchHistoryCommonAppErrors = {
    /** Failed to parse search history response */
    ResponseParsingFailed: <AppError>{
        id: 15000,
        source: SearchHistoryCommonSourceFile,
        severity,
        pb
    },

    /** Search history service call failed */
    ServiceCallFailed: <AppError>{
        id: 15001,
        source: SearchHistoryCommonSourceFile,
        severity,
        pb
    },

    /** Failed to render search history */
    RenderFailed: <AppError>{
        id: 15002,
        source: SearchHistoryCommonSourceFile,
        severity,
        pb
    },

    /** Search history api response not received within configured time */
    Timeout: <AppError>{
        id: 15003,
        source: SearchHistoryCommonSourceFile,
        severity,
        pb
    },

    /** Search history api call unauthorized */
    Unauthorized: <AppError>{
        id: 15004,
        source: SearchHistoryCommonSourceFile,
        severity,
        pb
    },

    /** Search history api call forbidden */
    Forbidden: <AppError>{
        id: 15005,
        source: SearchHistoryCommonSourceFile,
        severity,
        pb
    },

    /** Search history api not able to find recent search associated with this request */
    NotFound: <AppError>{
        id: 15006,
        source: SearchHistoryCommonSourceFile,
        severity,
        pb
    },

    /** Search history api response not received within configured time but wait for searchHistoryFetchPromise to be completed */
    TimeoutDetail: <AppError>{
        id: 15007,
        source: SearchHistoryCommonSourceFile,
        severity,
        pb
    }
};

/** Represents search history edgenext failed to render */
export const SearchHistoryEdgenextRenderFailed: AppError = {
    id: 15008,
    source: "SearchHistoryEdgenext.tsx",
    severity: Severity.HighImpact,
    pb
};

const SearchHistoryEdgeNextSourceFile = "SearchHistoryEdgenext.connector.ts";
/** Represents failed to retrieve account type */
export const FailedToRetrieveAccountType: AppError = {
    id: 15009,
    source: SearchHistoryEdgeNextSourceFile,
    severity: Severity.NoAlert,
    pb
};

/** Represents failed to retrieve account info */
export const FailedToRetrieveAccountInfo: AppError = {
    id: 15010,
    source: SearchHistoryEdgeNextSourceFile,
    severity: Severity.NoAlert,
    pb
};

/** Represents failed to get access token */
export const FailedToGetAccessToken: AppError = {
    id: 15011,
    source: SearchHistoryEdgeNextSourceFile,
    severity: Severity.NoAlert,
    pb
};

const SearchHistoryServiceSourceFile = "SearchHistoryService.ts";
export const SearchHistoryResponseParsingFailed: AppError = {
    id: 15020,
    source: SearchHistoryServiceSourceFile,
    severity,
    pb
};

/** Search history service call failed */
export const SearchHistoryServiceCallFailed: AppError = {
    id: 15021,
    source: SearchHistoryServiceSourceFile,
    severity,
    pb
};

/** Failed to render search history */
export const SearchHistoryRenderFailed: AppError = {
    id: 15022,
    source: SearchHistoryServiceSourceFile,
    severity,
    pb
};

/** Search history api response not received within configured time */
export const SearchHistoryTimeout: AppError = {
    id: 15023,
    source: SearchHistoryServiceSourceFile,
    severity,
    pb
};

/** Search history api call unauthorized */
export const SearchHistoryUnauthorized: AppError = {
    id: 15024,
    source: SearchHistoryServiceSourceFile,
    severity,
    pb
};

/** Search history api call forbidden */
export const SearchHistoryForbidden: AppError = {
    id: 15025,
    source: SearchHistoryServiceSourceFile,
    severity,
    pb
};

/** Search history api not able to find recent search associated with this request */
export const SearchHistoryNotFound: AppError = {
    id: 15026,
    source: SearchHistoryServiceSourceFile,
    severity,
    pb
};

/** Search history api response not received within configured time but wait for searchHistoryFetchPromise to be completed */
export const SearchHistoryTimeoutDetail: AppError = {
    id: 15027,
    source: SearchHistoryServiceSourceFile,
    severity,
    pb
};

const SearchHistoryEdgeNextWCSourceFile = "SearchHistoryEdgeNextWC.ts";
/** Represents failed to retrieve account type */
export const SearchHistoryFailedToRetrieveAccountType: AppError = {
    id: 15029,
    source: SearchHistoryEdgeNextWCSourceFile,
    severity: Severity.NoAlert,
    pb
};

/** Represents failed to retrieve account info */
export const SearchHistoryFailedToRetrieveAccountInfo: AppError = {
    id: 15030,
    source: SearchHistoryEdgeNextWCSourceFile,
    severity: Severity.NoAlert,
    pb
};

/** Represents failed to get acces s token */
export const SearchHistoryFailedToGetAccessToken: AppError = {
    id: 15031,
    source: SearchHistoryEdgeNextWCSourceFile,
    severity: Severity.NoAlert,
    pb
};

/** Represents a Search related error */
export const SearchError: AppError = {
    id: 15100,
    source: "SearchBox.tsx",
    severity: Severity.HighImpact,
    pb
};

/** Represents missing form code/parameters related App errors */
export const MissingFormCode: AppError = {
    id: 15101,
    source: "SearchBoxCommon.connector.ts",
    severity,
    pb
};

/** Represents RecommendedSearch related App errors */
const RecommendedSearchSourceFile = "RecommendedSearchDataService.ts";
export const RecommendedSearchErrors = {
    /** Warning for failed to parse recommended search response */
    ResponseParsingFailed: <AppError>{
        id: 15200,
        source: RecommendedSearchSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /** warning for recommended search service returned invalid Json response */
    InvalidJsonResponse: <AppError>{
        id: 15201,
        source: RecommendedSearchSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /** Warning for recommended search service call failed */
    ServiceCallFailed: <AppError>{
        id: 15202,
        source: RecommendedSearchSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /** Failed to render recommended searches */
    RenderFailed: <AppError>{
        id: 15203,
        source: RecommendedSearchSourceFile,
        severity,
        pb
    },

    /** Warning for recommended search api response not received within configured time */
    Timeout: <AppError>{
        id: 15204,
        source: RecommendedSearchSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /** recommended search back fill failed to fetch content from CMS */
    ContentFetchError: <AppError>{
        id: 15205,
        source: RecommendedSearchSourceFile,
        severity,
        pb
    }
};

/** Represents recommende search below search box rendered failed to render */
export const RecommendedSearchBelowSearchBoxRenderFailed: AppError = {
    id: 15211,
    source: "RecommendedSearchBelowSearchBox.tsx",
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Represents Trending Now related app errors */
const TrendingNowSourceFile = "TrendingNow.connector.ts";
export const TrendingNowErrors = {
    /** Failed to fetch Trending now content from CMS */
    ContentFetchError: <AppError>{
        id: 15250,
        source: TrendingNowSourceFile,
        severity,
        pb
    },

    /** Failed to fetch child experience config */
    FailedToFetchConfig: <AppError>{
        id: 15251,
        source: TrendingNowSourceFile,
        severity,
        pb
    },

    /** Failed to load child experience */
    FailedToLoadChildExperience: <AppError>{
        id: 15252,
        source: TrendingNowSourceFile,
        severity,
        pb
    },

    /** OCID not set for canvas */
    OCIDNotSetWarning: <AppError>{
        id: 15253,
        source: "TrendingNowServiceClient.ts",
        severity: Severity.NoAlert,
        pb
    },

    /** Failed to fetch Trending now content from URL */
    ContentURLFetchError: <AppError>{
        id: 15254,
        source: "SearchBoxEdgeNextWC.ts",
        severity,
        pb
    }
};

const RelatedSearchesCardConnectorFile = "RelatedSearchesCard.connector.ts";
export const RelatedSearchesNoSearchTerm: AppError = {
    id: 15260,
    source: RelatedSearchesCardConnectorFile,
    severity,
    pb
};

export const RelatedSearchesServiceCallError: AppError = {
    id: 15261,
    source: RelatedSearchesCardConnectorFile,
    severity,
    pb
};

export const RelatedSearchesResponseEmpty: AppError = {
    id: 15262,
    source: RelatedSearchesCardConnectorFile,
    severity: Severity.NoAlert,
    pb
};

/** Represents Voice Search related app errors */
export const VoiceSearchErrors = {
    /** Failed to render Voice Search */
    RenderFailed: <AppError>{
        id: 15300,
        source: "VoiceSearch.tsx",
        severity: Severity.Critical,
        pb: {
            userImpacting: true
        }
    },

    /** Represents SearchBoxCommon Voice Search not configured App errors */
    SearchBoxCommonVoiceSearchError: <AppError>{
        id: 15301,
        source: "SearchBoxCommon.connector.ts",
        severity,
        pb
    },

    /** Represents SearchBoxEdgeNext Voice Search not configured App errors */
    SearchBoxEdgeNextVoiceSearchError: <AppError>{
        id: 15302,
        source: "SearchBoxEdgeNext.connector.ts",
        severity,
        pb
    },

    /** Represents Voice Search communication App errors */
    CommunicationsError: <AppError>{
        id: 15303,
        source: "VoiceSearch.connector.ts",
        severity,
        pb
    }
};

/** Represents Image Search related app errors */
const ImageSearchConnectorFile = "ImageSearch.connector.ts";
/** Failed to render Image Search */
export const ImageSearchRenderFailed: AppError = {
    id: 15400,
    source: "ImageSearch.tsx",
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Represents SearchBoxCommon Image Search not configured App errors */
export const SearchBoxCommonImageSearchError: AppError = {
    id: 15401,
    source: "SearchBoxCommon.connector.ts",
    severity,
    pb
};

/** Represents SearchBoxEdgeNext Image Search not configured App errors */
export const SearchBoxEdgeNextImageSearchError: AppError = {
    id: 15402,
    source: "SearchBoxEdgeNext.connector.ts",
    severity,
    pb
};

/** Represents Image Search communication App errors */
export const ImageSearchCommunicationsError: AppError = {
    id: 15403,
    source: ImageSearchConnectorFile,
    severity,
    pb
};

const PollsCardConnectorSourceFile = "PollsCard.connector.ts";
const PollsCardSchemaTransformerFile = "PollsCard.SchemaTransformer.ts";
export const PollsCardAppErrors = {
    /**
     * Failed to fetch polls list
     */
    PollsListFetchError: <AppError>{
        id: 18000,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    },

    /**
     * Failed to fetch poll data
     */
    PollDataFetchError: <AppError>{
        id: 18001,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    },

    /**
     * No data{questions} for poll
     */
    PollsCardInvalidPoll: <AppError>{
        id: 18002,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    },

    /**
     * Invalid Poll Quiz
     */
    PollsCardInvalidQuizPoll: <AppError>{
        id: 18003,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    },

    /**
     * PollsVoteError
     */
    PollsCardVoteError: <AppError>{
        id: 18004,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    },

    /**
     * PollsMapStateToPropsError
     */
    PollsCardMapStateToPropsError: <AppError>{
        id: 18005,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    },

    /**
     * PollsUnknownError
     */
    PollsUnknownError: <AppError>{
        id: 18006,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    },

    /**
     * Poll Result Percentage Error
     */
    PollsResultPercentageError: <AppError>{
        id: 18007,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    }
};

export const PollAutoCuratedErrors = {
    /**
    * Poll Auto curate content error
    */
    PollAutoCuratedContentError: <AppError>{
        id: 18008,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    },

    /**
     * Poll Auto curate content error
     */
    PollAutoCuratedInvalid: <AppError>{
        id: 18009,
        source: PollsCardSchemaTransformerFile,
        severity,
        pb
    },

    /**
     * Poll Auto curate content error
     */
    PollAutoCuratedResultsFetchError: <AppError>{
        id: 18010,
        source: PollsCardConnectorSourceFile,
        severity,
        pb
    },

    PollAutoCuratedResultsTransformError: <AppError>{
        id: 18011,
        source: PollsCardSchemaTransformerFile,
        severity,
        pb
    }
};

const EdgeChromiumPageConnectorFileName = "EdgeChromiumPage.connector.ts";
export const EdgeNextAppErrors = {
    /**
     * Error during a critical error on the EdgeNext page. This can be during initialization or a react rendering error.
     */
    EdgeNextErrorPage: <AppError>{
        id: 19000,
        source: pageTsFileName,
        severity,
        pb
    },

    /** Error fetching the APP_ANON cookie on EdgeNext page load */
    ErrorFetchingAppAnonCookie: <AppError>{
        id: 19001,
        source: pageTsFileName,
        severity,
        pb
    },

    /**
     * Tracking errors where we ended up showing the local ntp page on bundle syntax errors.
     */
    EdgeNextLocalNTPPageShownSyntaxErrors: <AppError>{
        id: 19002,
        source: pageTsFileName,
        severity,
        pb
    },

    /**
     * Tracking errors where we ended up showing the local ntp page on bundle failures.
     */
    EdgeNextLocalNTPPageShownBundleFailureErrors: <AppError>{
        id: 19003,
        source: pageTsFileName,
        severity,
        pb
    },

    /**
     * Tracking errors where we ended up showing the local ntp page on page exception.
     */
    EdgeNextLocalNTPPageShownPageExceptionError: <AppError>{
        id: 19004,
        source: pageTsFileName,
        severity: Severity.Critical,
        pb: {
            userImpacting: true
        }
    },

    /**
     * Tracking errors where we ended up showing the local ntp page on page exception.
     */
    EdgeNextLocalNTPPageShownJSSMismatchExceptionError: <AppError>{
        id: 19005,
        source: pageTsFileName,
        severity: Severity.Critical,
        pb: {
            userImpacting: true
        }
    },

    /**
     * When the page is redirected to the local NTP page due to an unavoidable network failure.
     */
    EdgeNextLocalNtpDueToNetwork: <AppError>{
        id: 19006,
        source: pageTsFileName,
        severity,
        pb
    },

    /**
     * Tracking errors where we ended up reloading the NTP page on jss mismatch exception.
     */
    EdgeNextPageReloadedPageExceptionError: <AppError>{
        id: 19010,
        source: pageTsFileName,
        severity,
        pb
    },

    /**
     * Tracking errors where we ended up reloading the NTP page on jss mismatch exception.
     */
    EdgeNextPageReloadedJSSMismatchExceptionError: <AppError>{
        id: 19011,
        source: "PersistRenderCache.ts",
        severity,
        pb
    },

    /**
    * Errors when form code is missing for Edge Chromium Page.
    */
    EdgeChromiumPageMissingFormCode: <AppError>{
        id: 19012,
        source: EdgeChromiumPageConnectorFileName,
        severity,
        pb
    },

    /**
     * When the is reloaded due to an unavoidable network failure.
     */
    EdgeNextPageReloadedDueToNetwork: <AppError>{
        id: 19013,
        source: pageTsFileName,
        severity,
        pb
    },

    /**
     * When the page fails to render due to an unavoidable network failure.
     */
    EdgeNextPageRenderFailedDueToNetwork: <AppError>{
        id: 19014,
        source: pageTsFileName,
        severity,
        pb
    }
};

const AdRefreshSourceFile = "AdsRefresh.ts";
const ApnWrapperSourceFile = "AppnexusWrapper.ts";
const DisplayAdsAdsDetectionSourceFile = "AdsDetection.ts";
const DisplayAdsConnectorSourceFile = "DisplayAds.connector.ts";
const DisplayAdsManagerConnectorSourceFile = "DisplayAdsManager.connector.ts";
const NativeAdConnectorSourceFile = "NativeAd.connector.ts";
const OathWrapperSourceFile = "OathAdsWrapper.ts";
const JacWrapperSourceFile = "JacAdsWrapper.ts";
const PlacementManagerConnectorSourceFile = "PlacementManager.connector.ts";
const PlacementManagerCookieSyncSourceFile = "PlacementManagerCookieSync.ts";
const PlacementManagerCMSServiceSourceFile = "PlacementManagerCMSService.ts";
const NativeAdClientConnectorSourceFile = "NativeAdClient.connector.ts";
const PartnerScriptLoaderFile = "PartnerScriptLoader.ts";
const NativeAdBeaconSourceFile = "Beacon.ts";

export const MonetizationAppErrors = {
    /** Failed to load Placement Manager. */
    PlacementManagerLoadError: <AppError>{
        id: 20000,
        source: NativeAdConnectorSourceFile,
        severity,
        pb
    },

    /** Failed to load arrangement position on native ad experience. */
    ArrangementPositionLoadError: <AppError>{
        id: 20001,
        source: NativeAdConnectorSourceFile,
        severity,
        pb
    },

    /** Failed to get CMS Content. */
    GetCMSContentError: <AppError>{
        id: 20002,
        source: PlacementManagerCMSServiceSourceFile,
        severity,
        pb
    },

    /** Failed to get SAM RTB Response. */
    GetSAMRTBResponseError: <AppError>{
        id: 20003,
        source: PlacementManagerConnectorSourceFile,
        severity,
        pb
    },

    /** Failed to load MediaNet End Of Article Block. */
    MediaNetEOABError: <AppError>{
        id: 20005,
        source: "medianetNativeAdOnArticle.ts",
        severity,
        pb
    },

    /** Invalid promo module config */
    InvalidPromoModuleConfig: <AppError>{
        id: 20006,
        source: NativeAdConnectorSourceFile,
        severity,
        pb
    },

    /** Failed to download cookie sync external script. */
    CookieSyncDownloadExternalScriptError: <AppError>{
        id: 20007,
        source: PlacementManagerCookieSyncSourceFile,
        severity,
        pb
    },

    /** Missing cookie sync external script. */
    CookieSyncMissingExternalScriptUrl: <AppError>{
        id: 20008,
        source: PlacementManagerCookieSyncSourceFile,
        severity,
        pb
    },

    /** Cookie sync timeout. */
    CookieSyncTimeout: <AppError>{
        id: 20009,
        source: PlacementManagerCookieSyncSourceFile,
        severity,
        pb
    },

    /** Cookie sync partner script error */
    CookieSyncPartnerScriptError: <AppError>{
        id: 20010,
        source: PlacementManagerCookieSyncSourceFile,
        severity,
        pb
    },

    /** Cookie pixel sync partner download error */
    CookieSyncPixelPartnerDownloadError: <AppError>{
        id: 20011,
        source: PlacementManagerCookieSyncSourceFile,
        severity,
        pb
    },

    /** Failed to load cms or backfill native ad. */
    CMSOrBackfillNativeAdLoadError: <AppError>{
        id: 20012,
        source: NativeAdConnectorSourceFile,
        severity,
        pb
    },

    /** Failed to load cms or backfill native ad. */
    PlacementPositionMismatchingError: <AppError>{
        id: 20013,
        source: NativeAdConnectorSourceFile,
        severity,
        pb
    },

    /** Missing required property on placement manager config */
    MissingRequiredPropertyPlacementManagerConfig: <AppError>{
        id: 20014,
        source: PlacementManagerConnectorSourceFile,
        severity,
        pb
    },

    /** Missing page type target scope. */
    NativeAdTargetScopeError: <AppError>{
        id: 20016,
        source: NativeAdConnectorSourceFile,
        severity,
        pb
    },

    /** River video ad partner error. */
    RiverVideoAdPartnerError: <AppError>{
        id: 20017,
        source: PartnerScriptLoaderFile,
        severity,
        pb
    },

    /** JsTracker ad partner script load error. */
    JsTrackerPartnerScriptLoadError: <AppError>{
        id: 20018,
        source: NativeAdBeaconSourceFile,
        severity,
        pb
    },

    /** Viglink load error. */
    ViglinkError: <AppError>{
        id: 20019,
        source: PartnerScriptLoaderFile,
        severity,
        pb
    },

    /** Missing topic id for stripe native ad */
    MissingTopicId: <AppError>{
        id: 20020,
        source: NativeAdConnectorSourceFile,
        severity,
        pb
    },

    /** Topic id not found for stripe native ad */
    TopicIdNotFound: <AppError>{
        id: 20021,
        source: NativeAdConnectorSourceFile,
        severity,
        pb
    },

    /** Failed to get SAM RTB Response. This App error doesn't trigger alert */
    GetSAMRTBResponseErrorNoAlert: <AppError>{
        id: 20022,
        source: PlacementManagerConnectorSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /** Failed to load Oath JS */
    DisplayAdsOathLoadError: <AppError>{
        id: 20050,
        source: OathWrapperSourceFile,
        severity,
        pb
    },

    /** Failed to load AppNexus JS */
    DisplayAdsApnLoadError: <AppError>{
        id: 20051,
        source: ApnWrapperSourceFile,
        severity,
        pb
    },

    /** AppNexus Provider was empty */
    DisplayAdsApnEmptyProvider: <AppError>{
        id: 20052,
        source: ApnWrapperSourceFile,
        severity: Severity.Alert
    },

    /** Ads Detection no initialized properly Provider was empty */
    DisplayAdsAdsDetectionNotInit: <AppError>{
        id: 20053,
        source: DisplayAdsAdsDetectionSourceFile,
        severity: Severity.Alert,
        pb
    },

    /** Failed to load Moat JS */
    DisplayAdsMoatLoadError: <AppError>{
        id: 20054,
        source: DisplayAdsManagerConnectorSourceFile,
        severity,
        pb
    },

    /** Display ad objects were not available for refresh */
    DisplayAdsNoAdObjects: <AppError>{
        id: 20055,
        source: AdRefreshSourceFile,
        severity,
        pb
    },

    /** There was a problem in encoding CMS provider ID from base 62 to 32 */
    DisplayAdsProviderIdEncode: <AppError>{
        id: 20056,
        source: DisplayAdsConnectorSourceFile,
        severity,
        pb
    },

    /** Failed to load Jac JS */
    DisplayAdsJacLoadError: <AppError>{
        id: 20057,
        source: JacWrapperSourceFile,
        severity,
        pb
    },

    /** Display Ad size is not valid */
    DisplayAdsInvalidSizeError: <AppError>{
        id: 20058,
        source: JacWrapperSourceFile,
        severity,
        pb
    },

    /** parameter for rendering ads is invalid */
    DisplayAdsInvalidParameterError: <AppError>{
        id: 20059,
        source: JacWrapperSourceFile,
        severity,
        pb
    }
};

export const TaboolaRiverAppErrors = {
    /** Failed to fetch taboola river data. */
    TaboolaRiverFetchError: <AppError>{
        id: 20100,
        source: "TaboolaRiver.connector.ts",
        severity,
        pb
    }
};

const NativeAdServiceFile = "NativeAdService.ts";
const CMSAdServiceFile = "CMSAdService.ts";
const SAMAdServiceFile = "SAMAdService.ts";

/** Failed to get valid Response from AdService library */
export const GetNativeAdRequestError: AppError = {
    id: 20150,
    source: NativeAdServiceFile,
    severity,
    pb
};

/** Failed to get valid Response from AdService library */
export const GetNativeAdResponseError: AppError = {
    id: 20151,
    source: NativeAdServiceFile,
    severity,
    pb
};

/** Failed to get valid CMS Response from AdService library */
export const GetCMSAdResponseError: AppError = {
    id: 20152,
    source: CMSAdServiceFile,
    severity,
    pb
};

/** Failed to get valid SAM Response from AdService library */
export const GetSAMRTBResponseErrorNoAlert: AppError = {
    id: 20153,
    source: SAMAdServiceFile,
    severity: Severity.NoAlert,
    pb
};

/** Failed to get valid SAM Response from AdService library */
export const GetSAMRTBRequestError: AppError = {
    id: 20154,
    source: SAMAdServiceFile,
    severity,
    pb
};

/** Failed to get valid SAM Response from AdService library */
export const GetSAMRTBResponseError: AppError = {
    id: 20155,
    source: SAMAdServiceFile,
    severity,
    pb
};

/** Failed to get valid SAM Response from AdService library */
export const GetSAMRTBResponseParseError: AppError = {
    id: 20156,
    source: SAMAdServiceFile,
    severity,
    pb
};

/** Failed to get valid SAM Response from AdService library */
export const GetSAMRTBResponseException: AppError = {
    id: 20157,
    source: SAMAdServiceFile,
    severity,
    pb
};

const NativeAdFeedCardFile = "NativeAdFeedCard.connector.ts";
const NativeAdGlanceCardFile = "NativeAdGlanceCard.connector.ts";

/** Failed to get valid Response from AdService library in SuperApp Feed card*/
export const GetNativeAdFeedCardError: AppError = {
    id: 20160,
    source: NativeAdFeedCardFile,
    severity: Severity.NoAlert,
    pb
};

/** Failed to get valid Response from AdService library in SuperApp Glance card*/
export const GetNativeAdGlanceCardError: AppError = {
    id: 20161,
    source: NativeAdGlanceCardFile,
    severity: Severity.NoAlert,
    pb
};

const PCSPageHTMLFile = "PCS-Page.cshtml";
export const CoreLibAppErrors = {
    /** Deprecated composition layout config in use */
    DeprecatedCompositionLayoutUsed: <AppError>{
        id: 20200,
        source: "LayoutBase.inspector.ts",
        severity: Severity.NoAlert,
        pb
    },

    /** Deprecated composition layout config in use */
    InvalidColumnInGridConfig: <AppError>{
        id: 20201,
        source: "GridBase.inspector.ts",
        severity,
        pb
    },

    /**
     * Error during loading of page bundles.
     */
    ErrorLoadingBundle: <AppError>{
        id: 20202,
        source: PCSPageHTMLFile,
        severity: Severity.HighImpact,
        pb
    },

    /**
     * Generic JS exceptions.
     */
    GenericJSException: <AppError>{
        id: 20203,
        source: PCSPageHTMLFile,
        severity,
        pb
    },

    /**
     * Missing localStorage exception
     */
    LocalStorageDoesntExist: <AppError>{
        id: 20204,
        source: "WebStorage.ts",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Missing sessionStorage exception
     */
    SessionStorageDoesntExist: <AppError>{
        id: 20205,
        source: "WebStorage.ts",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Exception thrown during rendering in React, with no additional handling specified.
     * This means React has unmounted your DOM tree and you'll be getting a blank page.
     */
    ReactRenderException: <AppError>{
        id: 20206,
        source: PCSPageHTMLFile,
        severity: Severity.Critical,
        pb: {
            userImpacting: true
        }
    },

    /** App Error to log network connection status */
    NetworkConnectionStatus: <AppError>{
        id: 20207,
        source: "NetworkCheck.ts",
        severity: Severity.NoAlert,
        pb
    }
};

/** Represents Authenticatedfetch related App errors */
const AuthenticatedFetchSourceFile = "AuthenticatedFetch.ts";
export const AuthenticatedFetchErrors = {
    ErrorCodeReceived: <AppError>{
        id: 20302,
        source: AuthenticatedFetchSourceFile,
        severity,
        pb
    },

    CallFailed: <AppError>{
        id: 20303,
        source: AuthenticatedFetchSourceFile,
        severity,
        pb
    },

    EmptyJsonResponse: <AppError>{
        id: 20304,
        source: AuthenticatedFetchSourceFile,
        severity,
        pb
    }
};

const resolverSourceFile = "resolver.ts";
export const ResolverAppErrors = {
    /**
     * IndexedDb write failed in resolver.
     */
    IndexedDbWriteFailed: <AppError>{
        id: 22010,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * IndexedDb read failed in resolver.
     */
    IndexedDbReadFailed: <AppError>{
        id: 22011,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Config Service Resolver fetch errors.
     */
    CrsFetchError: <AppError>{
        id: 22012,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Config Service Resolver was enabled but we still went to Assets to getConfig and merge configs at client (classic resolver).
     */
    ReceivedConfigFromCdnAsCrsFetchFailed: <AppError>{
        id: 22013,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Exception In GetConfig function.
     */
    GetConfigException: <AppError>{
        id: 22014,
        source: resolverSourceFile,
        severity: Severity.Critical,
        pb: {
            userImpacting: true
        }
    },

    /**
     * IndexedDb bulk read failed in resolver.
     */
    IndexedDbBulkReadFailed: <AppError>{
        id: 22015,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * IndexedDb failed to remove cached config
     */
    IndexedDbFailedToRemoveCachedConfig: <AppError>{
        id: 22016,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * IndexedDb failed to clear all configs
     */
    IndexedDbFailedToClearAllCachedConfigs: <AppError>{
        id: 22017,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Render cache is purged due to changes in config
     */
    RenderCachePurgedDueToChangesInConfig: <AppError>{
        id: 22018,
        source: resolverSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Render cache failed purge even though config changes were detected.
     */
    FailedToPurgeRenderCache: <AppError>{
        id: 22019,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Failed to refresh config cache
     */
    RefreshConfigCacheFailed: <AppError>{
        id: 22020,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Failed to initialize indexedDb
     */
    IndexedDbInitFailed: <AppError>{
        id: 22021,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Different cache busting identifier
     */
    DifferentCbid: <AppError>{
        id: 22022,
        source: resolverSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error writing prg flights to localStorage
     */
    PrgFlightLsWriteError: <AppError>{
        id: 22023,
        source: resolverSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error reading prg flights to localStorage
     */
    PrgFlightLsReadError: <AppError>{
        id: 22024,
        source: resolverSourceFile,
        severity: Severity.NoAlert,
        pb
    }
};

/**
 * Configs served from CDN after CRS fetch failed
 */
export const ResolverErrorConfigsDownloadedByCdn: AppError = {
    id: 22025,
    source: resolverSourceFile,
    severity: Severity.NoAlert,
    pb
};

/**
 * Cached configs are for different flight
 */
export const CachedConfigsForDifferentFlight: AppError = {
    id: 22026,
    source: resolverSourceFile,
    severity: Severity.NoAlert,
    pb
};

/**
 * CRS sent non-200 response
 */
export const ConfigCrsFetchNon200Response: AppError = {
    id: 22027,
    source: resolverSourceFile,
    severity,
    pb
};

/**
 * Cdn config fetch received non-200 response
 */
export const ConfigCdnFetchNon200Response: AppError = {
    id: 22028,
    source: resolverSourceFile,
    severity,
    pb
};

/**
 * Invalid config ref received to resolve configs when Git configs is enabled
 */
export const GitConfigsInvalidConfigRef: AppError = {
    id: 22029,
    source: resolverSourceFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/**
 * Invalid configResolver object for Git configs flight
 */
export const GitConfigsInvalidConfigResolver: AppError = {
    id: 22030,
    source: resolverSourceFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

export const HubAppErrors = {
    /**
     * Error during app initialization for hub page experience.
     */
    HubErrorPage: <AppError>{
        id: 22100,
        source: "web.page.ts",
        severity,
        pb
    },

    /**
     * Warning that hub server side rendering is skipped and client side rendering is triggered
     * due to either server error or slow server response.
     */
    HubSSRSkip: <AppError>{
        id: 22110,
        source: "web.page.ts",
        severity: Severity.NoAlert,
        pb
    }
};

const EdgePageFileName = "Edge/web.page.ts";

/** Edge App Type errors. */
export const EdgeAppErrors = {
    ErrorFetchingChildCookie: <AppError>{
        id: 23000,
        source: EdgePageFileName,
        severity,
        pb
    },

    /**
     * Error during app initialization for Edge page experience.
     */
    EdgeErrorPage: <AppError>{
        id: 23001,
        source: EdgePageFileName,
        severity,
        pb
    },

    /**
     * Error fetching the APP_ANON cookie on Edge page load
     */
    ErrorFetchingAppAnonCookie: <AppError>{
        id: 23002,
        source: EdgePageFileName,
        severity,
        pb
    },

    /**
     * Error during set flight instrumentation data
     */
    ErrorSetFlightInstrumentation: <AppError>{
        id: 23003,
        source: EdgePageFileName,
        severity,
        pb
    },

    /**
     * Warning that edge server side rendering is skipped and client side rendering is triggered
     * due to either server error or slow server response.
     */
    EdgeSSRSkip: <AppError>{
        id: 23004,
        source: EdgePageFileName,
        severity: Severity.NoAlert,
        pb
    }
};

export const BingCardsAppErrors = {
    BingCardGetHotelAnswersError: <AppError>{
        id: 24001,
        source: "BingCardsServiceClient.ts",
        severity,
        pb
    },

    BingCardGetHotelErrorFromService: <AppError>{
        id: 24002,
        source: "BingCardsServiceClient.ts",
        severity,
        pb
    },

    BingCardGetAnswersError: <AppError>{
        id: 24003,
        source: "BingCardsExperience.connector.ts",
        severity,
        pb
    },

    BingCardInvalidEntityType: <AppError>{
        id: 24004,
        source: "BingCardsExperience.connector.ts",
        severity,
        pb
    },

    BingCardErrorOut: <AppError>{
        id: 24005,
        source: "BingCardsExperience.connector.ts",
        severity,
        pb
    }
};

export const BingAnswersAppErrors = {
    UnknownError: <AppError>{
        id: 24101,
        source: "BingAnswers.connector.ts",
        severity,
        pb
    },
    FailedToLoadData: <AppError>{
        id: 24102,
        source: "BingAnswers.connector.ts",
        severity,
        pb
    },
    NoCardsContentType: <AppError>{
        id: 24103,
        source: "BingAnswers.connector.ts",
        severity,
        pb
    },
    NoCardsRelated: <AppError>{
        id: 24104,
        source: "BingAnswers.connector.ts",
        severity: Severity.NoAlert,
        pb
    }
};

const BingShoppingShopCardServiceClientSourceFile = "bing-shopping/ShopCardServiceClient.ts";
const BingShoppingShopCardWpoDataMapperFile = "libs/feed-layout/.../ShoppingCardDataMapper.ts";

export const BingShoppingShopCardError = {
    ShopCardServiceFetchError: <AppError>{
        id: 24200,
        source: BingShoppingShopCardServiceClientSourceFile,
        severity,
        pb
    },
    ShopCardServiceFetchInvalidResponse: <AppError>{
        id: 24201,
        source: BingShoppingShopCardServiceClientSourceFile,
        severity,
        pb
    },
    ShopCardServiceEmptyPersonalizedResponse: <AppError>{
        id: 24202,
        source: BingShoppingShopCardServiceClientSourceFile,
        severity,
        pb
    },
    ShopCardServiceInvalidPersonalizedResponse: <AppError>{
        id: 24203,
        source: BingShoppingShopCardServiceClientSourceFile,
        severity,
        pb
    },
    ShopCardServiceEmptyTrendingResponse: <AppError>{
        id: 24204,
        source: BingShoppingShopCardServiceClientSourceFile,
        severity,
        pb
    },
    ShopCardServiceInvalidTrendingResponse: <AppError>{
        id: 24205,
        source: BingShoppingShopCardServiceClientSourceFile,
        severity,
        pb
    }
};

export const ShopCardWpoDataMapperError: AppError = {
    id: 24206,
    source: BingShoppingShopCardWpoDataMapperFile,
    severity,
    pb
};

const BingShoppingLifestyleCardServiceClientSourceFile =
    "bing-shopping-lifestyle-card/BingShoppingLifestyleCardServiceClient.ts";

const BingShoppingLifestyleCardCdnServiceClientSourceFile =
    "bing-shopping-lifestyle-card/BingShoppingLifestyleCardCdnServiceClient.ts";

const BingShoppingLifestyleAutosuggestServiceClientSourceFile =
    "bing-shopping-lifestyle-card/BingShoppingLifestyleAutosuggestServiceClient.ts";

const BingShoppingLifestyleConnectorSourceFile =
    "bing-shopping-lifestyle-card/BingShoppingLifestyleCard.connector.ts";
export const BingShoppingLifestyleCardError = {
    // Lifestyle Shopcard service Errors
    LifestyleShopCardServiceFetchError: <AppError>{
        id: 24300,
        source: BingShoppingLifestyleCardServiceClientSourceFile,
        severity,
        pb
    },
    LifestyleShopCardServiceFetchInvalidResponse: <AppError>{
        id: 24301,
        source: BingShoppingLifestyleCardServiceClientSourceFile,
        severity,
        pb
    },
    LifestyleShopCardServiceEmptyResponse: <AppError>{
        id: 24302,
        source: BingShoppingLifestyleCardServiceClientSourceFile,
        severity,
        pb
    },
    LifestyleShopCardServiceInvalidResponse: <AppError>{
        id: 24303,
        source: BingShoppingLifestyleCardServiceClientSourceFile,
        severity,
        pb
    },

    // Lifestyle Shopcard Cdn service Errors
    LifestyleShopCardCdnServiceFetchError: <AppError>{
        id: 24304,
        source: BingShoppingLifestyleCardCdnServiceClientSourceFile,
        severity,
        pb
    },
    LifestyleShopCardCdnServiceFetchInvalidResponse: <AppError>{
        id: 24305,
        source: BingShoppingLifestyleCardCdnServiceClientSourceFile,
        severity,
        pb
    },
    LifestyleShopCardCdnServiceInvalidResponse: <AppError>{
        id: 24306,
        source: BingShoppingLifestyleCardCdnServiceClientSourceFile,
        severity,
        pb
    },

    // Lifestyle Autosuggest service Errors
    LifestyleShopAutosuggestServiceFetchError: <AppError>{
        id: 24310,
        source: BingShoppingLifestyleAutosuggestServiceClientSourceFile,
        severity,
        pb
    },
    LifestyleShopAutosuggestServiceFetchInvalidResponse: <AppError>{
        id: 24311,
        source: BingShoppingLifestyleAutosuggestServiceClientSourceFile,
        severity,
        pb
    },
    LifestyleShopAutosuggestServiceEmptyResponse: <AppError>{
        id: 24312,
        source: BingShoppingLifestyleAutosuggestServiceClientSourceFile,
        severity,
        pb
    },
    LifestyleShopAutosuggestServiceInvalidResponse: <AppError>{
        id: 24313,
        source: BingShoppingLifestyleAutosuggestServiceClientSourceFile,
        severity,
        pb
    },
    LifestyleShopAutosuggestInvalidSuggestionUrl: <AppError>{
        id: 24314,
        source: BingShoppingLifestyleConnectorSourceFile,
        severity,
        pb
    }
};

const ShoppingSmartListServiceClientSourceFile =
    "shopping-smart-list/ShoppingSmartListServiceClient.ts";

const ShoppingSmartListConnectorSourceFile =
    "shopping-smart-list/ShoppingSmartList.connector.ts";

export const ShoppingSmartListError = {
    ShoppingSmartListServiceEmptyResponse: <AppError>{
        id: 24400,
        source: ShoppingSmartListServiceClientSourceFile,
        severity,
        pb
    },
    ShoppingSmartListServiceFetchError: <AppError>{
        id: 24401,
        source: ShoppingSmartListServiceClientSourceFile,
        severity,
        pb
    },
    ShoppingSmartListServiceInvalidResponse: <AppError>{
        id: 24402,
        source: ShoppingSmartListServiceClientSourceFile,
        severity,
        pb
    },
    ShoppingSmartListServiceMissingRequiredFields: <AppError>{
        id: 24403,
        source: ShoppingSmartListServiceClientSourceFile,
        severity,
        pb
    },
    ShoppingSmartListChromiumApiConfigDataFetchFailure: <AppError>{
        id: 24404,
        source: ShoppingSmartListConnectorSourceFile,
        severity,
        pb
    }
};

const BingMiniShoppingServiceClientSourceFile =
    "bing-mini-shopping/BingMiniShoppingServiceClient.ts";

export const BingMiniShoppingError = {
    // Bing-MiniShopping service Errors
    BingMiniShoppingServiceFetchError: <AppError>{
        id: 24500,
        source: BingMiniShoppingServiceClientSourceFile,
        severity,
        pb
    },
    BingMiniShoppingServiceEmptyResponse: <AppError>{
        id: 24501,
        source: BingMiniShoppingServiceClientSourceFile,
        severity,
        pb
    },
    BingMiniShoppingServiceInvalidResponse: <AppError>{
        id: 24502,
        source: BingMiniShoppingServiceClientSourceFile,
        severity,
        pb
    },
    BingMiniShoppingServiceInsufficientDealsItems: <AppError>{
        id: 24503,
        source: BingMiniShoppingServiceClientSourceFile,
        severity,
        pb
    },
    BingMiniShoppingServiceInsufficientActiveDealsItems: <AppError>{
        id: 24504,
        source: BingMiniShoppingServiceClientSourceFile,
        severity,
        pb
    },
    BingMiniShoppingServiceInsufficientTrendingItems: <AppError>{
        id: 24505,
        source: BingMiniShoppingServiceClientSourceFile,
        severity,
        pb
    },
    BingMiniShoppingServiceInsufficientTrendingCategories: <AppError>{
        id: 24506,
        source: BingMiniShoppingServiceClientSourceFile,
        severity,
        pb
    }
};

const BingShoppingMeStripeServiceClientSourceFile =
    "bing-shopping-mestripe/BingShoppingMeStripeServiceClient.ts";

export const BingShoppingMeStripeServiceFetchError: AppError = {
    id: 24510,
    source: BingShoppingMeStripeServiceClientSourceFile,
    severity,
    pb
};

export const BingShoppingMeStripeServiceEmptyResponse: AppError = {
    id: 24511,
    source: BingShoppingMeStripeServiceClientSourceFile,
    severity,
    pb
};

export const BingShoppingMeStripeServiceInvalidResponse: AppError = {
    id: 24512,
    source: BingShoppingMeStripeServiceClientSourceFile,
    severity,
    pb
};

export const BingShoppingMeStripeServiceMissingRequiredFields: AppError = {
    id: 24513,
    source: BingShoppingMeStripeServiceClientSourceFile,
    severity,
    pb
};

const ShoppingCarouselServiceClientSourceFile =
    "shopping-carousel/ShoppingCarouselServiceClient.ts";

export const ShoppingCarouselEntitiesDataServiceFetchError: AppError = {
    id: 24520,
    source: ShoppingCarouselServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingCarouselEntitiesDataServiceEmptyResponse: AppError = {
    id: 24521,
    source: ShoppingCarouselServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingCarouselEntitiesDataServiceInvalidResponse: AppError = {
    id: 24522,
    source: ShoppingCarouselServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingCarouselEntitiesDataServiceInsufficientItems: AppError = {
    id: 24523,
    source: ShoppingCarouselServiceClientSourceFile,
    severity,
    pb
};

const ShoppingCarouselConnectorSourceFile =
    "shopping-carousel/ShoppingCarousel.connector.ts";

export const ShoppingCarouselGenericError: AppError = {
    id: 24524,
    source: ShoppingCarouselConnectorSourceFile,
    severity,
    pb
};

const ShoppingCarouselSourceFile =
    "shopping-carousel/ShoppingCarousel.tsx";

export const ShoppingCarouselRenderError: AppError = {
    id: 24525,
    source: ShoppingCarouselSourceFile,
    severity,
    pb
};

const ShoppingCarouselMapperSourceFile =
    "shopping-carousel/ShoppingCarouselCardDataMapper.ts";

export const ShoppingCarouselCardMapperError: AppError = {
    id: 24526,
    source: ShoppingCarouselMapperSourceFile,
    severity,
    pb
};

const ShoppingContentCardServiceClientSourceFile =
    "ShoppingContentCard.serviceClient.ts";

export const ShoppingContentCardServiceFetchError: AppError = {
    id: 24530,
    source: ShoppingContentCardServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingContentCardServiceEmptyResponse: AppError = {
    id: 24531,
    source: ShoppingContentCardServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingContentServiceInvalidData: AppError = {
    id: 24532,
    source: ShoppingContentCardServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingContentServiceInsufficientActiveItems: AppError = {
    id: 24533,
    source: ShoppingContentCardServiceClientSourceFile,
    severity,
    pb
};

export const ShoppingContentServiceMissingFields: AppError = {
    id: 24534,
    source: ShoppingContentCardServiceClientSourceFile,
    severity,
    pb
};

/**
 * Error in page.ts of Shopping app type.
 */
export const ShoppingErrorPage: AppError = {
    id: 24900,
    source: "Shopping/page.ts",
    severity,
    pb
};

export const PageSettingsDataConnectorAppErrors = {
    PageSettingsGetInitialState: <AppError>{
        id: 26001,
        source: "PageSettingsData.connector.ts",
        severity,
        pb
    }
};

const MicrosoftNewsEntryPointFileName = "MicrosoftNews.connector.ts";
export const MicrosoftNewsEntryPointAppErrors = {
    /**
     * Error during app initialization for MicrosoftNews page experience.
     */
    MicrosoftNewsErrorPage: <AppError>{
        id: 27000,
        source: MicrosoftNewsEntryPointFileName,
        severity,
        pb
    }
};

export const NavigationDataConnectorAppErrors = {
    /**
     * Error while trying to fetch the Navigation Items
     */
    NavigationDataGetNavigationItems: <AppError>{
        id: 28000,
        source: "NavigationData.connector.ts",
        severity,
        pb
    },

    NavigationDataIndexedDbInitializationFailed: <AppError>{
        id: 28001,
        source: "NavigationData.connector.ts",
        severity,
        pb
    },

    NavigationDataIndexedDbWriteFailed: <AppError>{
        id: 28002,
        source: "NavigationData.connector.ts",
        severity,
        pb
    },

    NavigationDataIndexedDbBulkReadFailed: <AppError>{
        id: 28003,
        source: "NavigationData.connector.ts",
        severity,
        pb
    }
};

export const CategoryDataConnectorAppErrors = {
    /**
     * Error while to get the category details
     */
    CategoryDataGetCategoryDetail: <AppError>{
        id: 28100,
        source: "CategoryData.connector.ts",
        severity: Severity.NoAlert,
        pb
    },

    CategoryDataIndexedDbInitializationFailed: <AppError>{
        id: 28101,
        source: "CategoryData.connector.ts",
        severity,
        pb
    },

    CategoryDataIndexedDbWriteFailed: <AppError>{
        id: 28102,
        source: "CategoryData.connector.ts",
        severity,
        pb
    },

    CategoryDataIndexedDbBulkReadFailed: <AppError>{
        id: 28103,
        source: "CategoryData.connector.ts",
        severity,
        pb
    }
};

/**
 * App reporting messages related to OneService Content Data Connector
 */
export const OneServiceContentDataConnector = {
    /**
     * Message sent when a feature tries to update
     * the cache bypass value but the connector was not initialized
     * for the page
     */
    FailedToUpdateBypassValueConnectorNotInitialized: <AppError>{
        id: 28200,
        source: "OneServiceContentHelper.ts",
        severity,
        pb
    },
    /**
     * Message sent when a feature tries to update
     * the cache bypass value but the connector was not initialized
     * for the page
     */
    FailedToGetBypassValueConnectorNotInitialized: <AppError>{
        id: 28201,
        source: "OneServiceContentHelper.ts",
        severity,
        pb
    }
};

const OneSericeUtility = "OneSericeFetch.ts";
/* App errors related to centralized OneService fetch call */
export const FetchResponseNullOrError: AppError = {
    id: 28210,
    source: OneSericeUtility,
    severity,
    pb
};

export const NonAuthFetchResponseNullOrError: AppError = {
    id: 28211,
    source: OneSericeUtility,
    severity,
    pb
};

export const AuthFetchResponseNullOrError: AppError = {
    id: 28212,
    source: OneSericeUtility,
    severity,
    pb
};

/**
 * App reporting messages related to Stripe Experience
 */
export const StripeAppErrors = {
    InvalidConfig: <AppError>{
        id: 28300,
        source: "Stripe.connector.ts",
        severity,
        pb
    }
};

export const NotificationBellErrors = {
    /** Failed to fetch the notification bell data content */
    DataFetchError: <AppError>{
        id: 28400,
        source: "NotificationBell.connector.ts",
        severity,
        pb
    },
    JsonParseError: <AppError>{
        id: 28401,
        source: "LocalStorageSettings.ts",
        severity: Severity.NoAlert,
        pb
    },
    NewsItemDataFetchError: <AppError>{
        id: 28402,
        source: "NotificationBellServiceClient.ts",
        severity,
        pb
    },
    MalformedNewsItemResponseError: <AppError>{
        id: 28403,
        source: "NotificationBellServiceClient.ts",
        severity,
        pb
    }
};

/** Represents Layout Preference Data Connector related App errors */
const LayoutPreferenceDataSourceFile = "LayoutPreferenceData.connector.ts";
export const LayoutPreferenceDataErrors = {
    /** Failed to retrieve Layout Preference Data */
    LayoutPreferenceDataGetFailed: <AppError>{
        id: 28500,
        source: LayoutPreferenceDataSourceFile,
        severity,
        pb
    }
};

/**
 * App reporting messages related to StripeView Experience
 */
export const StripeViewAppErrors = {
    InvalidConfig: <AppError>{
        id: 28600,
        source: "StripeView.connector.ts",
        severity,
        pb
    },
    ChildLoadError: <AppError>{
        id: 28601,
        source: "StripeView.connector.ts",
        severity,
        pb
    }
};

/** Represents Sentiment Line Chart Connector related app errors */
const SentimentLineChartSourceFile = "SentimentLineChart.connector.ts";
export const SentimentLineChartErrors = {
    ContentFetchError: <AppError>{
        id: 28700,
        source: SentimentLineChartSourceFile,
        severity,
        pb
    }
};

/** Represents Sentiment Result Card Connector related app errors */
const SentimentResultCardSourceFile = "SentimentResultCard.connector.ts";
export const SentimentResultCardErrors = {
    ContentFetchError: <AppError>{
        id: 28710,
        source: SentimentResultCardSourceFile,
        severity,
        pb
    }
};

/** Represents Sentiment Map Connector related app errors */
const SentimentMapSourceFile = "SentimentMap.connector.ts";
export const SentimentMapErrors = {
    ContentFetchError: <AppError>{
        id: 28720,
        source: SentimentMapSourceFile,
        severity,
        pb
    }
};

/** Represents Election Results Card Connector related app errors */
const ElectionResultsCardFile = "ElectionResultsCard.connector.ts";
export const ElectionResultsCardErrors = {
    ContentFetchError: <AppError>{
        id: 28730,
        source: ElectionResultsCardFile,
        severity,
        pb
    }
};

/** Represents Sentiment Bar chart Connector related app errors */
const SentimentBarChartSourceFile = "SentimentBarChart.connector.ts";
export const SentimentBarChartErrors = {
    ContentFetchError: <AppError>{
        id: 28740,
        source: SentimentBarChartSourceFile,
        severity,
        pb
    }
};

/** Elction Result Map connector App errors */
const ElectionResultMapConnectorSourceFile = "ElectionResultMap.connector.ts";
export const ElectionResultMapConnectorErrors = {
    UnknownError: <AppError>{
        id: 28750,
        source: ElectionResultMapConnectorSourceFile,
        severity
    },

    ContentFetchError: <AppError>{
        id: 28751,
        source: ElectionResultMapConnectorSourceFile,
        severity
    }
};

/** Elction Result Map Inspector App errors */
const ElectionResultMapInspectorSourceFile = "ElectionResultMap.inspector.ts";
export const ElectionResultMapInspectorErrors = {
    EmptyResultSummaryError: <AppError>{
        id: 28752,
        source: ElectionResultMapInspectorSourceFile,
        severity
    }
};
/** Elction Result Map App errors */
const ElectionResultMapSourceFile = "ElectionResultMap.tsx";
export const ElectionResultMapErrors = {
    FetchAndUpdateElectionDataError: <AppError>{
        id: 28753,
        source: ElectionResultMapSourceFile,
        severity
    }
};

/** Election Editor's Note App errors */
export const ElectionEditorsNoteErrors = {

    ContentFetchError: <AppError>{
        id: 28760,
        source: "ElectionEditorsNote.connector.ts",
        severity,
        pb
    }
};

/** Represents Election Delegate Counter related app errors */
export const ElectionDelegateCounterErrors = {
    ContentFetchError: <AppError>{
        id: 28770,
        source: "ElectionDelegateCounter.connector.ts",
        severity,
        pb
    }
};

/** Represents fetch failure for Feed visualizer experience */
export const FeedVisualizerFetchFailed: AppError = {
    id: 28780,
    source: "FeedVisualizer.connector.ts",
    severity,
    pb
};

/**
 * Topics hub - failed to fetch topic by name
 */
export const TopicByNameFetchFailed: AppError = {
    id: 28790,
    source: "TopicsHub.connector.ts",
    severity,
    pb
};

/**
 * App reporting messages related to Partner Widget Experience
 */
export const PartnerWidgetAppErrors = {
    /** default breakpoint settings missing */
    DefaultBreakpointSettingsMissing: <AppError>{
        id: 28801,
        source: "PartnerWidget.connector.ts",
        severity,
        pb
    },

    /** non whitelisted source url */
    NonWhitelistedSourceUrl: <AppError>{
        id: 28802,
        source: "PartnerWidget.connector.ts",
        severity,
        pb
    }
};

/** Represents RecommendedSearch related App errors */
const RecommendedTopicsSourceFile = "RecommendedTopics.connector.ts";
export const RecommendedTopicsErrors = {
    /** Failed to connect to the Topics Data Connector */
    TopicsDataConnectorFailed: <AppError>{
        id: 28901,
        source: RecommendedTopicsSourceFile,
        severity,
        pb
    },

    /** Topic Clicked was null */
    InvalidTopicClick: <AppError>{
        id: 28902,
        source: RecommendedTopicsSourceFile,
        severity,
        pb
    }
};

const webWorkerSource = "Web worker";
const webWorkerUtility = "WebWorkerUtility.ts";
export const WebWorkerErrors = {
    /** Web worker script load error (404 response)  */
    FileNotFound: <AppError>{
        id: 29000,
        source: webWorkerUtility,
        severity,
        pb
    },

    /** Web worker runtime error, fired by web worker on unhandled exceptions */
    RuntimeError: <AppError>{
        id: 29001,
        source: webWorkerSource,
        severity,
        pb
    },

    /** Web worker message timeout */
    MessageTimeout: <AppError>{
        id: 29002,
        source: webWorkerUtility,
        severity: Severity.NoAlert,
        pb
    },

    /** Web worker handled errors. These are the errors received as messages from web worker */
    HandledError: <AppError>{
        id: 29003,
        source: webWorkerSource,
        severity,
        pb
    }
};

const PageTimingsSource = "entry-point-shared-utils/src/PageTimings.ts";
/**
 * Indicates that for some reason the page never received all the perf markers it was waiting for
 * resulting in the timeout logic kicking in.
 */
export const WaitForAllPerfMarkersTimeoutInvoked: AppError = {
    id: 29100,
    source: PageTimingsSource,
    severity: Severity.Alert,
    pb
};

/**
 * Indicates that for some reason the page never received all the secondary perf markers
 * it was waiting for, resulting in the timeout logic kicking in.
 */
export const WaitForAllSecondaryPerfMarkersTimeoutInvoked: AppError = {
    id: 29101,
    source: PageTimingsSource,
    severity: Severity.Alert,
    pb
};

const PageLoadTimeSource = "entry-point-shared-utils/src/TrackPageLoadTime.ts";
/**
 * Indicates that for some reason the page load time is not able to be sent out.
 */
export const PageLoadTimeError: AppError = {
    id: 29102,
    source: PageLoadTimeSource,
    severity: Severity.Alert,
    pb
};

const FirstPartyEntryPointFileName = "FirstParty.connector.ts";
export const FirstPartyEntryPointAppErrors = {
    /**
     * Error during app initialization for FirstParty page experience.
     */
    FirstPartyErrorPage: <AppError>{
        id: 29200,
        source: FirstPartyEntryPointFileName,
        severity,
        pb
    },
    /** Error during initial SSO call, either the call failed of the user was not signed in */
    SSOFailedError: <AppError>{
        id: 29250,
        source: pageTsFileName,
        severity,
        pb
    }
};

const HomePageEntryPointFileName = "HomePage.connector.ts";
export const HomePageEntryPointAppErrors = {
    /**
     * Error during app initialization for Home page experience.
     */
    HomePageErrorPage: <AppError>{
        id: 29300,
        source: HomePageEntryPointFileName,
        severity,
        pb
    },
    /** Error during initial SSO call, either the call failed of the user was not signed in */
    SSOFailedError: <AppError>{
        id: 29301,
        source: pageTsFileName,
        severity,
        pb
    }
};

const WindowsEntryPointFileName = "Windows.connector.ts";

export const WindowsEntryPointAppErrors = {
    /**
     * Error during app initialization for Windows page experience.
     */
    WindowsErrorPage: <AppError>{
        id: 29400,
        source: WindowsEntryPointFileName,
        severity,
        pb
    },

    /**
     * Tracking errors where we ended up showing the local ntp page on page exception.
     */
    WindowsPageShownPageExceptionError: <AppError>{
        id: 29401,
        source: pageTsFileName,
        severity,
        pb
    },

    /**
     * Tracking errors where we ended up reloading the NTP page on jss mismatch exception.
     */
    WindowsPageReloadedPageExceptionError: <AppError>{
        id: 29402,
        source: pageTsFileName,
        severity,
        pb
    },

    /**
     * Error fetching the APP_ANON cookie on Windows page load
     */
    ErrorFetchingAppAnonCookie: <AppError>{
        id: 29403,
        source: pageTsFileName,
        severity,
        pb
    }
};

const WeatherAppEntrySourceFile = "Weather.connector.ts";
const WeatherLocationSearchSourceFile = "WeatherLocationSearch.connector.ts";
const WeatherMapSourceFile = "WeatherMap.connector.ts";
const WeatherMapHelperClient = "WeatherMapHelperClient.ts";
const WeatherInfoCardConnectorSourceFile = "WeatherInfoCard.connector.ts";
const WeatherWpoApiDataMapperFile = "libs/feed-layout/.../WeatherCardDataMapper";

/** Error during 'weather' app initialization. */
export const WeatherEntryPointAppError: AppError = {
    id: 29500,
    source: WeatherAppEntrySourceFile,
    severity,
    pb
};

/** Fetch no data when search for locations from msn api */
export const WeatherLocationSearchNullError: AppError = {
    id: 29501,
    source: WeatherDataConnectorSourceFile,
    severity,
    pb
};

/** Exception to fetch search for locations from msn api */
export const WeatherLocationSearchFetchError: AppError = {
    id: 29502,
    source: WeatherDataConnectorSourceFile,
    severity,
    pb
};

/** Warnings when weather fail on GoogleBot traffic */
export const WeatherGoogleBotError: AppError = {
    id: 29503,
    source: WeatherOverviewApiClientSourceFile,
    severity,
    pb
};

/** Warnings when weather location search is invalid */
export const WeatherLocationSearchInvalidSuggestionIdError: AppError = {
    id: 29504,
    source: WeatherLocationSearchSourceFile,
    severity,
    pb
};

/** Exception when fail to fetch hurricane data */
export const WeatherMapHurricaneApiError: AppError = {
    id: 29505,
    source: WeatherMapSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when Hurricane schema broken */
export const WeatherMapHurricaneSchemaError: AppError = {
    id: 29506,
    source: WeatherMapSourceFile,
    severity: Severity.Alert,
    pb
};

/** Exception when write user weather settings to pdp */
export const WeatherPDPUserSettingsWriteFailed: AppError = {
    id: 29507,
    source: WeatherPdpClientSourceFile,
    severity,
    pb
};

/** Exception when OneService response is incorrect */
export const WeatherCardOneServiceDataError: AppError = {
    id: 29508,
    source: WeatherInfoCardConnectorSourceFile,
    severity,
    pb
};

/** Exception when weather card is in invalid state */
export const WeatherCardInvalidStateError: AppError = {
    id: 29509,
    source: WeatherCardConnectorSourceFile,
    severity,
    pb
};

/** Exception when fail to fetch weather tile version */
export const WeatherTileVersionApiError: AppError = {
    id: 29510,
    source: WeatherMapHelperClient,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Exception when schema not match when fetch weather tile version */
export const WeatherTileVersionApiSchemaError: AppError = {
    id: 29511,
    source: WeatherMapHelperClient,
    severity: Severity.HighImpact,
    pb
};

/** Exception when save home fail for non-sign-in user with HttpOnly cookie */
export const WeatherServerCookieSaveError: AppError = {
    id: 29512,
    source: WeatherDataConnectorSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fail to fetch Bing map tile data */
export const WeatherMapBingTileLayerApiError: AppError = {
    id: 29513,
    source: WeatherMapWeatherLayersSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fail to fetch radar tile data */
export const WeatherMapRadarApiError: AppError = {
    id: 29514,
    source: WeatherMapRadarSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fail to fetch Precipitation tile data */
export const WeatherMapPrecipitationApiError: AppError = {
    id: 29515,
    source: WeatherMapPrecipitationSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fail to fetch GenericSevere tile data */
export const WeatherMapGenericSevereApiError: AppError = {
    id: 29516,
    source: WeatherMapGenericSevereLayerSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fail to fetch Heatmap tile data */
export const WeatherMapHeatmapApiError: AppError = {
    id: 29517,
    source: WeatherMapHeatmapRenderLayerSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fail to fetch animation tile data */
export const WeatherMapWindAnimationApiError: AppError = {
    id: 29518,
    source: WeatherMapWeatherLayersSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fail to fetch animation tile data */
export const WeatherMapBorderApiError: AppError = {
    id: 29519,
    source: WeatherMapWeatherLayersSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fail to fetch Bing json label tile data */
export const WeatherMapJsonLabelApiError: AppError = {
    id: 29520,
    source: WeatherMapBingJsonLabelLayerSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fail to fetch OneCanvasTileLayer tile data */
export const WeatherMapOneCanvasTileLayerApiError: AppError = {
    id: 29521,
    source: WeatherMapBingOneCanvasTileLayerSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when fetch current weather failed */
export const WeatherFetchCurrentWeatherError: AppError = {
    id: 29522,
    source: MultiLocationWeatherServiceSourceFile,
    severity: Severity.HighImpact,
    pb
};

/** Exception when weather data mapper error */
export const WeatherWpoApiDataMapperError: AppError = {
    id: 29523,
    source: WeatherWpoApiDataMapperFile,
    severity,
    pb
};

export const WeatherSSRSkip: AppError = {
    id: 29524,
    source: "web.page.ts",
    severity,
    pb
};

export const WeatherSSRException: AppError = {
    id: 29525,
    source: "node.index.ts",
    severity,
    pb
};

/** Failed to fetch weather data for Summary card in O365 feed */
export const SummaryCardWeatherDataConnector: AppError = {
    id: 29526,
    source: WeatherDataConnectorSourceFile,
    severity,
    pb
};

const WindowsShellPage = "windows-shell/page.ts";

/**
 * Windows Shell Page Error fetching the APP_ANON cookie
 */
export const WindowsShellPageErrorFetchingAppAnonCookie: AppError = {
    id: 29600,
    source: WindowsShellPage,
    severity,
    pb
};

/**
 * Windows Shell Page level exception was thrown
 */
export const WindowsShellPageError: AppError = {

    id: 29601,
    source: WindowsShellPage,
    severity,
    pb
};

const WindowsShellCardProvider = "WindowsShellCardProvider.ts";
/**
 * Content that was retrieved from shell was invalid
 */
export const WindowsShellInvalidShellContentResponse: AppError = {
    id: 29602,
    source: WindowsShellCardProvider,
    severity,
    pb
};

/**
 * Content format was invalid
 */
export const WindowsShellInvalidContentResponse: AppError = {
    id: 29603,
    source: WindowsShellCardProvider,
    severity,
    pb
};

/**
 * Content was valid JSON but was missing items
 */
export const WindowsShellEmptyShellContentResponse: AppError = {
    id: 29604,
    source: WindowsShellCardProvider,
    severity,
    pb
};

/**
 * Received empty content after asking client to update the Oneservice content
 */
export const WindowsShellFailedToGetContentOnRetry: AppError = {
    id: 29605,
    source: WindowsShellCardProvider,
    severity,
    pb
};

/**
 * Tracking how often we need to ask client to retry getting new Oneservice content
 */
export const WindowsShellAskingToFetchNewContentForRetry: AppError = {
    id: 29606,
    source: WindowsShellCardProvider,
    severity: Severity.NoAlert,
    pb
};

const WindowsShellConnector = "WindowsShellConnector.connector.ts";

export const WindowsShellEmptyHeaderResponse: AppError = {
    id: 29700,
    source: WindowsShellConnector,
    severity,
    pb
};

export const WindowsShellInvalidFeedResponse: AppError = {
    id: 29701,
    source: WindowsShellConnector,
    severity,
    pb
};

export const WindowsShellFailedVersionResponse: AppError = {
    id: 29702,
    source: WindowsShellConnector,
    severity,
    pb
};

export const WindowsShellInvalidTreatmentInfo: AppError = {
    id: 29703,
    source: WindowsShellConnector,
    severity: Severity.NoAlert,
    pb
};

export const WindowsShellNoSessionIdProvided: AppError = {
    id: 29704,
    source: WindowsShellConnector,
    severity: Severity.NoAlert,
    pb
};

const WindowsNewsAndInterestsPage = "windows-newsandinterests/page.ts";
const WindowsNewsAndInterestsConnector = "WindowsNewsAndInterestsConnector.connector.ts";

export const WindowsNewsAndInterestsPageError: AppError = {

    id: 29800,
    source: WindowsNewsAndInterestsPage,
    severity,
    pb
};

export const WindowsNewsAndInterestsPageErrorFetchingAppAnonCookie: AppError = {
    id: 29801,
    source: WindowsNewsAndInterestsPage,
    severity,
    pb
};

/** Invalid Dashboard load data */
export const InvalidDashboardData: AppError = {
    id: 29802,
    source: WindowsNewsAndInterestsConnector,
    severity,
    pb
};

/** Exception fetching dashboard data from host api */
export const ExceptionFetchingDashboardData: AppError = {
    id: 29803,
    source: WindowsNewsAndInterestsConnector,
    severity,
    pb
};

/** Exception fetching user account info from host api */
export const ExceptionFetchingAccountInfo: AppError = {
    id: 29804,
    source: WindowsNewsAndInterestsConnector,
    severity,
    pb
};

/** Exception fetching auth token from host api */
export const ExceptionFetchingAuthToken: AppError = {
    id: 29805,
    source: WindowsNewsAndInterestsConnector,
    severity,
    pb
};

/** Exception loading host api */
export const ExceptionLoadingHostApi: AppError = {
    id: 29806,
    source: WindowsNewsAndInterestsConnector,
    severity,
    pb
};

/** Exception subscribing dashboard events */
export const ExceptionSubscribingDashboardEvents: AppError = {
    id: 29807,
    source: WindowsNewsAndInterestsConnector,
    severity,
    pb
};

/** Exception subscribing dashboard events */
export const NotifyFeedDisplayReadyEventFailed: AppError = {
    id: 29808,
    source: WindowsNewsAndInterestsConnector,
    severity: Severity.NoAlert,
    pb
};

const WindowsNewsPlusPage = "windows-newsplus/page.ts";
export const WindowsNewsPlusPageError: AppError = {
    id: 29900,
    source: WindowsNewsPlusPage,
    severity,
    pb
};

/** Auth related app messages (errors|warnings) */
export const AuthenticationAppMessages = {
    AuthRefreshBlockedError: <AppError>{
        id: 33000,
        source: "",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error fetching the APP_ANON cookie
     */
    ErrorFetchingAppAnonCookie: <AppError>{
        id: 33001,
        source: "",
        severity,
        pb
    },

    /**
     * Error during app initialization for migration consent
     */
    EstablishMigrationConsent: <AppError>{
        id: 33010,
        source: "EstablishMigrationConsent.ts",
        severity,
        pb
    },

    MigrationConsentServiceClient: <AppError>{
        id: 33011,
        source: "MigrationConsentServiceClient.ts",
        severity,
        pb
    },

    // This is a duplicate of 1405 and will be logged in addition to it.
    // This is to maintain backwards compatibility while allowing us to alert on this
    // from EdgeNext
    EstablishSignedInState: <AppError>{
        id: 33012,
        source: establishSignInSource,
        severity,
        pb
    },

    // Auth private property is not available in edge chromium browser
    EdgeChromiumAuthPropertyNotAvailable: <AppError>{
        id: 33013,
        source: pageTsFileName,
        severity: Severity.NoAlert,
        pb
    },

    // The browser has reported that a user is signed in, but the request to fetch the user token has failed
    // This will usually result in a sign in state mismatch, which will cause a single page refresh for the user
    // If the user cannot get the token on the refreshed page they will no longer see signed in personalization
    EdgeChromiumTokenAccessFailure: <AppError>{
        id: 33014,
        source: "EstablishEdgeChromiumSignedInState.ts",
        severity,
        pb
    }
};

/** Server side rendering error */
export const RenderServerError: AppError = {
    id: 34000,
    source: "service-render-app.ts",
    severity: Severity.NoAlert,
    pb
};

/**
 * SSR Inline script errors
 */
export const SSRAppErrors = {
    /**
     * Warning that user clicked on the page before hydration and telemetry is initialized.
     */
    UntrackedClick: <AppError>{
        id: 34001,
        source: "",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Warning that SSR fragment fetch failed
     */
    FragmentFetchFailed: <AppError>{
        id: 34002,
        source: "SSRInline.ts",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error that the Performance API was not available.
     */
    DidUseBrowserCacheAPIError: <AppError>{
        id: 34003,
        source: "SSRInline.ts",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error that the expected response header was not available.
     */
    DidUseBrowserCachePolyfillResponseHeaderInvalid: <AppError>{
        id: 34004,
        source: "SSRInline.ts",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error that when SSR failed to getConfig.
     */
    ServerCacheFetchFailed: <AppError>{
        id: 34005,
        source: "Resolver.ts",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error that when connectorMap is invalid.
     */
    ConnectorMapInvalid: <AppError>{
        id: 34006,
        source: "Resolver.ts",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error that SSR inline script failed.
     */
    FetchSSRPageFailed: <AppError>{
        id: 34007,
        source: "inline-head.ts",
        severity: Severity.Alert,
        pb
    },

    /**
     * Error that no window.innerWidth was reported.
     * Devices on mobile may report a window.innerWidth of 0 or undefined until the window.onLoad page completes.
     * This error is thrown when window.innerWidth is still 0 or undefined when onload has completed.
     * We expect this error to never throw.
     */
    FetchSSRPageFailedFromInvalidWindowInnerWidth: <AppError>{
        id: 34008,
        source: "inline-head.ts",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error that when Server Cache config refresh failed
     */
    ServerCacheRefreshFailed: <AppError>{
        id: 34009,
        source: "Resolver.ts",
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error during app initialization for page experience.
     */
    WebEntryFailed: <AppError>{
        id: 34010,
        source: "webEntry.ts",
        severity: Severity.Alert,
        pb
    },

    /**
     * SSR config resolver returned null config
     */
    SsrResolverReturnedNullConfig: <AppError>{
        id: 34011,
        source: "SsrResolver.ts",
        severity: Severity.HighImpact,
        pb
    }
};

const irisArcCallerFileName = "Iris.arcCaller.ts";
const irisArcParserFileName = "Iris.arcParser.ts";
const irisMasterFileName = "Iris.master.ts";

/** The query parameters presented to the arc caller were found to be invalid. */
export const AppError_InvalidQParams_35001: AppError = {
    id: 35001,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_ArcCallFailed_35002: AppError = {
    id: 35002,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_ArcResponseNotOk_35003: AppError = {
    id: 35003,
    source: irisArcCallerFileName,
    severity: Severity.NoAlert,
    pb
};
export const AppError_ArcResponseUnparsable_35004: AppError = {
    id: 35004,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_UnknownIrisErrorFound_35006: AppError = {
    id: 35006,
    source: irisArcParserFileName,
    severity,
    pb
};
export const AppError_TimeoutIrisErrorFound_35007: AppError = {
    id: 35007,
    source: irisArcParserFileName,
    severity,
    pb
};
export const AppError_TemplateIrisErrorFound_35008: AppError = {
    id: 35008,
    source: irisArcParserFileName,
    severity,
    pb
};
export const AppError_ClientErrorIrisErrorFound_35009: AppError = {
    id: 35009,
    source: irisArcParserFileName,
    severity,
    pb
};
export const AppError_DSourceIrisErrorFound_35010: AppError = {
    id: 35010,
    source: irisArcParserFileName,
    severity,
    pb
};
export const AppError_ActionCallFailure_35011: AppError = {
    id: 35011,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_ActionFetchFailure_35012: AppError = {
    id: 35012,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_ActionResponseFailure_35013: AppError = {
    id: 35013,
    source: irisArcCallerFileName,
    severity: Severity.NoAlert,
    pb
};
export const AppError_UncaughtErrorDetected_35014: AppError = {
    id: 35014,
    source: irisMasterFileName,
    severity,
    pb
};
export const AppError_UnconvertableCreative_35015: AppError = {
    id: 35015,
    source: irisMasterFileName,
    severity,
    pb
};
export const AppError_InvalidResponseFormat_35016: AppError = {
    id: 35016,
    source: irisMasterFileName,
    severity,
    pb
};
export const AppError_UnparsableSingleResponseJSON_35018: AppError = {
    id: 35018,
    source: irisMasterFileName,
    severity,
    pb
};
export const AppError_MissingQueryParams_35019: AppError = {
    id: 35019,
    source: irisMasterFileName,
    severity,
    pb
};
export const AppError_MissingContext_35020: AppError = {
    id: 35020,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_MissingAppEnvironment_35021: AppError = {
    id: 35021,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_UndefinedRequestParams_35022: AppError = {
    id: 35022,
    source: irisArcCallerFileName,
    severity: Severity.NoAlert,
    pb
};
export const AppError_UndefinedAppEnvironment_35023: AppError = {
    id: 35023,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_MissingAppEnvironmentProperty_35024: AppError = {
    id: 35024,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_ArcCallFailure_35025: AppError = {
    id: 35025,
    source: irisArcCallerFileName,
    severity,
    pb
};
export const AppError_ArcCallFailureIrisUpdate_35026: AppError = {
    id: 35026,
    source: irisArcCallerFileName,
    severity,
    pb
};

/** Represents Iris data connector related App errors */
const IrisDataConnectorSourceFile = "IrisData.connector.ts";
const SubscriberValidatorSourceFile = "SubscriberValidator.ts";
const IrisPersistentStorageUtilityFile = "IrisPersistentStorageUtility.ts";
const IrisPersistentStorage = "IrisPersistentStorage.ts";

export const SubscriberNotFound: AppError = {
    id: 35117,
    source: SubscriberValidatorSourceFile,
    severity,
    pb
};

export const CreativeCountNegativeOrZero: AppError = {
    id: 35118,
    source: IrisDataConnectorSourceFile,
    severity,
    pb
};

export const MissingIrisParameterForConditionalBanner: AppError = {
    id: 35119,
    source: SubscriberValidatorSourceFile,
    severity: Severity.NoAlert,
    pb
};

export const ErrorFoundForSurface: AppError = {
    id: 35120,
    source: IrisDataConnectorSourceFile,
    severity,
    pb
};

export const SubscriberNotFoundInIrisPlacementDictionary: AppError = {
    id: 35121,
    source: IrisDataConnectorSourceFile,
    severity,
    pb
};

export const ConfigDataUndefined: AppError = {
    id: 35122,
    source: IrisPersistentStorageUtilityFile,
    severity,
    pb
};

export const FailedToFetchConfigData: AppError = {
    id: 35123,
    source: IrisPersistentStorageUtilityFile,
    severity,
    pb
};

export const ExceptionFetchingConfigData: AppError = {
    id: 35124,
    source: IrisPersistentStorageUtilityFile,
    severity,
    pb
};

export const FailedToReadNurturingAcceptedFlag: AppError = {
    id: 35125,
    source: IrisPersistentStorageUtilityFile,
    severity,
    pb
};

/** Error when Iris fails to return Hotspots Data to the client. Previous #8181 */
export const FailedHotSpotsDataDelivery: AppError = {
    id: 35126,
    source: IrisPersistentStorageUtilityFile,
    severity,
    pb
};

/** Hotspots data that was returned failed to validate. Previous #8182 */
export const IncompleteHotSpotsContract: AppError = {
    id: 35127,
    source: IrisPersistentStorageUtilityFile,
    severity,
    pb
};

/** Exception making iris call from data connector */
export const ExceptionRequestingSurfaceInfo: AppError = {
    id: 35128,
    source: IrisDataConnectorSourceFile,
    severity,
    pb
};

/** Error missing iris parameters for iris call to background images surface */
export const MissingIrisParameterForBackgroundImages: AppError = {
    id: 35129,
    source: SubscriberValidatorSourceFile,
    severity: Severity.NoAlert,
    pb
};

/** Error missing params in surface collection when reading data from iris response */
export const SurfaceCollectionContextMissingParams: AppError = {
    id: 35130,
    source: IrisDataConnectorSourceFile,
    severity,
    pb
};

/** Chromium API not available to persist data */
export const IrisPersistChromiumAPIsNotAvailable: AppError = {
    id: 35131,
    source: IrisPersistentStorage,
    severity,
    pb
};

/** Chromium API not available to get data */
export const IrisGetChromiumAPIsNotAvailable: AppError = {
    id: 35132,
    source: IrisPersistentStorage,
    severity,
    pb
};

/** Chromium API failure */
export const IrisChromiumAPIsFailure: AppError = {
    id: 35133,
    source: IrisPersistentStorage,
    severity,
    pb
};

/** Represents AccessTokens related App errors */
const AccessTokensSourceFile = "AadAuthHelper.ts";
const PivotContentErrorPlaceholderFile = "AadErrorPlaceholder.tsx";
export const EdgeChromiumAPIsNotAvailable: AppError = {
    id: 35200,
    source: AccessTokensSourceFile,
    severity,
    pb
};

export const AcquireAccessTokenFailed: AppError = {
    id: 35201,
    source: AccessTokensSourceFile,
    severity,
    pb
};

export const AcquireAccessTokenFailedForPivotContent: AppError = {
    id: 35202,
    source: PivotContentErrorPlaceholderFile,
    severity,
    pb
};

export const AcquireAccessTokenHandledFailure: AppError = {
    id: 35203,
    source: AccessTokensSourceFile,
    severity: Severity.NoAlert,
    pb
};

export const AcquireAccessTokenHandledFailureForPivotContent: AppError = {
    id: 35204,
    source: PivotContentErrorPlaceholderFile,
    severity: Severity.NoAlert,
    pb
};

/** Represents Render Cache errors*/
const PersistRenderCacheFile = "PersistRenderCache.ts";
export const RenderCacheErrors = {
    JssStyleMismatchWarning: <AppError>{
        id: 35300,
        source: PersistRenderCacheFile,
        severity: Severity.NoAlert,
        pb
    },

    JssStyleCriticalMismatchError: <AppError>{
        id: 35301,
        source: PersistRenderCacheFile,
        severity: Severity.Alert,
        pb
    }
};

/** Represents OcvFeedbackData related App errors */
const OcvFeedbackDataSourceFile = "OcvFeedbackData.ts";
export const OcvFeedbackDataErrors = {
    UndefinedDataNotAvailable: <AppError>{
        id: 35400,
        source: OcvFeedbackDataSourceFile,
        severity: Severity.NoAlert,
        pb
    }
};

export const PivotHoverErrors = {
    /** Failed to fetch feed summary data for the Pivot Hover list. */
    FeedSummaryFetchError: <AppError>{
        id: 35500,
        source: "FeedSummaryFetcher.ts",
        severity,
        pb
    }
};

const PreloadActionHelpers = "PreloadActionHelpers.ts";
export const PreloadReportingMessages = {
    TtvrMissing: <AppError>{
        id: 35600,
        source: PreloadActionHelpers,
        severity: Severity.NoAlert,
        pb
    },
    TfprMissing: <AppError>{
        id: 35601,
        source: PreloadActionHelpers,
        severity: Severity.NoAlert,
        pb
    },
    TtvrSearchBoxMissing: <AppError>{
        id: 35602,
        source: PreloadActionHelpers,
        severity: Severity.NoAlert,
        pb
    },
    TtfSearchBoxMissing: <AppError>{
        id: 35603,
        source: PreloadActionHelpers,
        severity: Severity.NoAlert,
        pb
    }
};

const PrerenderPageTimingHelper = "PrerenderPageTimingHelper.ts";
export const PrerenderTtvrMissing: AppError = {
    id: 35604,
    source: PrerenderPageTimingHelper,
    severity: Severity.NoAlert,
    pb
};

export const PrerenderTtprMissing: AppError = {
    id: 35605,
    source: PreloadActionHelpers,
    severity: Severity.NoAlert,
    pb
};

export const PrerenderTtvrSearchBoxMissing: AppError = {
    id: 35606,
    source: PrerenderPageTimingHelper,
    severity: Severity.NoAlert,
    pb
};

export const PrerenderTtfSearchBoxMissing: AppError = {
    id: 35607,
    source: PrerenderPageTimingHelper,
    severity: Severity.NoAlert,
    pb
};

export const PrerenderRevalidateException: AppError = {
    id: 35608,
    source: "PageRevealStaleStracker.ts",
    severity: Severity.NoAlert,
    pb
};

export const GetTabTimingsReturnedFalse: AppError = {
    id: 35609,
    source: PrerenderPageTimingHelper,
    severity: Severity.NoAlert,
    pb
};

export const GetTabTimingsReturnedInvalidTimings: AppError = {
    id: 35610,
    source: PrerenderPageTimingHelper,
    severity: Severity.NoAlert,
    pb
};

/** Represents push notifications lib App errors */
const PushNotificationsLibFile = "PushNotifications.ts";
export const PushNotificationsLibErrors = {
    FetchKeyError: <AppError>{
        id: 35700,
        source: PushNotificationsLibFile,
        severity,
        pb
    },
    SubscribeUnsubscribeError: <AppError>{
        id: 35701,
        source: PushNotificationsLibFile,
        severity,
        pb
    }
};

/** Represents content truncation lib App errors */
const ContentTruncationSourceFile = "ContentTruncation.ts";
export const ContentTruncationErrors = {
    OverTruncationError: <AppError>{
        id: 35800,
        source: ContentTruncationSourceFile,
        severity,
        pb
    }
};

const SuperAppEntryPointFileName = "SuperAppPage.connector.ts";

export const SuperAppEntryPointAppErrors = {
    /**
     * Super app page is not able to load Error Page.
     */
    SuperAppErrorPage: <AppError>{
        id: 35901,
        source: SuperAppEntryPointFileName,
        severity,
        pb
    }
};

/** Error fetching feed with location in Local News experience */
const LocalNewsFileName = "LocalNews.connector.ts";
export const LocalNewsInterestByLocationFetchFailed: AppError = {
    id: 36001,
    source: LocalNewsFileName,
    severity,
    pb
};

/** 361XX represent office related errors */
const GenerateShareUiFile = "GenerateShareUi.tsx";
const OfficeShareUiFile = "OfficeShareUi.tsx";
const OfficeFallbackHandlerFileName = "OfficeFallbackHandler.ts";
const EnterpriseWaffleFile = "EnterpriseWaffle.connector.ts";
const OfficeDocumentsTableConnectorFile = "OfficeDocumentsTable.connector.ts";
const OfficeDocumentsTableMruUtilitiesFile = "mru-plusplus.utility.ts";
const OfficeSharePointSitesTableConnectorFile = "SharePointSitesTable.connector.ts";
const OfficeDocumentsTableDiscoverFile = "DiscoverDocumentProvider.ts";
const RecommendedDocumentsConnectorFile = "RecommendedDocuments.connector.ts";
const ShellNewTabDataFileName = "ShellNewTabData.connector.ts";
const OfficeDocumentProviderFile = "OfficeDocumentProvider.ts";

export const OfficeFallbackHandlerAppErrors = {
    /**
     * Tracking errors where we ended up reloading the office content on jss mismatch exception.
     */
    OfficeContentReloadedJSSMismatchExceptionError: <AppError>{
        id: 36101,
        source: OfficeFallbackHandlerFileName,
        severity,
        pb
    }
};

/**
 * Error for failure to generate office share ui
 */
export const OfficeGenerateShareUiError: AppError = {
    id: 36105,
    source: GenerateShareUiFile,
    severity,
    pb
};

/**
 * Error for failure inside office share ui
 */
export const OfficeShareUiError: AppError = {
    id: 36106,
    source: OfficeShareUiFile,
    severity,
    pb
};

/**
 * Error for Enterprise Waffle not able to retrieve app links from ShellNewTabData
 */
export const EnterpriseWaffleAppError: AppError = {
    id: 36107,
    source: EnterpriseWaffleFile,
    severity,
    pb
};

/**
 * Error for failed call to Shell NTP API for Enterprise waffle and company logo
 */
export const ShellNewTabDataAPIError: AppError = {
    id: 36108,
    source: ShellNewTabDataFileName,
    severity,
    pb
};

/**
 * Tracking errors when Office Documents Table type is not provided
 */
export const OfficeDocumentsTableInvalidTypeError: AppError = {
    id: 36110,
    source: OfficeDocumentsTableConnectorFile,
    severity,
    pb
};

/**
 * Tracking errors when Office Documents Table fails to fetch documents
 */
export const OfficeDocumentsTableDocumentFetchFailedError: AppError = {
    id: 36111,
    source: OfficeDocumentsTableConnectorFile,
    severity,
    pb
};

/**
 * Tracking errors when Office Documents Table fails to fetch documents
 */
export const OfficeDocumentsTableLinkFetchFailedError: AppError = {
    id: 36112,
    source: OfficeDocumentsTableConnectorFile,
    severity,
    pb
};

/**
 * Tracking errors when Office Documents Table fails to update document properties
 */
export const OfficeDocumentsTableUpdateDocumentPropertiesFailed: AppError = {
    id: 36113,
    source: OfficeDocumentsTableMruUtilitiesFile,
    severity,
    pb
};

/**
* Tracking errors when Office Documents Table fails to fetch activity data
*/
export const OfficeDocumentsTableFetchActivitiesFailed: AppError = {
    id: 36114,
    source: OfficeDocumentsTableDiscoverFile,
    severity,
    pb
};

/**
* Tracking errors when Office Documents Table fails to fetch sharepoint data
*/
export const OfficeDocumentsTableFetchSharepointFailed: AppError = {
    id: 36115,
    source: OfficeDocumentsTableDiscoverFile,
    severity,
    pb
};

/**
* Tracking errors when Office Documents Table fails to generate document item
*/
export const OfficeDocumentsTableGenerateDocumentItemError: AppError = {
    id: 36116,
    source: OfficeDocumentProviderFile,
    severity,
    pb
};

/**
 * Tracking errors when Office Share Point Table fails to fetch sites
 */
export const OfficeSharepointSitesTableFetchFailedError: AppError = {
    id: 36120,
    source: OfficeSharePointSitesTableConnectorFile,
    severity,
    pb
};

/**
 * Tracking errors when failed to get Recommended Documents
 */
export const RecommendedDocumentsFetchFailedError: AppError = {
    id: 36130,
    source: RecommendedDocumentsConnectorFile,
    severity,
    pb
};

/**
 * Tracking errors when failed to fetch preview image for recommended documents
 */
export const RecommendedDocumentsImageFetchFailedError: AppError = {
    id: 36131,
    source: RecommendedDocumentsConnectorFile,
    severity,
    pb
};

/** Represents autos experiences App errors */
const AutosExperienceConnectorFile = "AutosCard.connector.ts";
const AutosExperienceServiceClientFile = "AutosServiceClient.ts";

/**
 * Error while fetching research make models
 */
export const FetchResearchMakeModelDocumentError: AppError = {
    id: 36200,
    source: AutosExperienceServiceClientFile,
    severity: Severity.NoAlert,
    pb
};

/**
 * Error while fetching Film Strip Document Error
 */
export const FetchFilmStripDocumentError: AppError = {
    id: 36201,
    source: AutosExperienceServiceClientFile,
    severity: Severity.NoAlert,
    pb
};

/**
 * Error while fetching lead classified cms documents
 */
export const FetchClassifiedLeadCMSDocumentsError: AppError = {
    id: 36202,
    source: AutosExperienceServiceClientFile,
    severity: Severity.NoAlert,
    pb
};

/**
 * Error while fetching lead classified make models
 */
export const FetchLeadClassifiedMakeModelDocumentError: AppError = {
    id: 36203,
    source: AutosExperienceServiceClientFile,
    severity: Severity.NoAlert,
    pb
};

/**
 * Error for incorrect tab configuration
 */
export const TabsIncorrectConfiguration: AppError = {
    id: 36204,
    source: AutosExperienceConnectorFile,
    severity: Severity.NoAlert,
    pb
};

/** Whole Page Optimization(WPO) App errors */

const WpoServiceFileName = "WpoServiceClient.ts";
const WpoMapFeedDataFileName = "DesktopFeedEdgeNext.connector.ts";
const WpoCardTypeUndefinedFileName = "WpoCardFeedDataMapper.ts";

/**
 * WPO service call failure.
 */
export const WpoServiceCallFailure: AppError = {
    id: 36301,
    source: WpoServiceFileName,
    severity,
    pb
};

/**
 * Error updating local storage with wpo service call response.
 */
export const WpoFailedToStoreToLocalStorage: AppError = {
    id: 36302,
    source: WpoServiceFileName,
    severity,
    pb
};

/** Whole Page Optimization(WPO) App errors in SuperApp */
const WpoServiceHelperFileName = "WPOServiceHelper.ts";

/**
 * WPO service call failure for glance cards in SA.
 */
export const WpoGlanceServiceCallFailure: AppError = {
    id: 36303,
    source: WpoServiceHelperFileName,
    severity,
    pb
};

/**
 * WPO service call no content respose for glance cards in SA.
 */
export const WpoGlanceServiceCallNoContent: AppError = {
    id: 36304,
    source: WpoServiceHelperFileName,
    severity,
    pb
};

/**
 * WPO service call failure for feed modules in SA.
 */
export const WpoFeedServiceCallFailure: AppError = {
    id: 36305,
    source: WpoServiceHelperFileName,
    severity,
    pb
};

/**
 * WPO service call no content respose for feed modules in SA.
 */
export const WpoFeedServiceCallNoContent: AppError = {
    id: 36306,
    source: WpoServiceHelperFileName,
    severity,
    pb
};

/**
 * Error updating local storage with wpo service call response in SuperApp.
 */
export const WpoFailedToStoreToSALocalStorage: AppError = {
    id: 36307,
    source: WpoServiceHelperFileName,
    severity,
    pb
};

/**
 * Data Mapper error when WPO map feed data for card.
 */
export const WpoServiceFeedDataMapperError: AppError = {
    id: 36308,
    source: WpoMapFeedDataFileName,
    severity,
    pb
};

/**
 * Card type Undefined Error when WPO return card data.
 */
export const WpoServiceCardTypeUndefinedError: AppError = {
    id: 36309,
    source: WpoCardTypeUndefinedFileName,
    severity,
    pb
};

/** MarketSelector App errors */
const MarketSelectorFileName = "marketSetting/index.ts";

/**
 * Error when load current market setting when initialize market selector
 */
export const MarketSelectorFailedToReadCurrentMarket: AppError = {
    id: 36401,
    source: MarketSelectorFileName,
    severity,
    pb
};

/** FlyoutCard App errors */
const FlyoutCardFileName = "FlyoutCard.connector.ts";

/**
 * Error when fetching feed in FlyoutCard experience
 */
export const FlyoutCardFailedToLoadOneServiceData: AppError = {
    id: 36410,
    source: FlyoutCardFileName,
    severity,
    pb
};

/** The Microsoft logo component inside of the HeaderEdgeNext experience */
const MicrosoftLogo = "MicrosoftLogo.tsx";

/** Represents a Microsoft Logo related error */
export const MicrosoftLogoErrors: AppError = {
    id: 36420,
    source: MicrosoftLogo,
    severity: Severity.HighImpact,
    pb
};

/**
 * Clarity Script App Errors
 */
const ClarityScriptLoaderFile = "ScriptLoader.ts";

/**
 * Error loading clarity script.
 */
export const LoadScriptError: AppError = {
    id: 36500,
    source: ClarityScriptLoaderFile,
    severity,
    pb
};

/**
 * Error starting clarity script.
 */
export const ScriptStartError: AppError = {
    id: 36501,
    source: ClarityScriptLoaderFile,
    severity,
    pb
};

/** 366XX are River OneService App Errors */
export const EmptyFeedWithFeedId: AppError = {
    id: 36600,
    source: OneServiceCardProviderFileName,
    severity,
    pb
};

export const EmptySearchedFeedWithFeedId: AppError = {
    id: 36601,
    source: OneServiceCardProviderFileName,
    severity,
    pb
};

export const EmptySearchedFeedWithFeedQuery: AppError = {
    id: 36602,
    source: OneServiceCardProviderFileName,
    severity,
    pb
};

export const EmptyKidsModeFeed: AppError = {
    id: 36603,
    source: OneServiceCardProviderFileName,
    severity,
    pb
};

export const UnauthorizedFeedResponse: AppError = {
    id: 36604,
    source: OneServiceCardProviderFileName,
    severity,
    pb
};

export const CardViewFeedDataMapperInvalidData: AppError = {
    id: 36605,
    source: "OneServiceFeedDataMapper.ts",
    severity,
    pb
};

export const TabRiverRequestEmptyResponse: AppError = {
    id: 36606,
    source: OneServiceCardProviderFileName,
    severity,
    pb
};

export const TabsRequestEmptyResponse: AppError = {
    id: 36607,
    source: OneServiceCardProviderFileName,
    severity,
    pb
};

/** 367XX are River App Errors */
export const FallingBackToSkipStrategy: AppError = {
    id: 36700,
    source: RiverSourceFile,
    severity,
    pb
};

export const FallingBackToFullHeightStrategy: AppError = {
    id: 36701,
    source: RiverSourceFile,
    severity: Severity.NoAlert,
    pb
};

export const RiverContentProviderFetchNextPageException: AppError = {
    id: 36702,
    source: RiverSourceFile,
    severity,
    pb
};

export const InvalidEmptyRiverContent: AppError = {
    id: 36703,
    source: RiverSourceFile,
    severity,
    pb
};

export const GridSectionCardRenderFailure: AppError = {
    id: 36704,
    source: "GridSection.tsx",
    severity,
    pb
};

/** Represents OutlookEmailPreview related App errors */
const OutlookEmailPreviewDataSourceFile = "OutlookEmailPreviewDataHelper.ts";

export const OutlookEmailPreviewEmailFetchError: AppError = {
    id: 36800,
    source: OutlookEmailPreviewDataSourceFile,
    severity,
    pb
};

export const OutlookEmailPreviewUnreadEmailCountFetchError: AppError = {
    id: 36801,
    source: OutlookEmailPreviewDataSourceFile,
    severity,
    pb
};

const OutlookEmailPreviewAuthSourceFile = "OutlookEmailPreviewAuthHelper.ts";
export const OutlookEmailPreviewAccessTokenFetchError: AppError = {
    id: 36802,
    source: OutlookEmailPreviewAuthSourceFile,
    severity,
    pb
};

const FinancePageFilename = "Finance.connector.ts";
const FinanceUserServiceImplFile = "FinanceUserServiceImpl.ts";
const FinanceServiceImplFile = "FinanceServiceImpl.ts";
const FinancePrimeServiceImplFile = "FinancePrimeServiceImpl.ts";
const FinanceAutoSuggestServiceImplFile = "AutoSuggestServiceImpl";

/**
 * Error when initializing Finance page experience.
 */
export const FinanceEntryPointAppErrors: AppError = {
    id: 36900,
    source: FinancePageFilename,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get autosuggest because of unsuccessful fetch */
export const FinanceAutoSuggestFetchError: AppError = {
    id: 36901,
    source: FinanceAutoSuggestServiceImplFile,
    severity,
    pb
};

/** Failed to get autosuggest because the http code is not 20x */
export const FinanceAutoSuggestStatusError: AppError = {
    id: 36902,
    source: FinanceAutoSuggestServiceImplFile,
    severity,
    pb
};

/** Failed to get autosuggest because the response body is not valid */
export const FinanceAutoSuggestInvalidResponseBody: AppError = {
    id: 36903,
    source: FinanceAutoSuggestServiceImplFile,
    severity,
    pb
};

/** Failed to get MostActiveQuotes because unsuccess fetch */
export const FinanceMostActiveQuotesFetchError: AppError = {
    id: 36904,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get MostActiveQuotes because the http code is not 20x */
export const FinanceMostActiveQuotesStatusError: AppError = {
    id: 36905,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get MostActiveQuotes because the response body is not valid */
export const FinanceMostActiveQuotesInvalidResponseBody: AppError = {
    id: 36906,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Exception when fetch watchlist */
export const FinanceWatchListFetchFailed: AppError = {
    id: 36907,
    source: FinanceUserServiceImplFile,
    severity,
    pb
};

/** Failed to get Chart because unsuccess fetch */
export const FinanceChartFetchError: AppError = {
    id: 36908,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get Chart because the http code is not 20x */
export const FinanceChartStatusError: AppError = {
    id: 36909,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get Chart because the response body is not valid */
export const FinanceChartInvalidResponseBody: AppError = {
    id: 36910,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get QuoteSummary because unsuccess fetch */
export const FinanceQuoteSummaryFetchError: AppError = {
    id: 36911,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get QuoteSummary because the http code is not 20x */
export const FinanceQuoteSummaryStatusError: AppError = {
    id: 36912,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get QuoteSummary because the response body is not valid */
export const FinanceQuoteSummaryInvalidResponseBody: AppError = {
    id: 36913,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get Exchange because unsuccess fetch */
export const FinanceExchangeFetchError: AppError = {
    id: 36914,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get Exchange because the http code is not 20x */
export const FinanceExchangeStatusError: AppError = {
    id: 36915,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get Exchange because the response body is not valid */
export const FinanceExchangeInvalidResponseBody: AppError = {
    id: 36916,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get indices because of unsuccessful fetch */
export const FinanceIndicesFetchError: AppError = {
    id: 36917,
    source: FinanceAutoSuggestServiceImplFile,
    severity,
    pb
};

/** Failed to get indices because the http code is not 20x */
export const FinanceIndicesStatusError: AppError = {
    id: 36918,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get indices because the response body is not valid */
export const FinanceIndicesInvalidResponseBody: AppError = {
    id: 36919,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get equities because of unsuccessful fetch */
export const FinanceEquitiesFetchError: AppError = {
    id: 36920,
    source: FinanceAutoSuggestServiceImplFile,
    severity,
    pb
};

/** Failed to get equities because the http code is not 20x */
export const FinanceEquitiesStatusError: AppError = {
    id: 36921,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get equities because the response body is not valid */
export const FinanceEquitiesInvalidResponseBody: AppError = {
    id: 36922,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get equity financials because of unsuccessful fetch */
export const FinanceEquityFinancialsFetchError: AppError = {
    id: 36923,
    source: FinanceAutoSuggestServiceImplFile,
    severity,
    pb
};

/** Failed to get equity financials because the http code is not 20x */
export const FinanceEquityFinancialsStatusError: AppError = {
    id: 36924,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get equity financials because the response body is not valid */
export const FinanceEquityFinancialsInvalidResponseBody: AppError = {
    id: 36925,
    source: FinanceServiceImplFile,
    severity,
    pb
};

const MoneyInfoCardConnectorSourceFile = "MoneyInfoCard.connector.ts";

/** Exception when OneService response is incorrect */
export const MoneyInfoCardOneServiceDataError: AppError = {
    id: 36926,
    source: MoneyInfoCardConnectorSourceFile,
    severity,
    pb
};

/** Failed to get all currencies because of unsuccessful fetch */
export const FinanceGetAllCurrenciesFetchError: AppError = {
    id: 36927,
    source: FinanceAutoSuggestServiceImplFile,
    severity,
    pb
};

/** Failed to get all currencies because the http code is not 20x */
export const FinanceGetAllCurrenciesStatusError: AppError = {
    id: 36928,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get all currencies because the response body is not valid */
export const FinanceGetAllCurrenciesInvalidResponseBody: AppError = {
    id: 36929,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get microsoft id because of unsuccessful fetch */
export const FinanceGetMicrosoftIdFetchError: AppError = {
    id: 36930,
    source: FinancePrimeServiceImplFile,
    severity,
    pb
};

/** Failed to get microsoft id because the http code is not 20x */
export const FinanceGetMicrosoftIdStatusError: AppError = {
    id: 36931,
    source: FinancePrimeServiceImplFile,
    severity,
    pb
};

/** Failed to get microsoft id because the response body is not valid */
export const FinanceGetMicrosoftIdInvalidResponseBody: AppError = {
    id: 36932,
    source: FinancePrimeServiceImplFile,
    severity,
    pb
};

/** Failed to get MorningStar id because of unsuccessful fetch */
export const FinanceGetMorningStarIdFetchError: AppError = {
    id: 36933,
    source: FinancePrimeServiceImplFile,
    severity,
    pb
};

/** Failed to get MorningStar id because the http code is not 20x */
export const FinanceGetMorningStarIdStatusError: AppError = {
    id: 36934,
    source: FinancePrimeServiceImplFile,
    severity,
    pb
};

/** Failed to get MorningStar id because the response body is not valid */
export const FinanceGetMorningStarIdInvalidResponseBody: AppError = {
    id: 36935,
    source: FinancePrimeServiceImplFile,
    severity,
    pb
};

/** Exception when write user money settings to pdp */
export const FinancePDPUserSettingsWriteFailed: AppError = {
    id: 36936,
    source: FinanceUserServiceImplFile,
    severity,
    pb
};

/** Failed to get Funds because unsuccess fetch */
export const FinanceFundsFetchError: AppError = {
    id: 36937,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get Funds because the http code is not 20x */
export const FinanceFundsStatusError: AppError = {
    id: 36938,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get Funds because the response body is not valid */
export const FinanceFundsInvalidResponseBody: AppError = {
    id: 36939,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Reserve 3694* ids for errors in TTVR  */
/** Failed to get watchlist data before TTVR */
export const FinanceWatchlistDataVisuallyUnavailable: AppError = {
    id: 36940,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get quotes data before TTVR */
export const FinanceQuotesDataVisuallyUnavailable: AppError = {
    id: 36941,
    source: FinanceServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get charts data before TTVR */
export const FinanceChartsDataVisuallyUnavailable: AppError = {
    id: 36942,
    source: FinanceServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get movers data before TTVR */
export const FinanceMoversDataVisuallyUnavailable: AppError = {
    id: 36943,
    source: FinanceServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get exchanges data before TTVR */
export const FinanceExchangesDataVisuallyUnavailable: AppError = {
    id: 36944,
    source: FinanceServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get indices data before TTVR */
export const FinanceIndicesDataVisuallyUnavailable: AppError = {
    id: 36945,
    source: FinanceServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get equities data before TTVR */
export const FinanceEquitiesDataVisuallyUnavailable: AppError = {
    id: 36946,
    source: FinanceServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get equity financials data before TTVR */
export const FinanceEquityFinancialsDataVisuallyUnavailable: AppError = {
    id: 36947,
    source: FinanceServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get currencies data before TTVR */
export const FinanceCurrenciesDataVisuallyUnavailable: AppError = {
    id: 36948,
    source: FinanceServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get funds data before TTVR */
export const FinanceFundsDataVisuallyUnavailable: AppError = {
    id: 36949,
    source: FinanceServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get microsoft id before TTVR */
export const FinanceGetMicrosoftIdDataVisuallyUnavailable: AppError = {
    id: 36950,
    source: FinancePrimeServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get morning star id before TTVR */
export const FinanceGetMorningStarIdDataVisuallyUnavailable: AppError = {
    id: 36951,
    source: FinancePrimeServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get news data before TTVR */
export const FinanceNewsDataVisuallyUnavailable: AppError = {
    id: 36952,
    source: OneServiceCardProviderFileName,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to create portfolio */
export const FinanceCreatePortfolioFailed: AppError = {
    id: 36960,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to create transaction */
export const FinanceCreateTransactionFailed: AppError = {
    id: 36961,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to add to watchlist */
export const FinanceAddToWatchlistFailed: AppError = {
    id: 36962,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to sort watchlist */
export const FinanceSortWatchlistFailed: AppError = {
    id: 36963,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to delete from watchlist */
export const FinanceDeleteFromWatchlistFailed: AppError = {
    id: 36964,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get portfolio list. */
export const FinancePortfolioListFetchError: AppError = {
    id: 36965,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get portfolio data. */
export const FinancePortfolioDataFetchError: AppError = {
    id: 36966,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to delete portfolio. */
export const FinanceDeletePortfolioError: AppError = {
    id: 36967,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to update portfolio. */
export const FinanceUpdatePortfolioError: AppError = {
    id: 36968,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to delete transaction. */
export const FinanceDeleteTransactionError: AppError = {
    id: 36969,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to update transaction. */
export const FinanceUpdateTransactionError: AppError = {
    id: 36970,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to add to portfolio. */
export const FinanceAddToPortfolioError: AppError = {
    id: 36971,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to delete from portfolio. */
export const FinanceDeleteFromPortfolioError: AppError = {
    id: 36972,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to sort portfolio. */
export const FinanceSortPortfolioError: AppError = {
    id: 36973,
    source: FinanceUserServiceImplFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Failed to get events because of unsuccessful fetch */
export const FinanceEventsFetchError: AppError = {
    id: 36974,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get events because the http code is not 20x */
export const FinanceEventsStatusError: AppError = {
    id: 36975,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Failed to get events because the response body is not valid */
export const FinanceEventsInvalidResponseBody: AppError = {
    id: 36976,
    source: FinanceServiceImplFile,
    severity,
    pb
};

/** Warnings on Finance watchlist api. */
export const FinanceWatchlistFetchWarning: AppError = {
    id: 36990,
    source: FinanceUserServiceImplFile,
    severity: Severity.Alert,
    pb: {
        userImpacting: false
    }
};

/**
 * Error when initializing Social page experience.
 */
const SocialConnectorSourceFile = "Social.connector.ts";

export const SocialNoModuleName: AppError = {
    id: 37000,
    source: SocialConnectorSourceFile,
    severity,
    pb
};

/**
 * Error when Social experience reducer recieves incorrect arguments
 */

const SocialExperienceReducerFile = "Social.reducer.ts";

export const SocialExperienceReducerIncorrectArgumentError: AppError = {
    id: 37001,
    source: SocialExperienceReducerFile,
    severity,
    pb
};

/**
 * Error when trying to bind social data connector in social experience.
 */
export const SocialDataConnectorNotFound: AppError = {
    id: 37002,
    source: SocialExperienceReducerFile,
    severity,
    pb
};

/**
 * Errors on Social experience http requests
 */

const SocialAPISourceFile = "Social.api.ts";

export const SocialAPIRequestErrorUnknown: AppError = {
    id: 37020,
    source: SocialAPISourceFile,
    severity,
    pb
};

export const SocialAPIRequestErrorContents: AppError = {
    id: 37021,
    source: SocialAPISourceFile,
    severity,
    pb
};

export const SocialAPIRequestErrorComments: AppError = {
    id: 37022,
    source: SocialAPISourceFile,
    severity,
    pb
};

export const SocialAPIRequestErrorUsers: AppError = {
    id: 37023,
    source: SocialAPISourceFile,
    severity,
    pb
};

/**
 * Errors on Social experience component renders
 */

const SocialExperienceFile = "Social.tsx";

export const SocialExperienceRenderErrorComment: AppError = {
    id: 37050,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorProfilePage: AppError = {
    id: 37051,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorProfileCard: AppError = {
    id: 37052,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorProfileTabs: AppError = {
    id: 37053,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorSocialBar: AppError = {
    id: 37054,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorReactionBreakdown: AppError = {
    id: 37055,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorGuideline: AppError = {
    id: 37057,
    source: SocialExperienceFile,
    severity,
    pb
};
export const SocialExperienceRenderErrorMultilineLogin: AppError = {
    id: 37058,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorNotificationTray: AppError = {
    id: 37059,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorOnboarding: AppError = {
    id: 37060,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorOnbardingThirdParty: AppError = {
    id: 37061,
    source: SocialExperienceFile,
    severity,
    pb
};

export const SocialExperienceRenderErrorUnknownComponent: AppError = {
    id: 37062,
    source: SocialExperienceFile,
    severity,
    pb
};

const LocationSelectorConnectorFileName = "LocationSelector.connector.ts";
/**
 * Suggestion Id is out of bounds or it doesn't exist;
 */
export const LocationSelectorInvalidSuggestionId: AppError = {
    id: 37100,
    source: LocationSelectorConnectorFileName,
    severity,
    pb
};

export const CommunityErrorPage: AppError = {
    id: 37200,
    source: "app-types/community/src/page.ts",
    severity,
    pb
};

/**
 * PDP Service library errors - 3730X
 */
export const UnknownBulkLoadGraphActionType: AppError = {
    id: 37300,
    source: "PdpServiceClient.ts",
    severity: Severity.NoAlert,
    pb
};

/**
 * Set of errors in story-snippet experience.
 */
export const StorySnippetInvalidStoryData: AppError = {
    id: 37450,
    source: "StorySnippet.connector.ts",
    severity,
    pb
};

/**
 * Erro when Story Snippet experience receives invalid data schema from related content cluster endpoint.
 */
export const StorySnippetInvalidRelatedContentClusterData: AppError = {
    id: 37451,
    source: "StorySnippet.connector.ts",
    severity,
    pb
};

/**
 * Errors related to MyBriefRiverCard
 */
const MyBriefRiverCardFileName = "MyBriefRiverCard.connector.ts";
export const MyBriefRiverCardMissingConfig: AppError = {
    id: 37500,
    source: MyBriefRiverCardFileName,
    severity,
    pb
};

export const MyBriefRiverCardEmptyOrNullResponse: AppError = {
    id: 37501,
    source: MyBriefRiverCardFileName,
    severity,
    pb
};

const WindowsCardProviderFileName = "WindowsCardProvider.ts";
export const WindowsCardProviderResponseWasNullOrErroneous: AppError = {
    id: 37550,
    source: WindowsCardProviderFileName,
    severity,
    pb
};

export const WindowsCardProviderErrorFetchingData: AppError = {
    id: 37551,
    source: WindowsCardProviderFileName,
    severity,
    pb
};

const OneServicePageProviderFileName = "OneServicePageProvider.ts";
export const OneServicePageProviderResponseWasNullOrErroneous: AppError = {
    id: 37560,
    source: OneServicePageProviderFileName,
    severity,
    pb
};

export const OneServicePageProviderErrorFetchingData: AppError = {
    id: 37561,
    source: OneServicePageProviderFileName,
    severity,
    pb
};

/**
 * Error related to Traffic experiences.
 */
const TrafficPageFileName = "traffic/page.ts";
const TrafficCardConnectorFileName = "traffic-card/TrafficCard.connector.ts";
const TrafficAutoSuggestClientFileName = "traffic-card/AutoSuggest/DirectionsAutoSuggestClient.ts";
const TrafficServiceFileName = "traffic-lib/TrafficService.ts";

export const TrafficEntryPointAppError: AppError = {
    id: 37600,
    source: TrafficPageFileName,
    severity,
    pb
};

export const TrafficIncidentsError: AppError = {
    id: 37601,
    source: TrafficCardConnectorFileName,
    severity,
    pb
};

export const TrafficAutoSuggestError: AppError = {
    id: 37602,
    source: TrafficAutoSuggestClientFileName,
    severity,
    pb
};

export const TrafficSettingsSaveError: AppError = {
    id: 37603,
    source: TrafficCardConnectorFileName,
    severity,
    pb
};

export const TrafficCardOneServiceJsonError: AppError = {
    id: 37604,
    source: TrafficCardConnectorFileName,
    severity,
    pb
};

export const TrafficCardOneServiceUserSettingsSaveError: AppError = {
    id: 37605,
    source: TrafficServiceFileName,
    severity,
    pb
};

/**
 * Represents GmailEmailPreview related App errors
 */
const GmailEmailPreviewDataSourceFile = "GmailEmailPreview.connector.ts";
export const GmailEmailPreviewGoogleAPIError: AppError = {
    id: 37700,
    source: GmailEmailPreviewDataSourceFile,
    severity,
    pb
};

export const GmailEmailPreviewGmailAPIError: AppError = {
    id: 37701,
    source: GmailEmailPreviewDataSourceFile,
    severity,
    pb
};

const LocalNewsViewCardConnectorFileName = "LocalNewsViewCard.connector.ts";
/**
 * Data connector fails to be found or to get the cards from;
 */
export const LocalNewsViewCardDataConnectorAppError: AppError = {
    id: 37800,
    source: LocalNewsViewCardConnectorFileName,
    severity,
    pb
};

/**
 * LocationFeedData Errors
 */
export const LocationFeedDataConnectorAppError: AppError = {
    id: 37900,
    source: "LocationFeed.connector.ts",
    severity,
    pb
};

/**
 * Errors related to Admin Portal
 */
const AdminPortalConnectorFileName = "AdminPortalData.connector.ts";
export const AdminPortalSettingsFetchAppError: AppError = {
    id: 38000,
    source: AdminPortalConnectorFileName,
    severity,
    pb
};

export const AdminPortalShimUiShown: AppError = {
    id: 38001,
    source: AdminPortalConnectorFileName,
    severity: Severity.NoAlert,
    pb
};

/**
 * Errors related to Desktop Feed
 */
const DesktopFeedConnectorFileName = "DesktopFeed.connector.ts";
export const DesktopFeedRiverContentFetchFailed: AppError = {
    id: 38010,
    source: DesktopFeedConnectorFileName,
    severity,
    pb
};

export const DesktopFeedUnsupportedCardType: AppError = {
    id: 38011,
    source: DesktopFeedConnectorFileName,
    severity,
    pb
};

export const DesktopFeedFullPageAPIFetchFailed: AppError = {
    id: 38012,
    source: DesktopFeedConnectorFileName,
    severity,
    pb
};

export const DesktopFeedFeedNativeAdServiceFetchFailed: AppError = {
    id: 38013,
    source: DesktopFeedConnectorFileName,
    severity,
    pb
};

export const DesktopFeedFeedInvalidRiverTemplateId: AppError = {
    id: 38014,
    source: DesktopFeedConnectorFileName,
    severity: Severity.NoAlert,
    pb
};

export const DesktopFeedFeedInvalidEmptyRiver: AppError = {
    id: 38015,
    source: DesktopFeedConnectorFileName,
    severity,
    pb
};

export const DesktopFeedInvalidSectionData: AppError = {
    id: 38016,
    source: DesktopFeedConnectorFileName,
    severity,
    pb
};

/**
 * Errors related to Common Header
 */
const CommonHeaderConnectorFileName = "CommonHeader.connector.ts";
const CommonHeaderTsxFileName = "CommonHeader.tsx";
export const CommonHeaderNavigationFailed: AppError = {
    id: 38020,
    source: CommonHeaderConnectorFileName,
    severity,
    pb
};

export const CommonHeaderInvalidConfig: AppError = {
    id: 38021,
    source: CommonHeaderConnectorFileName,
    severity,
    pb
};

export const CommonHeaderMissingFormCode: AppError = {
    id: 38022,
    source: CommonHeaderConnectorFileName,
    severity,
    pb
};

export const CommonHeaderRenderError: AppError = {
    id: 38023,
    source: CommonHeaderTsxFileName,
    severity,
    pb
};

export const CommonHeaderSSOFailed: AppError = {
    id: 38024,
    source: CommonHeaderTsxFileName,
    severity,
    pb
};

export const CommonHeaderMeControlLoadFailed: AppError = {
    id: 38025,
    source: CommonHeaderTsxFileName,
    severity,
    pb
};

export const CommonHeaderInvalidUrl: AppError = {
    id: 38026,
    source: CommonHeaderConnectorFileName,
    severity,
    pb
};

/**
 * Set of errors in video-card experience.
 */
export const VideoCardInvalidVideoData: AppError = {
    id: 38100,
    source: "VideoCard.connector.ts",
    severity,
    pb
};

const NewsbarFeedConnectorFileName = "NewsbarFeed.connector.ts";
export const NewsbarFeedNativeAdServiceFetchFailed: AppError = {
    id: 38200,
    source: NewsbarFeedConnectorFileName,
    severity,
    pb
};
export const NewsbarFeedOneServicePageProviderFetchFailed: AppError = {
    id: 38201,
    source: NewsbarFeedConnectorFileName,
    severity,
    pb
};
export const NewsbarFeedInvalidLayoutTemplateId: AppError = {
    id: 38202,
    source: NewsbarFeedConnectorFileName,
    severity,
    pb
};
/*
 * Set of errors in sports-card experience.
 */
const SportsDataServiceFileName = "SportsDataService.ts";
export const SportsDataServiceResponseWasNullOrErroneous: AppError = {
    id: 38300,
    source: SportsDataServiceFileName,
    severity,
    pb
};

export const SportsDataServiceFetchFailed: AppError = {
    id: 38301,
    source: SportsDataServiceFileName,
    severity,
    pb
};

export const SportsDataServiceUserSettingSaveFailed: AppError = {
    id: 38302,
    source: SportsDataServiceFileName,
    severity,
    pb
};

/**
 * Set of errors in the sports-matchup-card experience
 */

const SportsMatchupCardConnectorSourceFile = "SportsMatchupCard.connector.ts";

/** Exception when OneService response is incorrect */
export const SportsMatchupCardOneServiceDataError: AppError = {
    id: 38310,
    source: SportsMatchupCardConnectorSourceFile,
    severity,
    pb
};

/*
 * Set of errors in sports-card experience.
 */
const SportsDataMapperFileName = "SportsDataMapper.ts";
export const SportsDataMaperCardTitleIsNull: AppError = {
    id: 38320,
    source: SportsDataMapperFileName,
    severity,
    pb
};

export const SportsDataMaperCardParseError: AppError = {
    id: 38321,
    source: SportsDataMapperFileName,
    severity,
    pb
};

export const SportsDataMaperNullData: AppError = {
    id: 38322,
    source: SportsDataMapperFileName,
    severity,
    pb
};

/**
 * Set of errors in the sports-card-wc experience
 */

const SportsCardWCSourceFile = "SportsCardWC.ts";

/** Exception when create indexed db */
export const SportsCardWCIndexedDbError: AppError = {
    id: 38330,
    source: SportsCardWCSourceFile,
    severity,
    pb
};

/** Exception when fetch feed response data error */
export const SportsCardWCFeedDataError: AppError = {
    id: 38331,
    source: SportsCardWCSourceFile,
    severity,
    pb
};

/** Exception when search sports suggestion */
export const SportsCardWCSearchSuggestionError: AppError = {
    id: 38332,
    source: SportsCardWCSourceFile,
    severity,
    pb
};

/**
 * Set of errors in ugc app type.
 */
export const UgcErrorPage: AppError = {
    id: 38400,
    source: "UGC/page.ts",
    severity,
    pb
};

export const UgcCreatorRenderError: AppError = {
    id: 38401,
    source: "UgcCreator.ts",
    severity,
    pb
};

export const UgcCreatorApiError: AppError = {
    id: 38402,
    source: "UgcCreator.api.ts",
    severity,
    pb
};

/**
 * Errors related to Modern Right Rail
 */
const ModernRightRailConnectorFileName = "ModernRightRail.connector.ts";
const ContentProviderServiceFileName = "ContentProvider.services.ts";
const TrendingNewsServiceFileName = "ContentProvider.services.ts";
const RecommendingVideosServiceFileName = "ContentProvider.services.ts";

/** Failed to fetch provider details. */
export const ProviderDetailsFetchError: AppError = {
    id: 38500,
    source: ContentProviderServiceFileName,
    severity,
    pb
};

/** No content on fetching provider feed. */
export const ProviderFeedFetchEmpty: AppError = {
    id: 38501,
    source: ContentProviderServiceFileName,
    severity,
    pb
};

/** Failed to fetch trending news card data. */
export const TrendingNewsFetchError: AppError = {
    id: 38502,
    source: TrendingNewsServiceFileName,
    severity,
    pb
};

/** Failed to fetch trending news card backup data. */
export const TrendingNewsBackupFetchError: AppError = {
    id: 38503,
    source: TrendingNewsServiceFileName,
    severity,
    pb
};

/** Native Ad fetch failed */
export const NativeAdFetchFailed: AppError = {
    id: 38504,
    source: ModernRightRailConnectorFileName,
    severity,
    pb
};

/** Failed to fetch recommending videos card data. */
export const RecommendingVideosFetchError: AppError = {
    id: 38505,
    source: RecommendingVideosServiceFileName,
    severity,
    pb
};

/** Failed to fetch recommending videos card backup data. */
export const RecommendingVideosBackupFetchError: AppError = {
    id: 38506,
    source: RecommendingVideosServiceFileName,
    severity,
    pb
};

/**
 * Set of errors for BasePreferenceSettingManager.
 */
const BasePreferenceSettingManagerFileName = "BasePreferenceSettingManager.ts";

/**
 * window.chrome.ntpSettingsPrivate API is not available
 */
export const NtpSettingsPrivateNotAvailable: AppError = {
    id: 38600,
    source: BasePreferenceSettingManagerFileName,
    severity,
    pb
};

/**
 * Error with writing to browser with ntpSettingsPrivate.setPref
 */
export const NtpSetPrefFailureToWrite: AppError = {
    id: 38601,
    source: BasePreferenceSettingManagerFileName,
    severity,
    pb
};

/**
 * Error reading ntpSettingsPrivate.getPref
 */
export const NtpGetPrefFailureToRead: AppError = {
    id: 38602,
    source: BasePreferenceSettingManagerFileName,
    severity,
    pb
};

/**
 * Top level preference setting API is not available
 */
export const PreferenceSettingApiNotAvailable: AppError = {
    id: 38603,
    source: BasePreferenceSettingManagerFileName,
    severity,
    pb
};

/**
 * Preference key is missing
 */
export const PreferenceSettingKeyNotAvailable: AppError = {
    id: 38604,
    source: BasePreferenceSettingManagerFileName,
    severity,
    pb
};

/**
 * Preference setting did not save
 */
export const PreferenceSettingDidNotSave: AppError = {
    id: 38605,
    source: BasePreferenceSettingManagerFileName,
    severity,
    pb
};

/**
 * Errors related to Background image
 * More errors can be found on 818X
 */
const enterpriseImageProvider = "EnterpriseImageProvider.ts";

/** Failed to fetch provider details. */
export const CopyrightParseError: AppError = {
    id: 38700,
    source: bingImageProvider,
    severity,
    pb
};

/** FRE image invalid index */
export const FREImageInvalidIndex: AppError = {
    id: 38701,
    source: bingImageProvider,
    severity,
    pb
};

/** Enterprise image unexpected call */
export const EnterpriseImageUnexpected: AppError = {
    id: 38702,
    source: enterpriseImageProvider,
    severity,
    pb
};

/** Enterprise image invalid index */
export const EnterpriseInvalidIndex: AppError = {
    id: 38703,
    source: enterpriseImageProvider,
    severity,
    pb
};

const summaryCardConnectorFileName = "SummaryCard.connector.ts";

/** Error fetching weather data */
export const SummaryCardFetchWeatherData: AppError = {
    id: 38800,
    source: summaryCardConnectorFileName,
    severity,
    pb
};

/** Error fetching weather auto suggest data */
export const SummaryCardFetchAutoSuggestWeatherData: AppError = {
    id: 38801,
    source: summaryCardConnectorFileName,
    severity,
    pb
};

/** Error refreshing weather data */
export const SummaryCardRefreshWeatherData: AppError = {
    id: 38802,
    source: summaryCardConnectorFileName,
    severity,
    pb
};

/** Error connecting summary card component */
export const SummaryCardConnectComponent: AppError = {
    id: 38803,
    source: summaryCardConnectorFileName,
    severity,
    pb
};

const todaysEventsCardConnectorFileName = "TodaysEventsCard.connector.ts";

/** Error fetching today's events */
export const TodaysEventsCardFetch: AppError = {
    id: 38900,
    source: todaysEventsCardConnectorFileName,
    severity,
    pb
};

const todaysEventsCardManagerFileName = "EventManager.ts";

/** Error determining todays event's conflicts */
export const TodaysEventsCardManagerConflict: AppError = {
    id: 38901,
    source: todaysEventsCardManagerFileName,
    severity,
    pb
};

const todaysEventsCardTimeHelperName = "TimeHelper.ts";

/** Error with the Date-fns library */
export const TodaysEventsCardDateFnsError: AppError = {
    id: 38902,
    source: todaysEventsCardTimeHelperName,
    severity,
    pb
};

const frequentSitesCardConnectorFileName = "FrequentSitesCard.connector.ts";

// Error Fetching Frequent Sites Data
export const FrequentSitesCardFetch: AppError = {
    id: 39000,
    source: frequentSitesCardConnectorFileName,
    severity,
    pb
};

const recommendedCardConnectorFileName = "RecommendedCard.connector.ts";
const recommendedCardInspectorFileName = "RecommendedCard.inspector.ts";
const recommendedCardFeed = "RecommendedCardFeed";
const recommendedDocumentsServiceClient = "RecommendedDocumentsServiceClient.ts";

// Error Fetching Recommended Card Documents
export const RecommendedCardConnectorFetchDocuments: AppError = {
    id: 39100,
    source: recommendedCardConnectorFileName,
    severity,
    pb
};

export const RecommendedCardConnectorFetchImages: AppError = {
    id: 39101,
    source: recommendedCardConnectorFileName,
    severity,
    pb
};

// Error Fetching Recommended Card Preview Image
export const RecommendedCardConnectorFetchImage: AppError = {
    id: 39102,
    source: recommendedCardConnectorFileName,
    severity,
    pb
};

// Error creating file item props
export const RecommendedCardInspectorBuildFileItems: AppError = {
    id: 39103,
    source: recommendedCardInspectorFileName,
    severity,
    pb
};

// Error Fetching Documents from the Recommended Card Feed component
export const RecommendedCardFeedFetchDocuments: AppError = {
    id: 39104,
    source: recommendedCardFeed,
    severity,
    pb
};

// Error Fetching Preview Image from the Recommended Card Feed component
export const RecommendedCardFeedFetchImage: AppError = {
    id: 39105,
    source: recommendedCardFeed,
    severity,
    pb
};

// Error switching from the Recommended Card Feed component to Office pivto
export const RecommendedCardFeedSwitchToOffice: AppError = {
    id: 39106,
    source: recommendedCardFeed,
    severity,
    pb
};

// Error switching from the Recommended Card Feed component to Office pivto
export const RecommendedCardFeedMissingPivotSwitchConfig: AppError = {
    id: 39107,
    source: recommendedCardFeed,
    severity,
    pb
};

// Error for the Recommended Card to print duration when fetch exceeds the time out
export const RecommendedCardDocumentsServiceClientTimeOut: AppError = {
    id: 39108,
    source: recommendedDocumentsServiceClient,
    severity,
    pb
};

const productivityHubContainerConnectorFileName = "ProductivityHubContainer.connector.ts";

/** Error loading child experiences */
export const ProductivityHubContainerLoadChildren: AppError = {
    id: 39200,
    source: productivityHubContainerConnectorFileName,
    severity,
    pb
};

const edgeNextLayoutOptimizerFileName = "EdgeNextLayoutOptimizer.ts";

/** Error applying recommended layout */
export const ApplyingRecommendedLayoutFailed: AppError = {
    id: 39300,
    source: edgeNextLayoutOptimizerFileName,
    severity,
    pb
};

/**
 * Set of errors in the bing-video-card experience
 */

const BingVideoCardConnectorSourceFile = "BingVideoCard.connector.ts";
const BingVideoSlideCardSourceFile = "SlideCard.tsx";
const BingVideoOpalRecoInstrumentationSourceFile = "OpalRecoInstrumentation.ts";

/** Exception when OneService response is incorrect */
export const BingVideoCardOneServiceDataError: AppError = {
    id: 39400,
    source: BingVideoCardConnectorSourceFile,
    severity,
    pb
};

export const BingVideoCardOneServiceDataTypeError: AppError = {
    id: 39401,
    source: BingVideoCardConnectorSourceFile,
    severity,
    pb
};

export const BingVideoCardOneServiceDataParseError: AppError = {
    id: 39402,
    source: BingVideoCardConnectorSourceFile,
    severity,
    pb
};

export const BingVideoCardDataError: AppError = {
    id: 39403,
    source: BingVideoCardConnectorSourceFile,
    severity,
    pb
};

export const BingVideoCardDataParseError: AppError = {
    id: 39404,
    source: BingVideoCardConnectorSourceFile,
    severity,
    pb
};

export const BingVideoCardMotionThumbnailError: AppError = {
    id: 39405,
    source: BingVideoSlideCardSourceFile,
    severity,
    pb
};

export const BingVideoCardOpalFeedbackError: AppError = {
    id: 39406,
    source: BingVideoOpalRecoInstrumentationSourceFile,
    severity,
    pb
};

const FreServiceClientFileName = "FreServiceClient.ts";
const ColdStartConnectorFileName = "ColdStart.connector.ts";
const ColdStartFileName = "ColdStart.tsx";
const ColdStartArticlesTemplateFileName = "Articles.template.tsx";
const ColdStartPublishersTemplateFileName = "Publishers.template.tsx";
const ColdStartWCEFileName = "ColdStartWC.ts";

/** Error setting required parameters for FRE client functions */
export const SettingRequiredParametersFailed: AppError = {
    id: 39500,
    source: FreServiceClientFileName,
    severity,
    pb
};

/** Error getting data for artifacts */
export const GettingArtifactsDataFailed: AppError = {
    id: 39501,
    source: ColdStartConnectorFileName,
    severity,
    pb
};

/** Missing FRE signal */
export const MissingFRESignal: AppError = {
    id: 39502,
    source: DesktopFeedConnectorFileName,
    severity,
    pb
};

/** Empty FRE response */
export const EmptyFREResponse: AppError = {
    id: 39503,
    source: ColdStartConnectorFileName,
    severity,
    pb
};

/** FRE unsupported template */
export const FREUnsupportedTemplate: AppError = {
    id: 39504,
    source: ColdStartFileName,
    severity,
    pb
};

/** FRE missing publisher metadata */
export const FREMissingPublisherMetadata: AppError = {
    id: 39505,
    source: ColdStartPublishersTemplateFileName,
    severity,
    pb
};

/** FRE missing article metadata */
export const FREMissingArticleMetadata: AppError = {
    id: 39506,
    source: ColdStartArticlesTemplateFileName,
    severity,
    pb
};

/** FRE missing callback url */
export const FREMissingCallbackUrl: AppError = {
    id: 39507,
    source: ColdStartConnectorFileName,
    severity,
    pb
};

/**  Failed to go to next stage*/
export const FailedToGoToNextStage: AppError = {
    id: 39508,
    source: ColdStartConnectorFileName,
    severity,
    pb
};

/** The done Action call failed */
export const DoneActionFailed: AppError = {
    id: 39509,
    source: FreServiceClientFileName,
    severity,
    pb
};

/** An Actions call failed */
export const FreActionsCallFailed: AppError = {
    id: 39510,
    source: FreServiceClientFileName,
    severity,
    pb
};

/** Setting the maybe later state failed */
export const FreMaybeLaterStateFailed: AppError = {
    id: 39511,
    source: FreServiceClientFileName,
    severity,
    pb
};

/** Cold Start Topics Data Connector not defined */
export const ColdStartUndefinedTopicDataConnector: AppError = {
    id: 39512,
    source: ColdStartConnectorFileName,
    severity,
    pb
};

/** FRE missing sub cards */
export const FREMissingSubCards: AppError = {
    id: 39513,
    source: ColdStartConnectorFileName,
    severity,
    pb
};

/** Not enough data to render the first page of content */
export const FreInsufficientFirstPageData: AppError = {
    id: 39514,
    source: ColdStartWCEFileName,
    severity,
    pb
};

/** Represents HoroscopeAnswerCard related App errors. 396xx: Horoscope Answer Card*/
const horoscopeAnswerCardDataService = "HoroscopeAnswerCardDataService.ts";
const horoscopeDataMapperFile = "libs/feed-layout/.../HoroscopeCardDataMapper.ts";
/** Represents get horoscope info and user signin info error */
export const HoroscopeAnswerCardGetApiFailed: AppError = {
    id: 39600,
    source: horoscopeAnswerCardDataService,
    severity,
    pb
};

/** Represents horoscope selection post error */
export const HoroscopeAnswerCardPostApiFailed: AppError = {
    id: 39601,
    source: horoscopeAnswerCardDataService,
    severity,
    pb
};

/** Represents horoscope data mapper error */
export const HoroscopeDataMapperError: AppError = {
    id: 39602,
    source: horoscopeDataMapperFile,
    severity,
    pb
};

const targetActionCTA = "TargetAction.cta.ts";
/**
 * SetDefaultBrowser chromium api threw exception
 */
export const SetDefaultBrowserException: AppError = {
    id: 39700,
    source: targetActionCTA,
    severity,
    pb
};

/**
 * SetDefaultBrowser chromium api not available
 */
export const SetDefaultBrowserNotAvailable: AppError = {
    id: 39701,
    source: targetActionCTA,
    severity,
    pb
};

/**
 * Error reading ntp private settings preference.
 */
export const AppPinningPrivateException: AppError = {
    id: 39702,
    source: targetActionCTA,
    severity,
    pb
};

/**
 * window.chrome.desktopWidgetPrivate API is not available
 */
export const DesktopWidgetPrivateNotAvailable: AppError = {
    id: 39703,
    source: targetActionCTA,
    severity,
    pb
};

/**
 * Error reading ntp private settings preference.
 */
export const WebWidgetLaunchApiNotAvailable: AppError = {
    id: 39704,
    source: targetActionCTA,
    severity,
    pb
};

/*
* Error reading ntp private settings preference.
*/
export const WebWidgetEnableAutoStartApiNotAvailable: AppError = {
    id: 39705,
    source: targetActionCTA,
    severity,
    pb
};

/**
 * Error reading ntp private settings preference.
 */
export const FailedToLaunchAndDefaultWebWidget: AppError = {
    id: 39706,
    source: targetActionCTA,
    severity,
    pb
};

/** AppErrors for SocialEmbeds */
const FacebookEmbedFile = "FacebookEmbed.ts";
const InstagramEmbedFile = "InstagramEmbed.ts";
const InfogramEmbedFile = "InfogramEmbed.ts";
const GoogleMapsEmbedFile = "GoogleMapsEmbed.ts";
const TiktokEmbedFile = "TiktokEmbed.ts";
const SpotifyEmbedFile = "SpotifyEmbed.ts";
const FlourishEmbedFile = "FlourishEmbed.ts";
const RedditEmbedFile = "RedditEmbed.ts";
const PinterestEmbedFile = "PinterestEmbed.ts";
/** Represents facebook embed validation failed */
export const FacebookEmbedValidationError: AppError = {
    id: 39800,
    source: FacebookEmbedFile,
    severity,
    pb
};

/** Represents instagram embed validation failed */
export const InstagramEmbedValidationError: AppError = {
    id: 39810,
    source: InstagramEmbedFile,
    severity,
    pb
};

/** Represents infogram embed validation failed */
export const InfogramEmbedValidationError: AppError = {
    id: 39820,
    source: InfogramEmbedFile,
    severity,
    pb
};

/** Represents GoogleMaps embed validation failed */
export const GoogleMapsEmbedValidationError: AppError = {
    id: 39830,
    source: GoogleMapsEmbedFile,
    severity,
    pb
};

/** Represents Tiktok embed validation failed */
export const TiktokEmbedValidationError: AppError = {
    id: 39840,
    source: TiktokEmbedFile,
    severity,
    pb
};

/** Represents Flourish embed validation failed */
export const FlourishEmbedValidationError: AppError = {
    id: 39850,
    source: FlourishEmbedFile,
    severity,
    pb
};

/** Represents Spotify embed validation failed */
export const SpotifyEmbedValidationError: AppError = {
    id: 39860,
    source: SpotifyEmbedFile,
    severity,
    pb
};

/** Represents Reddit embed validation failed */
export const RedditEmbedValidationError: AppError = {
    id: 39870,
    source: RedditEmbedFile,
    severity,
    pb
};

/** Represents Pinterest embed validation failed */
export const PinterestEmbedValidationError: AppError = {
    id: 39880,
    source: PinterestEmbedFile,
    severity,
    pb
};

const onThisDayCardDataService = "OnThisDayCardDataService.ts";
const onThisDayCardDataMapperFile = "libs/feed-layout/.../OnThisDayCardDataMapper.ts";
/** Represents get onThisDay info failed */
export const OnThisDayCardGetApiFailed: AppError = {
    id: 39900,
    source: onThisDayCardDataService,
    severity,
    pb
};

/** Represents mapper data error */
export const OnThisDayCardDataMapperError: AppError = {
    id: 39901,
    source: onThisDayCardDataMapperFile,
    severity,
    pb
};

/**
 * Telemetry initialization error
 */
export const EventHubPageTelemetryInitializationError: AppError = {
    id: 40000,
    source: "EventHubPage.connector.ts",
    severity,
    pb
};

/**
 * Apperrors for the ntp consumer waffle
 */
const ConsumerWaffleFile = "ServiceWaffle.connector.ts";
export const ConsumerWaffleError: AppError = {
    id: 40100,
    source: ConsumerWaffleFile,
    severity,
    pb
};

export const ConsumerWaffleStringLoadError: AppError = {
    id: 40101,
    source: ConsumerWaffleFile,
    severity,
    pb
};

const portugueseElectionExperienceDataService = "PortugueseElectionExperienceDataService.ts";
/** Represents get portugueseElectionExperienceDataService info failed */
export const PortugueseElectionExperienceGetApiFailed: AppError = {
    id: 40201,
    source: portugueseElectionExperienceDataService,
    severity,
    pb
};

const WpoCardProviderFileName = "WpoCardProvider.ts";

export const WpoCardProviderAppErrors = {
    WpoResponseServerInternalError: <AppError>{
        id: 40300,
        source: WpoCardProviderFileName,
        severity,
        pb
    },

    ErrorFetchingWpoResponse: <AppError>{
        id: 40301,
        source: WpoCardProviderFileName,
        severity: Severity.HighImpact,
        pb
    },

    RiverConfigurationIssue: <AppError>{
        id: 40302,
        source: WpoCardProviderFileName,
        severity,
        pb
    },

    WpoGetMuidFailure: <AppError>{
        id: 40303,
        source: WpoCardProviderFileName,
        severity: Severity.NoAlert,
        pb
    },

    StaticContentLoaded: <AppError>{
        id: 40304,
        source: WpoCardProviderFileName,
        severity,
        pb
    },

    WpoFailedAtFirstPage: <AppError> {
        id: 40305,
        source: WpoCardProviderFileName,
        severity,
        pb
    },

    WpoResponseUnauthorized: <AppError>{
        id: 40306,
        source: WpoCardProviderFileName,
        severity,
        pb
    },

    WpoResponseNotFound: <AppError> {
        id: 40307,
        source: WpoCardProviderFileName,
        severity,
        pb
    },

    WpoResponseNoContent: <AppError> {
        id: 40308,
        source: WpoCardProviderFileName,
        severity,
        pb
    },

    WpoResponseNullOrUndefined: <AppError> {
        id: 40309,
        source: WpoCardProviderFileName,
        severity,
        pb
    }
};

/**
 * Errors related to Desktop Feed EdgeNext
 */
const DesktopFeedEdgeNextFileName = "DesktopFeedEdgeNext.tsx";
const DesktopFeedEdgeNextConnectorFileName = "DesktopFeedEdgeNext.connector.ts";

export const DesktopFeedEdgeNextNativeAdServiceFetchFailed: AppError = {
    id: 40401,
    source: DesktopFeedEdgeNextConnectorFileName,
    severity,
    pb
};

export const DesktopFeedEdgeNextRiverContentFetchFailed: AppError = {
    id: 40402,
    source: DesktopFeedEdgeNextConnectorFileName,
    severity,
    pb
};

export const DesktopFeedEdgeNextMissingFreSignal: AppError = {
    id: 40404,
    source: DesktopFeedEdgeNextConnectorFileName,
    severity,
    pb
};

export const DesktopFeedEdgeNextSwitchToWatch: AppError = {
    id: 40405,
    source: DesktopFeedEdgeNextFileName,
    severity,
    pb
};

/** App Errors for Recipes */
const RecipesPage = "recipes/page.ts";

/**
 * Recipes level exception was thrown
 */
export const RecipesPageError: AppError = {
    id: 40500,
    source: RecipesPage,
    severity,
    pb
};

/**
 * Recipes Page Error fetching the APP_ANON cookie
 */
export const RecipesPageErrorFetchingAppAnonCookie: AppError = {
    id: 40501,
    source: RecipesPage,
    severity,
    pb
};
/** End App Errors for Recipes */

/**
 * Errors related to Desktop Feed Single Column
 */
const DesktopFeedSingleColumnConnectorFileName = "DesktopFeedSingleColumn.connector.ts";
export const DesktopFeedSingleColumnNativeAdServiceFetchFailed: AppError = {
    id: 40601,
    source: DesktopFeedSingleColumnConnectorFileName,
    severity,
    pb
};

export const DesktopFeedSingleColumnRiverContentFetchFailed: AppError = {
    id: 40602,
    source: DesktopFeedSingleColumnConnectorFileName,
    severity,
    pb
};

export const DesktopFeedSingleColumnCmsAdsMatchResponseFailed: AppError = {
    id: 40603,
    source: DesktopFeedSingleColumnConnectorFileName,
    severity,
    pb
};

const SDCardProviderFileName = "SDCardProvider.ts";

export const SDCardProviderResponseWasNullOrErroneous: AppError = {
    id: 40700,
    source: SDCardProviderFileName,
    severity,
    pb
};

export const SDCardProviderErrorFetchingResponse: AppError = {
    id: 40701,
    source: SDCardProviderFileName,
    severity,
    pb
};

export const SDCardProviderConfigurationIssue: AppError = {
    id: 40702,
    source: SDCardProviderFileName,
    severity,
    pb
};

/** App Errors for QnA */
const QnaPage = "qna/page.ts";
/**
 * Qna level exception was thrown
 */
export const QnaPageError: AppError = {

    id: 40800,
    source: QnaPage,
    severity,
    pb
};

/**
 * Qna Page Error fetching the APP_ANON cookie
 */
export const QnaPageErrorFetchingAppAnonCookie: AppError = {
    id: 40801,
    source: QnaPage,
    severity,
    pb
};

/** Social Reactions App errors in SuperApp */
const SocialReactionsServiceResponseFileName = "ArticleCardRenderer.tsx";

/**
 * Error parsing response from OneService call to update Social Reaction count in SuperApp.
 */
export const SocialReactionsServiceResponseDataParse: AppError = {
    id: 40900,
    source: SocialReactionsServiceResponseFileName,
    severity,
    pb
};

const ToDoCardConnectorFileName = "TodoCard.connector.ts";

/** Error with the ToDo card API when trying to fetch existing tasks */
export const ToDoCardAPIFetchTasksFailed: AppError = {
    id: 41000,
    source: ToDoCardConnectorFileName,
    severity,
    pb
};

/** Error with the ToDo card API when trying create a new task */
export const ToDoCardAPICreateTaskFailed: AppError = {
    id: 41001,
    source: ToDoCardConnectorFileName,
    severity,
    pb
};

/** Error with the ToDo card API when trying to fetch existing task folders */
export const ToDoCardAPIRetrieveFoldersFailed: AppError = {
    id: 41002,
    source: ToDoCardConnectorFileName,
    severity,
    pb
};

/** Error with the ToDo card API when trying to update the status of a task */
export const ToDoCardAPIUpdateTaskStatusFailed: AppError = {
    id: 41003,
    source: ToDoCardConnectorFileName,
    severity,
    pb
};

/** Error with the ToDo card API when trying to update the importance of a task */
export const ToDoCardAPIUpdateTaskImportanceFailed: AppError = {
    id: 41004,
    source: ToDoCardConnectorFileName,
    severity,
    pb
};

/** Error with the ToDo card when trying to update the completion of a task */
export const ToDoCardAPICompleteTaskFailed: AppError = {
    id: 41005,
    source: ToDoCardConnectorFileName,
    severity,
    pb
};

/** Error with the ToDo card API when trying to update the subject of a task */
export const ToDoCardAPISubjectChangeFailed: AppError = {
    id: 41006,
    source: ToDoCardConnectorFileName,
    severity,
    pb
};

/** Error with the ToDo card when toggling between task lists */
export const ToDoCardFailedToToggleList: AppError = {
    id: 41007,
    source: ToDoCardConnectorFileName,
    severity,
    pb
};

const FinanceMarketCardFile = "FinanceMarketCard.connector.ts";
const FinanceDetailCardFile = "FinanceDetailsCard.connector.ts";
const FinanceWatchlistViewFile = "WatchlistView.connector.ts";
const FinanceNewsCardFile = "FinanceNewsCard.connector.ts";
const FinanceTrendCardFile = "FinanceTrendCard.connector.ts";
const FinanceFeedsFile = "FinanceFeeds.connector.ts";

/**
 * 411xx reserved for finance experience errors
*/

/** Error when finance market card unavailable */
export const FinanceMarketCardAppError: AppError = {
    id: 41100,
    source: FinanceMarketCardFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Error when finance detail card unavailable */
export const FinanceDetailCardAppError: AppError = {
    id: 41101,
    source: FinanceDetailCardFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Error when finance watchlist view sign status error, this err code is especially used for sign in */
export const FinanceWatchlistViewAppError: AppError = {
    id: 41102,
    source: FinanceWatchlistViewFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Error when finance news card unavailable */
export const FinanceNewsCardAppError: AppError = {
    id: 41103,
    source: FinanceNewsCardFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Error when finance trend card unavailable */
export const FinanceTrendCardAppError: AppError = {
    id: 41104,
    source: FinanceTrendCardFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/** Error when finance feeds unavailable */
export const FinanceFeedsAppError: AppError = {
    id: 41105,
    source: FinanceFeedsFile,
    severity: Severity.Critical,
    pb: {
        userImpacting: true
    }
};

/**
 * 412xx reserved for finance experiecne warnings
*/

/** Warning for finance market card. */
export const FinanceMarketCardAppWarning: AppError = {
    id: 41200,
    source: FinanceMarketCardFile,
    severity,
    pb
};

/** Warning for finance detail card. */
export const FinanceDetailCardAppWarning: AppError = {
    id: 41201,
    source: FinanceDetailCardFile,
    severity,
    pb
};

/** Warning for finance watchlist view */
export const FinanceWatchlistViewAppWarning: AppError = {
    id: 41202,
    source: FinanceWatchlistViewFile,
    severity,
    pb
};

/** Error when finance news card unavailable */
export const FinanceNewsCardAppWarning: AppError = {
    id: 41203,
    source: FinanceNewsCardFile,
    severity,
    pb
};

/** Error when finance trend card unavailable */
export const FinanceTrendCardAppWarning: AppError = {
    id: 41204,
    source: FinanceTrendCardFile,
    severity,
    pb
};

/** Error when finance feeds unavailable */
export const FinanceFeedsAppWarning: AppError = {
    id: 41205,
    source: FinanceFeedsFile,
    severity,
    pb
};

/** App Errors for Views FullPage Data Connector */
const ViewsFullPageConnector = "ViewsFullPage.connector.ts";
const ViewsFullPageServiceClient = "ViewsFullPageServiceClient.ts";

/**
 * Error while parsing data
 */
export const ViewsFullPageTransformError: AppError = {
    id: 41300,
    source: ViewsFullPageConnector,
    severity,
    pb
};

/**
 * Error while fetching data
 */
export const ViewsFullPageFetchError: AppError = {
    id: 41301,
    source: ViewsFullPageServiceClient,
    severity,
    pb
};

/**
 * Error while retrieveing sections data
 */
export const ViewsFullPageInvalidSection: AppError = {
    id: 41302,
    source: ViewsFullPageConnector,
    severity,
    pb
};

/**
 * No Content Id present
 */
export const ViewsFullPageUndefinedContentId: AppError = {
    id: 41303,
    source: ViewsFullPageConnector,
    severity,
    pb
};

const IndustryNewsCardFile = "IndustryNewsCard.ts";

/** The Industry News Card failed to fetch industry news articles. */
export const IndustryNewsCardFailedToFetch: AppError = {
    id: 41400,
    source: IndustryNewsCardFile,
    severity,
    pb
};

/**
 * Errors related to Stripe Feed
 */
const StripeFeedConnectorFileName = "StripeFeed.connector.ts";
export const StripeFeedRiverContentFetchFailed: AppError = {
    id: 41501,
    source: StripeFeedConnectorFileName,
    severity,
    pb
};

export const StripeFeedUnsupportedCardType: AppError = {
    id: 41502,
    source: StripeFeedConnectorFileName,
    severity,
    pb
};

export const StripeFeedWindowsTopStripeContentFetchFailed: AppError = {
    id: 41503,
    source: StripeFeedConnectorFileName,
    severity,
    pb
};

export const StripeFeedNativeAdServiceFetchFailed: AppError = {
    id: 41504,
    source: StripeFeedConnectorFileName,
    severity,
    pb
};

export const StripeFeedInvalidRiverTemplateId: AppError = {
    id: 41505,
    source: StripeFeedConnectorFileName,
    severity: Severity.NoAlert,
    pb
};

export const StripeFeedInvalidEmptyRiver: AppError = {
    id: 41506,
    source: StripeFeedConnectorFileName,
    severity,
    pb
};

/** Error during Service Worker registration. */
const ServiceWorkerRegistrationFileName = "ResolveServiceWorker.ts";
export const ServiceWorkerRegistrationAppError: AppError = {
    id: 43000,
    source: ServiceWorkerRegistrationFileName,
    severity,
    pb
};

/**
 * Errors related to Watch page
 */
const WatchConnectorFileName = "Watch.connector.ts";
export const WatchRiverContentFetchFailed: AppError = {
    id: 43201,
    source: WatchConnectorFileName,
    severity,
    pb
};

export const WatchInvalidEmptyRiver: AppError = {
    id: 43202,
    source: WatchConnectorFileName,
    severity,
    pb
};

/**
 * Related Article Block AppErrors
 */
const RelatedArticleConnectorFileName = "RelatedArticleBlock.connector.ts";
export const RelatedArticleParseError: AppError = {
    id: 43300,
    source: RelatedArticleConnectorFileName,
    severity,
    pb
};